import { useState, useEffect, useMemo, useRef } from 'react';
import { format, addDays, parseISO } from 'date-fns';
import {
  X,
  MapPin,
  FileText,
  Wrench,
  Package,
  Truck,
  AlertCircle,
  AlertTriangle,
  User,
  Loader2,
  Plus,
  Trash2,
  Pencil,
  GraduationCap,
  UserPlus,
  ChevronDown,
  ChevronRight,
  Check,
  Shield,
  ShieldOff,
  MoreVertical,
  ExternalLink,
} from 'lucide-react';
import clsx from 'clsx';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Job, Staff } from '../data/mockData';
import { extractDealName } from '../../lib/adapters';
import { formatLocationSource } from '../../lib/locationSource';
import { formatDateDDMMYY } from '../../lib/dateFormat';
import { friendlyifyIsoTimestamps } from '../../lib/mutationErrorToast';
import { useVehicleBusyDates } from '../../hooks/useVehicles';
import { useEquipmentBusyDates } from '../../hooks/useEquipment';
import { usePermissions } from '../../hooks/usePermissions';
import { useRescheduleFlow } from '../../hooks/useRescheduleFlow';
import { ReassignmentDialog } from './reschedule/ReassignmentDialog';
import { PhaseNotesEditor } from './PhaseNotesEditor';
import {
  api,
  REASON_LABELS,
  AddVehiclesConflictError,
  AddEquipmentConflictError,
  ApiHttpError,
} from '../../lib/api';
import type {
  ApiJob,
  ApiJobEquipment,
  ApiJobVehicle,
  ApiVehicle,
  AddVehiclesConflictDetails,
  ApiSkill,
  ScheduleWarning,
  ApiWorkerWarnings,
  ApiPhase,
  PhaseCost,
  PhaseCreateBody,
  PhaseCancelBody,
  // Phase 78-01: allocation-driven Materials section (§4 read contract).
  ApiWorkItemAllocations,
  ApiAllocProduct,
  ApiAllocSource,
  // Phase 78-02: reserve-from-stock picker source.
  ApiStockRow,
  // Issue #255 (Facet 2): specific WTR block reason + computed explainer.
  WtrBlockDetail,
} from '../../lib/api';

const statusLabels: Record<string, string> = {
  in_progress: 'In Progress',
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  postponed: 'Postponed',
  archived: 'Archived',
  draft: 'Draft',
};

// Which transitions a planner can trigger per status, and the button label/style.
// Start Job and Complete Job removed — planning board doesn't need workflow ceremony.
// Cancel Job and Postpone Job are in the footer (gated to scheduled/in_progress).
// Phase 23-05: Reschedule (cancelled→scheduled) and Archive removed — completed,
// postponed, and cancelled are terminal locked states. The const + render path
// stay so future transitions can be added without scaffolding.
const TRANSITIONS: Record<
  string,
  Array<{
    toStatus: ApiJob['status'];
    label: string;
    icon: React.ElementType;
    variant: 'primary' | 'danger' | 'neutral';
  }>
> = {
  scheduled: [],
  in_progress: [],
  completed: [],
  cancelled: [],
};

// ─── Phase 23-05: unified status derivation ───────────────────────────────────
//
// Replaces the two-element status display (lifecycle status pill + phase-status
// dropdown) with ONE pill at the top of the job card. Lifecycle states take
// precedence over commercial states; for status='scheduled' we display the
// commercial state per source (deal/manual → Provisional/Confirmed editable;
// ticket → Non-Chargeable read-only).

interface UnifiedStatus {
  /** User-facing label inside the pill. */
  label: string;
  /** Tailwind classes for the pill background + text colour. */
  pillClass: string;
  /** True when the user can toggle Provisional ↔ Confirmed via the pencil icon. */
  isStatusEditable: boolean;
  /** True when the entire job card becomes read-only (Phase 23-05 T3). */
  isJobLocked: boolean;
}

function computeUnifiedStatus(job: Job): UnifiedStatus {
  // Lifecycle states take precedence over commercial states.
  if (job.status === 'completed') {
    return {
      label: 'Completed',
      pillClass: 'bg-emerald-100 text-emerald-800',
      isStatusEditable: false,
      isJobLocked: true,
    };
  }
  if (job.status === 'postponed') {
    return {
      label: 'Postponed',
      pillClass: 'bg-amber-100 text-amber-800',
      isStatusEditable: false,
      isJobLocked: true,
    };
  }
  if (job.status === 'cancelled') {
    return {
      label: 'Cancelled',
      pillClass: 'bg-rose-100 text-rose-800',
      isStatusEditable: false,
      isJobLocked: true,
    };
  }
  if (job.status === 'in_progress') {
    return {
      label: 'In Progress',
      // Phase 30-01: emerald (green) — same as Completed pill, but mediated by
      // surface differentiation: in_progress renders inside an active card with
      // green border; completed renders inside a tombstone with green badge.
      // States are mutually exclusive on any single job; no within-card collision.
      pillClass: 'bg-emerald-100 text-emerald-800',
      isStatusEditable: false,
      isJobLocked: false,
    };
  }
  if (job.status === 'scheduled') {
    if (job.source === 'hubspot_ticket') {
      return {
        label: 'Non-Chargeable',
        pillClass: 'bg-violet-100 text-violet-800',
        isStatusEditable: false,
        isJobLocked: false,
      };
    }
    // Deal-sourced or manual: Provisional/Confirmed per phaseStatus, with toggle.
    // 80-02 collapse: read the single phaseStatus lifecycle (was the booking-status field).
    // Phase 30-01: red for Provisional, yellow for the committed states (matches
    // the calendar JobCard left-border colour family for the same state).
    const isCommitted = job.phaseStatus === 'confirmed';
    const label = isCommitted ? 'Confirmed' : 'Provisional';
    const pillClass = isCommitted ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';
    return { label, pillClass, isStatusEditable: true, isJobLocked: false };
  }
  // Fallback for draft / archived / unknown — show as-is, locked
  return {
    label: statusLabels[job.status] ?? job.status,
    pillClass: 'bg-neutral-100 text-neutral-700',
    isStatusEditable: false,
    isJobLocked: true,
  };
}

// ─── Phase 32-02: Linked Deal section (Issue #34 sub-item 1) ─────────────────
//
// Renders an inline summary row + popover for ticket-sourced jobs whose ticket
// has an associated HubSpot deal. The 4 linked-deal fields (id/ion/name/client)
// are persisted on work_items by the ticket sync; site address is taken from
// job.location (which equals the deal's siteAddress when location='Client Site
// Address' — the prevailing case for ticket-deal-linked work).
//
// Caller already gates rendering on (source==='hubspot_ticket' && linkedDealId).

/**
 * Phase 41-03 (#66): per-phase Complete checkbox helper.
 *
 * Renders the checkbox + label + inline confirmation copy (for the last-phase
 * cascade case) + inline error reason if the BE rejected with 422
 * PHASE_COMPLETE_BLOCKED.
 *
 * Disabled-state pre-computation deferred: BE is the source of truth for Q4
 * gates (active time entries, scheduled end_ts). The checkbox always renders
 * enabled for non-cancelled phases; click fires the API; 422 errors surface
 * as inline reason text via the parent's errorReason state.
 */
export function PhaseCompleteCheckboxInline({
  phase,
  pendingConfirmPhaseId,
  errorPhaseId,
  errorReason,
  onClick,
  isPending,
}: {
  phase: ApiPhase;
  pendingConfirmPhaseId: string | null;
  errorPhaseId: string | null;
  errorReason: string | null;
  onClick: () => void;
  isPending: boolean;
}) {
  const isTicked = phase.status === 'completed';
  const showConfirm = pendingConfirmPhaseId === phase.id && !isTicked;
  const showError = errorPhaseId === phase.id && errorReason !== null;
  return (
    <div className="flex flex-col gap-1">
      <label className="flex items-center gap-1.5 text-[11px] text-neutral-700 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={isTicked}
          onChange={onClick}
          disabled={isPending}
          className="w-3.5 h-3.5 accent-[#F26522] cursor-pointer disabled:cursor-wait"
        />
        Complete
        {showConfirm && (
          <span className="text-amber-700 italic">— click again to confirm + push to HubSpot</span>
        )}
        {isPending && <Loader2 className="w-3 h-3 animate-spin text-neutral-400" />}
      </label>
      {showError && (
        <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {errorReason}
        </p>
      )}
    </div>
  );
}

/**
 * Phase 47-01 (GH #67): per-phase "Viewable by operatives" checkbox.
 *
 * Reflects the phase's viewable_by_operatives flag (defaults unchecked) and
 * toggles it via the parent's PATCH mutation. The flag gates whether this
 * phase's FUTURE allocations surface on the operative's /time PWA schedule;
 * past/today allocations always show regardless. Backend (requireRole planner)
 * is the authoritative gate — the parent only renders this for editors.
 */
export function PhaseViewableCheckboxInline({
  phase,
  isPending,
  onChange,
}: {
  phase: ApiPhase;
  isPending: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 text-[11px] text-neutral-700 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={phase.viewable_by_operatives}
        onChange={(e) => onChange(e.target.checked)}
        disabled={isPending}
        className="w-3.5 h-3.5 accent-[#F26522] cursor-pointer disabled:cursor-wait"
      />
      Viewable by operatives
      {isPending && <Loader2 className="w-3 h-3 animate-spin text-neutral-400" />}
    </label>
  );
}

// Per-phase profitability: format a money value as GBP to the penny, or null
// when the value is absent (caller decides the placeholder, e.g. "not set" /
// "—"). 2dp so amounts like £1,553.50 render exactly — display only; the
// underlying budget/cost numbers used in calculations are unaffected.
function fmtGBP(n: number | null | undefined): string | null {
  if (n == null) return null;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

// ─── Issue #255 (Facet 2): computed WTR-block explainer ──────────────────────
// Turn a WTR pre-check block into a specific, planner-friendly reason plus a
// worked example — all derived from the violation data (statutory requirement,
// measured gap, the two adjacent shifts). NO AI call. The daily-rest (11h)
// case is Tom's flagged confusion; the weekly-rest case falls back to a plain
// summary since it has no single adjacent shift.

interface WtrExplainer {
  /** Short reason shown inline on the day row. */
  label: string;
  /** Policy sentence. */
  headline: string;
  /** The shift being proposed, e.g. "Tue 02/04, 08:00–17:00". */
  proposedShiftLabel: string;
  /** The clashing existing shift, or null (weekly-rest has no single shift). */
  offendingShiftLabel: string | null;
  /** Job context for the clashing shift ("16200 Night Works"), if known. */
  offendingJobLabel: string | null;
  /** "Only 6h rest between them — 11h is required." */
  gapSentence: string;
  /** Computed resolution hint, or null when it can't be derived cleanly. */
  resolution: string | null;
}

function ddmm(dateStr: string): string {
  return format(new Date(dateStr + 'T00:00:00'), 'EEE dd/MM');
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function fromMinutes(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Absolute wall-clock minutes since epoch for a YYYY-MM-DD + HH:MM (UTC-based). */
function absMinutes(dateStr: string, hhmm: string): number {
  const [y, mo, d] = dateStr.split('-').map(Number);
  return Math.floor(Date.UTC(y, mo - 1, d) / 60000) + toMinutes(hhmm);
}

function describeWtrBlock(
  detail: WtrBlockDetail,
  proposedDate: string,
  proposedStart: string,
  proposedEnd: string,
): WtrExplainer {
  const req = detail.requiredRestHours;
  const gap = detail.measuredRestGapHours;
  const gapText = gap != null ? `${gap}h` : 'less than the required';
  const headline = `Working Time Regulations require ${req} hours' rest between working days.`;
  const proposedShiftLabel = `${ddmm(proposedDate)}, ${proposedStart}–${proposedEnd}`;

  // Weekly-rest violation: no single adjacent shift — keep it simple.
  if (detail.type !== 'daily_rest_violation') {
    return {
      label: `Weekly rest required (${req}h in 14 days)`,
      headline: `Working Time Regulations require a ${req}-hour continuous rest period in every 14 days.`,
      proposedShiftLabel,
      offendingShiftLabel: null,
      offendingJobLabel: null,
      gapSentence: detail.reason,
      resolution: null,
    };
  }

  const offending = detail.offendingShifts[0] ?? null;
  // Adjacent date: prefer the resolved shift's date; else the window date that
  // isn't the proposed day.
  const adjacentDate =
    offending?.date ??
    (detail.windowStart === proposedDate ? detail.windowEnd : detail.windowStart);
  const label = `${req}h rest gap to the ${format(new Date(adjacentDate + 'T00:00:00'), 'dd/MM')} shift`;

  if (!offending) {
    return {
      label,
      headline,
      proposedShiftLabel,
      offendingShiftLabel: null,
      offendingJobLabel: null,
      gapSentence: `This would leave only ${gapText} rest — ${req}h is required.`,
      resolution: null,
    };
  }

  const offendingIsAfter = offending.date > proposedDate;
  const offendingShiftLabel = `${ddmm(offending.date)}, ${offending.startTime}–${offending.endTime}`;
  const offendingJobLabel =
    offending.ion || offending.jobName
      ? `${offending.ion ?? ''}${offending.jobName ? ` ${extractDealName(offending.jobName, offending.ion ?? '')}` : ''}`.trim()
      : null;

  const gapSentence = offendingIsAfter
    ? `Assigning this shift would leave only ${gapText} before their next shift (${ddmm(offending.date)} ${offending.startTime}) — ${req}h is required.`
    : `Their previous shift (${ddmm(offending.date)} ${offending.endTime}) would leave only ${gapText} before this shift starts — ${req}h is required.`;

  // Computed resolution (correct cross-day arithmetic — an 11h gap to an
  // adjacent-day shift spans midnight). Only shown when trimming/shifting THIS
  // shift on its own day would resolve it; otherwise omit (the real fix is to
  // move the shift to another day).
  const dayStartAbs = absMinutes(proposedDate, '00:00');
  let resolution: string | null = null;
  if (offendingIsAfter) {
    const latestFinishAbs = absMinutes(offending.date, offending.startTime) - req * 60;
    const tod = latestFinishAbs - dayStartAbs;
    if (tod >= 0 && tod < 1440 && latestFinishAbs >= absMinutes(proposedDate, proposedStart)) {
      resolution = `Finish this shift by ${fromMinutes(tod)} (instead of ${proposedEnd}) to leave ${req}h before the next shift.`;
    }
  } else {
    const earliestStartAbs = absMinutes(offending.date, offending.endTime) + req * 60;
    const tod = earliestStartAbs - dayStartAbs;
    if (tod >= 0 && tod < 1440 && earliestStartAbs <= absMinutes(proposedDate, proposedEnd)) {
      resolution = `Start this shift no earlier than ${fromMinutes(tod)} (instead of ${proposedStart}) to leave ${req}h after the previous shift.`;
    }
  }

  return {
    label,
    headline,
    proposedShiftLabel,
    offendingShiftLabel,
    offendingJobLabel,
    gapSentence,
    resolution,
  };
}

// Per-phase profitability money block. Rendered ONLY when the caller has
// confirmed the user can see money (can('contracts_board','update')).
//   - Chargeable (deal/manual): Amount (job-level) + per-phase Budget · Cost ·
//     Margin (gross_profit £ + margin_pct %); "estimated" marker when the labour
//     cost fell back to a blended rate. Budget follows the panel's standard
//     inline-edit pattern (value + edit pencil → input + confirm/cancel; #245)
//     until the phase is confirmed, then read-only + locked (🔒).
//   - Ticket (non-chargeable): only "Cost to business" = total_cost.
function PhaseProfitabilityInline({
  phaseCost,
  phase,
  isJobLocked,
  isTicket,
  jobAmount,
  isPhased,
  allPhasesBudgetTotal,
  isSaving,
  onSaveBudget,
}: {
  phaseCost: PhaseCost | undefined;
  phase: ApiPhase | null;
  isJobLocked: boolean;
  isTicket: boolean;
  jobAmount: number | null;
  // True for jobs with ≥2 phases. Phased jobs relabel "Amount" → "Amount for
  // all phases" and append a budget-reconciliation status (Σ phase budgets vs
  // the job Amount). Single-phase jobs keep the plain "Amount" row.
  isPhased: boolean;
  // JOB-level Σ of every phase's current budget (live from phase-costs).
  allPhasesBudgetTotal: number;
  isSaving: boolean;
  onSaveBudget: (phaseId: string, value: string | null) => void;
}) {
  // Standard inline-edit pattern (mirrors the Contracts Manager pencil/✓/✗;
  // #245): display the saved budget + an edit pencil; the pencil opens the input
  // with confirm/cancel. The draft is seeded from the phase's persisted budget.
  // Callers pass `key={phase.id}` so switching phases in the navigator remounts
  // this component and re-seeds the draft (avoids a setState-in-effect re-sync).
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState<string>(phase?.phase_budget ?? '');

  // Close edit mode once a save round-trip completes (isSaving true → false),
  // matching how the Contracts Manager edit closes on mutation success.
  const wasSavingRef = useRef(false);
  useEffect(() => {
    if (wasSavingRef.current && !isSaving) setIsEditingBudget(false);
    wasSavingRef.current = isSaving;
  }, [isSaving]);

  const rowClass = 'flex items-center justify-between gap-2';
  const labelClass = 'text-neutral-500';

  if (isTicket) {
    return (
      <div
        data-testid="phase-cost-to-business"
        className="mt-1 pt-1.5 border-t border-neutral-100 space-y-1"
      >
        <div className={rowClass}>
          <span className={labelClass}>Cost to business</span>
          <span className="font-semibold text-neutral-900">
            {fmtGBP(phaseCost?.total_cost) ?? '£0'}
          </span>
        </div>
        <p className="text-[10px] text-neutral-400">
          Non-chargeable
          {phaseCost?.labour_cost_estimated ? ' · labour estimated' : ''}
        </p>
      </div>
    );
  }

  // Preserve the parent/snapshot read-only cascade and the backend's terminal
  // phase guards, as well as the commercial lock on confirmed phases.
  const locked =
    isJobLocked ||
    phase?.status === 'confirmed' ||
    phase?.status === 'completed' ||
    phase?.status === 'cancelled';
  const lockedBudget = phase?.phase_budget != null ? fmtGBP(Number(phase.phase_budget)) : null;

  // Phased jobs: reconcile the sum of ALL phases' budgets against the job
  // Amount. Within ±£0.01 → on budget; Σ over → over budget; Σ under → under.
  // Only shown when the Amount is known (jobAmount non-null).
  let budgetReconSuffix: string | null = null;
  if (isPhased && jobAmount != null) {
    const delta = allPhasesBudgetTotal - jobAmount;
    if (Math.abs(delta) <= 0.01) budgetReconSuffix = '(on budget)';
    else if (delta > 0) budgetReconSuffix = `(${fmtGBP(delta)} over budget)`;
    else budgetReconSuffix = `(${fmtGBP(-delta)} under budget)`;
  }

  return (
    <div
      data-testid="phase-profitability"
      className="mt-1 pt-1.5 border-t border-neutral-100 space-y-1"
    >
      <div className={rowClass}>
        <span className={labelClass}>{isPhased ? 'Amount for all phases' : 'Amount'}</span>
        <span className="font-semibold text-neutral-900">
          {fmtGBP(jobAmount) ?? 'not set'}
          {budgetReconSuffix && (
            <span className="ml-1 text-neutral-500 font-normal">{budgetReconSuffix}</span>
          )}
        </span>
      </div>
      <div className={rowClass}>
        <span className={labelClass}>Budget</span>
        {locked || !phase ? (
          <span className="inline-flex items-center gap-1 font-semibold text-neutral-900">
            {lockedBudget ?? 'not set'}
            <span data-testid="budget-locked" title="Budget locked for this job or phase">
              🔒
            </span>
          </span>
        ) : isEditingBudget ? (
          <span className="inline-flex items-center gap-1">
            <span className="text-neutral-400">£</span>
            <input
              type="number"
              min="0"
              step="1"
              aria-label="Phase budget"
              value={budgetDraft}
              onChange={(e) => setBudgetDraft(e.target.value)}
              disabled={isSaving}
              placeholder="0"
              autoFocus
              className="w-20 text-right text-[11px] px-1.5 py-0.5 rounded border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-[#F26522] disabled:opacity-50"
            />
            <button
              type="button"
              onClick={() =>
                onSaveBudget(phase.id, budgetDraft.trim() === '' ? null : budgetDraft.trim())
              }
              disabled={isSaving}
              className="p-1 text-green-600 hover:text-green-800 transition-colors disabled:opacity-50"
              aria-label="Save budget"
            >
              {isSaving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Check className="w-3.5 h-3.5" />
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setBudgetDraft(phase.phase_budget ?? '');
                setIsEditingBudget(false);
              }}
              disabled={isSaving}
              className="p-1 text-neutral-400 hover:text-neutral-600 transition-colors disabled:opacity-50"
              aria-label="Cancel budget edit"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 font-semibold text-neutral-900">
            {lockedBudget ?? 'not set'}
            <button
              type="button"
              onClick={() => {
                setBudgetDraft(phase.phase_budget ?? '');
                setIsEditingBudget(true);
              }}
              className="p-1 text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Edit budget"
            >
              <Pencil className="w-3 h-3" />
            </button>
          </span>
        )}
      </div>
      <div className={rowClass}>
        <span className={labelClass}>Cost</span>
        <span className="font-semibold text-neutral-900">
          {fmtGBP(phaseCost?.total_cost) ?? '—'}
        </span>
      </div>
      <div className={rowClass}>
        <span className={labelClass}>Margin</span>
        <span className="font-semibold text-neutral-900">
          {fmtGBP(phaseCost?.gross_profit) ?? '—'}
          {phaseCost?.margin_pct != null && (
            <span className="ml-1 text-neutral-500 font-normal">
              ({phaseCost.margin_pct.toFixed(1)}%)
            </span>
          )}
        </span>
      </div>
      {phaseCost?.labour_cost_estimated && (
        <p className="text-[10px] text-amber-600">Cost includes estimated labour</p>
      )}
    </div>
  );
}

function LinkedDealSection({ job }: { job: Job }) {
  const dealUrl =
    job.sourcePortalId &&
    /^\d+$/.test(job.sourcePortalId) &&
    job.linkedDealId &&
    /^\d+$/.test(job.linkedDealId)
      ? `https://app.hubspot.com/contacts/${job.sourcePortalId}/deal/${job.linkedDealId}`
      : null;
  const dealName = job.linkedDealName ?? '—';
  const client = job.linkedDealClient ?? '—';
  const ion = job.linkedDealIon ?? '—';
  // Phase 32-05: source the deal's site_address (the customer's location),
  // NOT job.location (this ticket's resolved worksite). The two diverge for
  // tickets whose location enum is G2 / HQ / Employees Home Address — the
  // popover is Linked Deal context, so it shows where the customer is, not
  // where the ticket's work happens. Falls back to '—' when the deal has no
  // site_address property set in HubSpot.
  const siteAddress = job.linkedDealSiteAddress ?? '—';

  return (
    <div
      data-testid="linked-deal-section"
      className="flex flex-wrap items-center gap-x-1.5 gap-y-1 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-[12px] text-neutral-700"
    >
      <span className="font-medium text-neutral-500 uppercase tracking-wide text-[10px]">
        Linked Deal
      </span>
      <span className="text-neutral-300">·</span>
      <span className="font-mono text-neutral-600">{ion}</span>
      <span className="text-neutral-300">·</span>
      <span className="text-neutral-800">{dealName}</span>
      <span className="text-neutral-300">·</span>
      <span className="text-neutral-700">{client}</span>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="ml-auto rounded border border-neutral-300 bg-white px-2 py-0.5 text-[11px] text-neutral-700 hover:bg-neutral-100 transition-colors"
          >
            View Deal
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="space-y-2" data-testid="linked-deal-popover">
          <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-500">
            Linked Deal
          </div>
          <dl className="grid grid-cols-[80px_1fr] gap-y-1.5 gap-x-2 text-[12px]">
            <dt className="text-neutral-500">ION</dt>
            <dd className="font-mono text-neutral-800">{ion}</dd>
            <dt className="text-neutral-500">Deal Name</dt>
            <dd className="text-neutral-800">{dealName}</dd>
            <dt className="text-neutral-500">Client</dt>
            <dd className="text-neutral-800">{client}</dd>
            <dt className="text-neutral-500">Site Address</dt>
            <dd className="text-neutral-800">{siteAddress}</dd>
          </dl>
          {dealUrl && (
            <a
              href={dealUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full rounded border border-neutral-300 bg-white px-2 py-1 text-center text-[11px] text-neutral-700 hover:bg-neutral-100 transition-colors"
            >
              Open in HubSpot
            </a>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Phase 78-01 (§4): allocation lifecycle status → human label for the badge.
const ALLOCATION_STATUS_LABELS: Record<ApiAllocSource['status'], string> = {
  on_order: 'On Order',
  delivered: 'Delivered',
  allocated: 'Allocated',
  checked_out: 'Checked Out',
  consumed: 'Consumed',
  returned: 'Returned',
  released: 'Released',
  cancelled: 'Cancelled',
};

// Tailwind palette per status — kept subtle/read-only; mirrors the panel's
// other inline pill styling.
const ALLOCATION_STATUS_BADGE: Record<ApiAllocSource['status'], string> = {
  on_order: 'bg-amber-100 text-amber-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  allocated: 'bg-sky-100 text-sky-800',
  checked_out: 'bg-indigo-100 text-indigo-800',
  consumed: 'bg-neutral-200 text-neutral-700',
  returned: 'bg-teal-100 text-teal-800',
  released: 'bg-neutral-100 text-neutral-600',
  cancelled: 'bg-red-100 text-red-700',
};

// Phase 78-08: derive the per-stock-source disposition split from the raw
// counters returned by the §4 read, using Materials' EXACT formula
// (allocation-read-service.ts). A single stock row can simultaneously hold qty
// across several dispositions — a partially-checked-out reservation keeps
// status='checked_out' but still has (qty − qty_checked_out) reserved. Only the
// non-zero parts are rendered, reusing the lifecycle badge palette. Historical
// rows without disposition counters retain a plain lifecycle status badge.
function stockDisposition(s: ApiAllocSource): { key: string; label: string; badge: string }[] {
  const qty = Number(s.qty) || 0;
  const checkedOutCounter = Number(s.qty_checked_out) || 0;
  const consumed = Number(s.qty_consumed) || 0;
  const returned = Number(s.qty_returned) || 0;

  // Close sweep retains the original quantity/counters for history. Only open
  // stock allocations contribute live reservations/outstanding quantities,
  // matching allocation-read-service's stock summary lifecycle filter.
  const isOpen = s.status === 'allocated' || s.status === 'checked_out';
  const reserved = isOpen ? Math.max(qty - checkedOutCounter, 0) : 0;
  const checkedOut = isOpen ? checkedOutCounter - returned - consumed : 0;

  const dispositions = [
    {
      key: 'reserved',
      value: reserved,
      word: 'reserved',
      badge: ALLOCATION_STATUS_BADGE.allocated,
    },
    {
      key: 'checked_out',
      value: checkedOut,
      word: 'checked out',
      badge: ALLOCATION_STATUS_BADGE.checked_out,
    },
    { key: 'consumed', value: consumed, word: 'consumed', badge: ALLOCATION_STATUS_BADGE.consumed },
    { key: 'returned', value: returned, word: 'returned', badge: ALLOCATION_STATUS_BADGE.returned },
  ]
    .filter((d) => d.value > 0)
    .map((d) => ({ key: d.key, label: `${d.value} ${d.word}`, badge: d.badge }));

  return dispositions.length > 0
    ? dispositions
    : [
        {
          key: s.status,
          label: ALLOCATION_STATUS_LABELS[s.status],
          badge: ALLOCATION_STATUS_BADGE[s.status],
        },
      ];
}

/**
 * Phase 78-01: render one allocation product (name + unit + total) with a row
 * per source (qty · source label · status badge; PO ref + delivery status for
 * job_po). Stock sources show a reserved/checked-out/consumed/returned
 * disposition split (Phase 78-08) instead of a single badge. Read-only. Cost
 * (unit_cost) is shown only when `showCost` is true (viewers excluded — parity
 * with the prior cost_each gating).
 */
function MaterialAllocationProduct({
  product,
  showCost,
  canEdit = false,
  onRelease,
  releasing = false,
}: {
  product: ApiAllocProduct;
  showCost: boolean;
  // Phase 78-02: planner release control. A "Release" button renders only on a
  // stock-sourced source row in `allocated` state (not checked out / consumed /
  // returned / released / cancelled, never job_po) when canEdit is true.
  canEdit?: boolean;
  onRelease?: (allocationId: string) => void;
  releasing?: boolean;
}) {
  return (
    <div className="text-[12px] text-neutral-700 bg-neutral-50 border border-neutral-200 rounded px-2 py-1.5">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-medium">{product.product_name}</span>
        <span className="text-neutral-400">
          {product.item_code ? `${product.item_code} · ` : ''}Catalogue pack:{' '}
          {product.catalogue_pack_description ?? 'size not recorded'}
        </span>
        <span className="text-neutral-400">· total {Number(product.total_qty)} packs</span>
      </div>
      <div className="mt-1 space-y-1">
        {product.sources.map((s, idx) => {
          const sourceLabel =
            s.source === 'job_po' ? `PO #${s.po_number ?? '—'}` : 'Existing Stock';
          // For job_po rows: a present delivery_id means goods received → Delivered.
          const deliveryStatus = s.source === 'job_po' && s.delivery_id ? 'Delivered' : null;
          // Phase 78-02: a reservation is releasable only while still
          // `allocated` (not yet checked out) and stock-sourced.
          const canRelease =
            canEdit && s.source === 'stock' && s.status === 'allocated' && !!s.id && !!onRelease;
          return (
            <div
              key={`${product.catalog_row_id}-${idx}`}
              className="flex items-center gap-1.5 flex-wrap"
            >
              <span className="font-medium text-neutral-700">{Number(s.qty)} packs</span>
              <span className="text-neutral-500">{sourceLabel}</span>
              {s.source === 'stock' ? (
                // Phase 78-08: a stock row carries a single lifecycle `status`,
                // but a partially-progressed reservation actually spans several
                // dispositions at once (e.g. 5 = 1 reserved + 3 checked out + 1
                // consumed). Render the same reserved/checked-out/consumed/returned
                // split the Stock page derives from the counters (Materials'
                // allocation-read-service formula), showing only the non-zero parts.
                stockDisposition(s).map(({ key, label, badge }) => (
                  <span
                    key={key}
                    className={clsx(
                      'inline-block text-[10px] px-1.5 py-0.5 rounded font-semibold',
                      badge,
                    )}
                  >
                    {label}
                  </span>
                ))
              ) : (
                <span
                  className={clsx(
                    'inline-block text-[10px] px-1.5 py-0.5 rounded font-semibold',
                    ALLOCATION_STATUS_BADGE[s.status],
                  )}
                >
                  {ALLOCATION_STATUS_LABELS[s.status]}
                </span>
              )}
              {deliveryStatus && (
                <span className="text-[11px] text-neutral-400">{deliveryStatus}</span>
              )}
              {showCost && s.unit_cost != null && (
                <span className="text-[11px] text-neutral-400 ml-auto">£{Number(s.unit_cost)}</span>
              )}
              {canRelease && (
                <button
                  type="button"
                  onClick={() => onRelease!(s.id!)}
                  disabled={releasing}
                  className={clsx(
                    'text-[10px] px-1.5 py-0.5 rounded border border-neutral-300 text-neutral-600 hover:bg-neutral-100 hover:text-red-600 transition-colors disabled:opacity-50',
                    showCost && s.unit_cost != null ? 'ml-1.5' : 'ml-auto',
                  )}
                  title="Release this reservation back to available stock"
                >
                  Release
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function isScopeMap(value: unknown): value is Record<string, string> {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.values(value).every((scope) => typeof scope === 'string')
  );
}

export function JobDetailPanel({
  job,
  staffMap,
  selectedDate,
  phaseContext,
  postponementContext,
  onClose,
  onTransitioned,
  onEdited,
  expandSection,
}: {
  job: Job;
  staffMap: Map<string, Staff>;
  selectedDate?: Date;
  // #102: when opened via a JobCard "+N" overflow chip, pre-expand the named
  // collapsible section (the panel mounts fresh on each open, so seeding the
  // section's initial useState from this is sufficient — no effect needed).
  expandSection?: 'vehicles' | 'staff' | null;
  // Phase 39-07: the panel's "active phase" defaults to phaseContext.phaseId
  // when opened from a board card. The dropdown navigator (T1) lets the
  // planner switch active phase inside the panel. Null when the panel was
  // opened from a non-board context (e.g. Available Work) — falls back to
  // earliest non-cancelled phase from the live phase list.
  phaseContext?: {
    phaseId: string;
    phaseNumber: number;
    phaseStatus: string | null;
    phaseCountTotal: number;
  } | null;
  // Phase 39-07 (closes DEFERRED-39-05-D): set when the panel was opened
  // from a postponement-snapshot tombstone on the calendar. Renders an
  // informational banner above the navigator. The panel below the banner
  // continues to show the LIVE parent job state (not the snapshot's frozen
  // state — read-only-mode-on-snapshot is separate scope).
  postponementContext?: {
    postponedAt: string; // ISO timestamp
    postponedBy: string | null; // staff_id; staffMap can resolve to name
    postponedReason: string | null;
    phaseNumber: number;
  } | null;
  onClose: () => void;
  onTransitioned?: (updatedJob: ApiJob) => void;
  onEdited?: () => void;
}) {
  const queryClient = useQueryClient();
  const [cancelReason, setCancelReason] = useState('');
  const [pendingCancel, setPendingCancel] = useState(false);
  const [pendingReschedule, setPendingReschedule] = useState(false);
  const [pendingPostpone, setPendingPostpone] = useState(false);
  // GH #108 (part 2): the postpone modal carries a "Reason for postponing"
  // textarea. Threaded to api.jobs.postpone (optional; persists as
  // postponed_reason on the BE snapshot, Migration 065).
  const [postponeReason, setPostponeReason] = useState('');

  // ── Phase 39-07: per-phase navigator + Add/Cancel Phase state ──────────────
  // Active-phase state for the navigator dropdown. Defaults to the prop-passed
  // phaseContext.phaseId (the phase whose card was clicked on the calendar);
  // falls back to the earliest non-cancelled phase once `phases` loads if no
  // context was provided (e.g. panel opened from Available Work).
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(
    phaseContext?.phaseId ?? null,
  );
  const [pendingAddPhase, setPendingAddPhase] = useState(false);
  const [pendingCancelPhase, setPendingCancelPhase] = useState(false);
  const [phaseFormStartDate, setPhaseFormStartDate] = useState('');
  const [phaseFormEndDate, setPhaseFormEndDate] = useState('');
  const [phaseFormStatus, setPhaseFormStatus] = useState<'provisional' | 'confirmed'>(
    'provisional',
  );
  const [phaseFormNotes, setPhaseFormNotes] = useState('');
  // Phase 39-07 follow-up: CM picker on Add Phase. Without this the new
  // phase started with NULL CM and the planner had to set it via the pencil
  // afterwards — surfaced as confusing UX during Tom's UAT smoke.
  const [phaseFormContractsManagerId, setPhaseFormContractsManagerId] = useState('');
  const [cancelPhaseReason, setCancelPhaseReason] = useState('');
  // Collapsible section state. #102: a "+N" deep-link opens the panel with the
  // corresponding section already expanded (the panel mounts fresh per open).
  const [staffOpen, setStaffOpen] = useState(expandSection === 'staff');
  const [vehiclesOpen, setVehiclesOpen] = useState(expandSection === 'vehicles');
  const [equipmentOpen, setEquipmentOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);

  // Phase 78-02: reserve-from-stock form state (planner-gated). The picker is
  // sourced from api.materials.stock(); reserveCatalogRowId selects the product,
  // reserveQty the amount. reserveError holds the inline 409 over-available
  // message so a server reject never escapes as an uncaught throw.
  const [showReserveStock, setShowReserveStock] = useState(false);
  const [reserveCatalogRowId, setReserveCatalogRowId] = useState('');
  const [reserveQty, setReserveQty] = useState('');
  const [reserveError, setReserveError] = useState<string | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  // Equipment add state
  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [newEquipmentItemId, setNewEquipmentItemId] = useState('');
  // Phase 36-02 (Issue #37): bulk-add multi-date + quantity (drops the
  // single-date input from 36-01's shim).
  const [newEquipmentDates, setNewEquipmentDates] = useState<string[]>([]);
  const [newEquipmentQuantity, setNewEquipmentQuantity] = useState(1);
  const [equipmentAddConflict, setEquipmentAddConflict] = useState<string[]>([]);
  const [equipmentAddError, setEquipmentAddError] = useState<string | null>(null);
  // Phase 36-02 mid-loop bundle (Tom UAT Q1=a): pen icon → AlertDialog modal
  // with quantity dropdown + Save/Cancel. Replaces the inline-click "N ×"
  // pattern from the original 36-02 ship (3bc902c).
  const [editingEquipmentRow, setEditingEquipmentRow] = useState<ApiJobEquipment | null>(null);
  const [editingEquipmentQty, setEditingEquipmentQty] = useState(1);

  // Vehicles add state (Phase 34-03 — Issue #36)
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [newVehicleId, setNewVehicleId] = useState('');
  const [newVehicleDates, setNewVehicleDates] = useState<string[]>([]);
  const [vehicleAddError, setVehicleAddError] = useState<string | null>(null);
  const [vehicleAddConflicts, setVehicleAddConflicts] = useState<
    AddVehiclesConflictDetails['conflicts']
  >([]);

  // Staff assignment state
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [newStaffId, setNewStaffId] = useState('');
  const [newStartTime, setNewStartTime] = useState('08:00');
  const [newEndTime, setNewEndTime] = useState('17:00');

  // Per-worker "Assignment warnings" banner: dismiss + expand state. The
  // warnings themselves come from the persistent phase-warnings query (below),
  // replacing the old transient `assignmentWarnings` state that only reflected
  // the last mutation's response.
  const [warningsDismissed, setWarningsDismissed] = useState(false);
  const [expandedWarningStaffIds, setExpandedWarningStaffIds] = useState<Set<string>>(new Set());

  // Staff expand/edit state
  const [expandedStaffIds, setExpandedStaffIds] = useState<Set<string>>(new Set());
  // Issue #222: per-vehicle expand state — mirrors expandedStaffIds. A collapsed
  // vehicle shows only the selected day; expanding reveals the vehicle's other
  // days on this job/phase.
  const [expandedVehicleIds, setExpandedVehicleIds] = useState<Set<string>>(new Set());
  const [editingAssignmentId, setEditingAssignmentId] = useState<string | null>(null);
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');

  // Per-day grid state (multi-day jobs)
  const [showPerDayGrid, setShowPerDayGrid] = useState(false);
  const [perDaySegments, setPerDaySegments] = useState<
    {
      date: string;
      startTime: string;
      endTime: string;
      isLeave: boolean;
      isExisting: boolean;
      isConflict: boolean;
      conflictLabel: string;
      isWtrBlocked: boolean;
      wtrReason: string;
      wtrDetail: WtrBlockDetail | null;
      enabled: boolean;
    }[]
  >([]);

  // Inline date editing state
  const [dateEditMode, setDateEditMode] = useState(false);
  const [editStartDate, setEditStartDate] = useState(job.startDate);
  const [editEndDate, setEditEndDate] = useState(
    job.durationDays > 1
      ? new Date(job.date.getTime() + (job.durationDays - 1) * 86400000).toISOString().slice(0, 10)
      : '',
  );

  /**
   * Phase 23-02: when start_date changes, auto-suggest a new end_date that
   * preserves the existing duration. Soft suggestion — user can override
   * by typing in the end_date input directly. The "duration" is read from
   * the CURRENT state (editStartDate, editEndDate) at the moment of change,
   * so subsequent start_date edits respect any manual end_date overrides
   * (no separate anchor state needed; the inputs themselves are the anchor).
   */
  function handleStartDateChange(newStart: string) {
    const oldStart = editStartDate;
    const oldEnd = editEndDate;
    setEditStartDate(newStart);

    if (!newStart) return;
    const newStartMs = new Date(newStart).getTime();
    if (!Number.isFinite(newStartMs)) return;

    // Phase 40-01 fixup #3 (Tom UAT 2026-05-27): the prior implementation
    // derived durationMs from (editEndDate - editStartDate) and bailed when
    // that was NaN/<=0. Date inputs fire onChange during typing with
    // intermediate values like "2026-05-2" that parse as NaN, which gets
    // stored in editStartDate. The next valid keystroke then reads the bad
    // oldStart, computes NaN duration, and bails — end_date never auto-
    // shifts even though the user finished typing a valid date. Fix: if the
    // old anchor pair can't be parsed cleanly, fall back to the ACTIVE
    // PHASE's duration (panelDurationDays). User overrides of editEndDate
    // still re-anchor on subsequent edits via the fast path.
    let durationMs: number | null = null;
    if (oldEnd && oldStart) {
      const oldStartMs = new Date(oldStart).getTime();
      const oldEndMs = new Date(oldEnd).getTime();
      const candidate = oldEndMs - oldStartMs;
      if (Number.isFinite(candidate) && candidate >= 0) {
        durationMs = candidate;
      }
    }
    if (durationMs === null) {
      // Fall back to the active phase's duration (or zero for single-day).
      durationMs = (panelDurationDays - 1) * 86400000;
    }

    const newEnd = new Date(newStartMs + durationMs).toISOString().slice(0, 10);
    setEditEndDate(newEnd);
  }
  const [saveError, setSaveError] = useState<string | null>(null);
  const [prunedWarning, setPrunedWarning] = useState<string | null>(null);

  // Inline scope editing
  const [scopeEditMode, setScopeEditMode] = useState(false);
  const [inlineDailyScopes, setInlineDailyScopes] = useState<Record<string, string>>({});
  const [inlineScopeAllDays, setInlineScopeAllDays] = useState(false);

  // Contracts manager inline editing
  const [editingManager, setEditingManager] = useState(false);
  const [pendingManagerId, setPendingManagerId] = useState('');

  // Team Leader confirmation dialog state. Supports bulk-apply on collapsed
  // staff cards (Phase 26-06): assignmentIds is the full set of target
  // assignments to flip TL=true on confirm; conflicts enumerates the per-date
  // collisions that must be overwritten. For a single-day click (expanded
  // state) assignmentIds has length 1.
  const [tlConfirm, setTlConfirm] = useState<{
    assignmentIds: string[];
    staffName: string;
    conflicts: { date: string; currentTlName: string }[];
  } | null>(null);

  // Phase 47 (GH #104): whole-phase reassignment flow (dialog state + apply
  // mutation + dry-run preview) lives in useRescheduleFlow, shared with the
  // drag-and-drop board (GH #217). Opened by the Save click when the phase
  // dates changed; the hook runs a dry-run preview, shows the staff + vehicle
  // + equipment conflict analysis, then offers transfer / clear / cancel.
  const reschedule = useRescheduleFlow();

  // Add-days-to-existing-staff state
  const [addDaysStaffId, setAddDaysStaffId] = useState<string | null>(null);
  const [addDaysSegments, setAddDaysSegments] = useState<
    {
      date: string;
      startTime: string;
      endTime: string;
      isLeave: boolean;
      isConflict: boolean;
      conflictLabel: string;
      enabled: boolean;
    }[]
  >([]);

  // Derived: warn the planner when they are moving the start date.
  const today = new Date().toISOString().slice(0, 10);
  const startDateChanged = dateEditMode && editStartDate !== job.startDate;
  const startDateInPast = dateEditMode && !!editStartDate && editStartDate < today;

  const calendarDateKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;

  // Phase 39-07 / 40-01: phases data + active-phase scoping. Loaded here
  // (early in the body) so the phase-scoped date derivations are available
  // to jobDays + downstream affordances below. The phases useQuery itself is
  // declared further down (around line ~940) for grouping reasons; pull the
  // same data here via the same queryKey so React Query dedupes.
  const phasesQueryEarly = useQuery({
    queryKey: ['phases', job.id],
    queryFn: () => api.phases.list(job.id),
    staleTime: 0,
  });
  const phasesEarly: ApiPhase[] = phasesQueryEarly.data ?? [];
  const activePhaseEarly = selectedPhaseId
    ? phasesEarly.find((p) => p.id === selectedPhaseId)
    : null;
  // Keep a calendar day only while it belongs to the selected phase. Switching
  // to a disjoint phase must not hide its resources or edit the old day's scope.
  const selectedDateKey =
    calendarDateKey &&
    activePhaseEarly &&
    (calendarDateKey < activePhaseEarly.start_date ||
      calendarDateKey > (activePhaseEarly.end_date ?? activePhaseEarly.start_date))
      ? activePhaseEarly.start_date
      : calendarDateKey;
  const scopeValues = activePhaseEarly ? activePhaseEarly.daily_scopes : job.dailyScopes;
  const phaseScopeReady =
    phasesQueryEarly.isSuccess &&
    (selectedPhaseId ? activePhaseEarly != null : phasesEarly.length === 0) &&
    isScopeMap(scopeValues);
  const activeDailyScopes = phaseScopeReady && isScopeMap(scopeValues) ? scopeValues : {};

  // Phase 40-01 fixup: phase-scoped date derivations. The Job projection from
  // GET /jobs / adaptJob carries Phase 1's dates as `job.startDate` / `job.date`
  // / `job.durationDays` (legacy single-phase projection). For multi-phase jobs
  // where Phase 1 is single-day but later phases are multi-day, this would
  // hide the multi-day staff-add grid when viewing a later multi-day phase.
  // Derive phase-scoped values here so jobDays + the per-day grid + staff-leave
  // range query + assignment date default all reflect the ACTIVE phase, not
  // the parent job's Phase-1-projected range. Date-edit pencil + cascade KEEP
  // using job.* fields — those edit the parent job, not a phase.
  // Migration 067 backs this up at the DB: assignment_segments.date must fall
  // within the phase's range, so the FE must scope to phase dates too.
  // Phase 40-01 fixup #2 (Tom UAT 2026-05-27): date strings are parsed WITHOUT
  // 'T00:00:00' so they're date-only ISO → UTC midnight (per spec). The earlier
  // 'T00:00:00' form is "local time" per spec and Chrome respects that, which
  // in BST puts panelDate at UTC 23:00 of the previous day. `+N days` then
  // `.toISOString().slice(0,10)` would land 1 day short — causing the auto-
  // suggest to compute wrong end-dates when the planner shifts the start. The
  // rest of the codebase (adaptJob → job.date) uses the date-only form, so
  // staying consistent here makes the math line up across renders.
  const panelStartDate = activePhaseEarly?.start_date ?? job.startDate;
  const panelDate = activePhaseEarly ? new Date(activePhaseEarly.start_date) : job.date;
  const panelDurationDays =
    activePhaseEarly && activePhaseEarly.end_date
      ? Math.ceil(
          (new Date(activePhaseEarly.end_date).getTime() -
            new Date(activePhaseEarly.start_date).getTime()) /
            86400000,
        ) + 1
      : job.durationDays;

  // All calendar days for the active phase — drives per-day scope editor,
  // missing-TL warning, multi-day staff-add grid. Phase 40-01: phase-scoped
  // (was job-scoped pre-40-01 which hid affordances on multi-day phases of
  // jobs where Phase 1 is single-day).
  const jobDays = Array.from({ length: panelDurationDays }, (_, i) =>
    format(addDays(panelDate, i), 'yyyy-MM-dd'),
  );

  // Scope displayed staff and same-phase duplicate checks to the active phase.
  // job.assignedStaff (unfiltered) is still used for cross-phase conflict checks.
  const phaseAssignedStaff = selectedPhaseId
    ? job.assignedStaff.filter((a) => a.phaseId === selectedPhaseId || a.phaseId == null)
    : job.assignedStaff;

  // Group all assignments by staffId for the collapsed/expanded staff section
  const staffGroups = Array.from(
    phaseAssignedStaff
      .reduce((map, a) => {
        if (!map.has(a.staffId)) {
          map.set(a.staffId, {
            staffId: a.staffId,
            staffRecord: staffMap.get(a.staffId),
            allAssignments: [] as typeof job.assignedStaff,
          });
        }
        map.get(a.staffId)!.allAssignments.push(a);
        return map;
      }, new Map<string, { staffId: string; staffRecord: Staff | undefined; allAssignments: typeof job.assignedStaff }>())
      .values(),
  ).sort((a, b) => {
    // Reprotec staff alphabetically first, then subcontractors alphabetically
    const aSub = a.staffRecord?.role === 'subcontractor' ? 1 : 0;
    const bSub = b.staffRecord?.role === 'subcontractor' ? 1 : 0;
    if (aSub !== bSub) return aSub - bSub;
    const aName = a.staffRecord?.knownAs || a.staffRecord?.name || '';
    const bName = b.staffRecord?.knownAs || b.staffRecord?.name || '';
    return aName.localeCompare(bName);
  });

  // Dates with Reprotec operatives assigned but no Team Leader
  // Phase 39-07 (resource-scoping merge): scope to the active phase so the
  // missing-TL banner only flags days within the displayed phase.
  const datesMissingTl = (() => {
    const operativeAssignments = phaseAssignedStaff.filter((a) => {
      const staff = staffMap.get(a.staffId);
      return staff && staff.role === 'operative';
    });
    if (operativeAssignments.length === 0) return [];
    // Group by date, check if any has isTeamLeader
    const byDate = new Map<string, boolean>();
    for (const a of operativeAssignments) {
      const hasTl = byDate.get(a.date) || false;
      byDate.set(a.date, hasTl || a.isTeamLeader === true);
    }
    return Array.from(byDate.entries())
      .filter(([, hasTl]) => !hasTl)
      .map(([date]) => date)
      .sort();
  })();

  // #94 (panel half): the NOT-APPLICABLE inverse of datesMissingTl. Days where
  // the active phase HAS assignments but NONE is a Reprotec operative (fully
  // subcontracted day) → no Team Leader is required, so we show a subtle grey
  // "N/A" indicator rather than the amber missing-TL warning. Mirrors the
  // datesMissingTl derivation but for the inverse condition, per-day.
  const datesTlNotApplicable = (() => {
    const byDateHasOperative = new Map<string, boolean>();
    for (const a of phaseAssignedStaff) {
      const staff = staffMap.get(a.staffId);
      const isOperative = staff?.role === 'operative';
      byDateHasOperative.set(a.date, byDateHasOperative.get(a.date) || false || isOperative);
    }
    return Array.from(byDateHasOperative.entries())
      .filter(([, hasOperative]) => !hasOperative)
      .map(([date]) => date)
      .sort();
  })();

  function toggleStaffExpanded(staffId: string) {
    setExpandedStaffIds((prev) => {
      const next = new Set(prev);
      if (next.has(staffId)) next.delete(staffId);
      else next.add(staffId);
      return next;
    });
  }

  // Parallel expand/collapse for a worker's warnings block (only used when a
  // worker has >1 warning). Mirrors toggleStaffExpanded.
  function toggleWarningStaffExpanded(staffId: string) {
    setExpandedWarningStaffIds((prev) => {
      const next = new Set(prev);
      if (next.has(staffId)) next.delete(staffId);
      else next.add(staffId);
      return next;
    });
  }

  // Issue #222: mirror of toggleStaffExpanded for the per-vehicle expand control.
  function toggleVehicleExpanded(vehicleId: string) {
    setExpandedVehicleIds((prev) => {
      const next = new Set(prev);
      if (next.has(vehicleId)) next.delete(vehicleId);
      else next.add(vehicleId);
      return next;
    });
  }

  function startEditAssignment(a: (typeof job.assignedStaff)[number]) {
    setEditingAssignmentId(a.assignmentId);
    setEditStartTime(a.startTime.slice(0, 5));
    setEditEndTime(a.endTime.slice(0, 5));
  }

  // GH #130: permission-based gating replaces hard-coded role checks. The
  // backend enforces `contracts_board.update` (general edits) and
  // `contracts_board.manage_job_state` (lifecycle transitions) as SEPARATE
  // permissions, so the UI splits the gate to match.
  // - canEdit drives general edit affordances (dates/scope/assignments/
  //   equipment/vehicles/visibility) via canEditUnlocked → canEditActivePhase.
  // - canManageJobState additionally gates the lifecycle/job-state actions
  //   (Postpone, Cancel job/phase, Mark complete/incomplete, Add phase).
  const { can, me } = usePermissions();
  const canEdit = can('contracts_board', 'update');
  const canManageJobState = can('contracts_board', 'manage_job_state');
  // Phase 78-01: cost (unit_cost / value) on materials allocation rows is hidden
  // from viewers — parity with the prior cost_each gating. `me.role` is the
  // resolved auth_role from GET /me (same ['me'] cache as usePermissions()).
  const canViewMaterialsCost = me?.role !== 'viewer';

  // Phase 44-01-FIX (GH #71, AC-6): the assign-staff form pre-fills START/END
  // from the org Working Day (app_config.working_day) instead of a hardcoded
  // 08:00–16:00. #189: repointed from the old working_hours.start/.end to
  // working_day.start/.finish (the relabelled on-site window). Shares the
  // ['admin-config'] cache with SettingsPage; the read is planner-accessible
  // (admin.ts read-gate). Falls back to 08:00/16:00 when config is absent or
  // still loading. Existing saved assignments are untouched — this only seeds
  // the *suggested* defaults in the new-assignment form.
  const { data: appConfig } = useQuery({
    queryKey: ['admin-config'],
    queryFn: () => api.admin.getConfig(),
    staleTime: 60 * 1000,
  });
  const workingDay = appConfig?.working_day as { start?: string; finish?: string } | undefined;
  const defaultStartTime = workingDay?.start ?? '08:00';
  const defaultEndTime = workingDay?.finish ?? '16:00';

  // Seed the new-assignment time fields from config whenever the form is closed
  // (incl. once config loads, and after each successful submit resets it). Guarded
  // on !showAddStaff so a planner's in-progress edits are never overwritten.
  useEffect(() => {
    if (!showAddStaff) {
      setNewStartTime(defaultStartTime);
      setNewEndTime(defaultEndTime);
    }
  }, [defaultStartTime, defaultEndTime, showAddStaff]);
  // Phase 23-05: unified status derivation — drives both the header pill
  // (T2) and the readonly cascade across all edit affordances (T3).
  // #105: a postponed job is a read-only tombstone exactly like cancelled/
  // completed. Treat the panel as postponed when EITHER the job's own status is
  // 'postponed' OR it was opened from a postponement-snapshot tombstone
  // (postponementContext set — covers legacy/pre-phase jobs whose underlying
  // row may still read 'scheduled'). This forces the locked unified status, so
  // the whole edit cascade (isJobLocked → canEditUnlocked → canEditActivePhase →
  // every section gate + both pencils) reads read-only, and the badge shows
  // POSTPONED.
  const postponedView = job.status === 'postponed' || postponementContext != null;
  // #106: ticket-sourced jobs use "ticket" wording in lifecycle copy; deals and
  // manual jobs use "job". `Unit`/`unit` are the capitalised / lowercase nouns
  // for the lifecycle dialog prompts + buttons (GH #108 part 2).
  const isTicketJob = job.source === 'hubspot_ticket';
  const unit = isTicketJob ? 'ticket' : 'job';
  const Unit = isTicketJob ? 'Ticket' : 'Job';
  const unifiedStatus = postponedView
    ? {
        label: 'Postponed',
        pillClass: 'bg-amber-100 text-amber-800',
        isStatusEditable: false,
        isJobLocked: true,
      }
    : computeUnifiedStatus(job);
  const isJobLocked = unifiedStatus.isJobLocked;
  // canEditUnlocked: combines the role-based canEdit gate with the parent-job
  // lifecycle-state lock. Use this for JOB-level affordances that should hide
  // for completed/postponed/cancelled jobs (date pencil, +Add Phase, etc).
  // Do NOT use for transition buttons (Postpone Job / Cancel Job / Archive) —
  // those stay available per JOB_TRANSITIONS table.
  const canEditUnlocked = canEdit && !isJobLocked;
  // canEditActivePhase: layered on top — also requires the ACTIVE PHASE to be
  // editable (not cancelled / completed). Use this for PHASE-level affordances
  // that should hide when viewing a cancelled or completed phase: per-phase
  // CM change, scope edit, staff/equipment/vehicles/materials/skills
  // add/remove, lunch break, notes/scopes inline, TL toggle. Defined later in
  // the body once `activePhase` is computed; declared inline with `let` so the
  // JSX gates further down compile-resolve to the late binding.
  let canEditActivePhase = canEditUnlocked;

  const {
    data: documents = [],
    isLoading: docsLoading,
    isError: docsError,
    isFetching: docsFetching,
    refetch: refetchDocuments,
  } = useQuery({
    queryKey: ['job-documents', job.id],
    queryFn: () => api.jobs.documents(job.id),
    staleTime: 2 * 60 * 1000,
    retry: false,
  });

  // Phase 39-07 (resource-scoping bundle): per-phase filter. queryKey
  // includes selectedPhaseId so React Query treats each phase's data as
  // a distinct cache entry. selectedPhaseId may be null transiently while
  // phases load — that's fine; we still pass undefined to api which returns
  // all-phases data (unchanged legacy shape).
  // Phase 78-01: allocation-driven, READ-ONLY Materials section. Keyed on the
  // work_item id (api.materials.workItemAllocations — §4 read contract), it
  // replaces the HubSpot job_materials section + the Plan-04-02 supplier picker.
  // Gated on a resolvable workItemId (postponement tombstones / legacy mocks may
  // omit it). selectedPhaseId is NOT part of the key — the endpoint returns all
  // phases for the work-item and the UI groups by phase client-side.
  const {
    data: allocations,
    isLoading: materialsLoading,
    isError: materialsError,
  } = useQuery<ApiWorkItemAllocations>({
    queryKey: ['work-item-allocations', job.workItemId],
    queryFn: () => api.materials.workItemAllocations(job.workItemId!),
    enabled: !!job.workItemId,
    staleTime: 60 * 1000,
  });

  // GH #220 item 1: the allocations FETCH deliberately returns ALL phases
  // (keyed on workItemId only), but the Materials section must be scoped to the
  // currently-selected phase like equipment/vehicles/skills. Derive the visible
  // job phases here: those whose phase_id matches selectedPhaseId (same UUID the
  // panel sends at reserve). selectedPhaseId is null transiently before the
  // default-phase effect runs — fall back to ALL phases then so the section
  // doesn't flicker to empty on load. Tickets have no phases (handled below).
  const visibleMaterialPhases = useMemo(() => {
    if (!allocations || allocations.target_kind !== 'job') return [];
    const phases = allocations.phases ?? [];
    if (selectedPhaseId == null) return phases;
    return phases.filter((ph) => ph.phase_id === selectedPhaseId);
  }, [allocations, selectedPhaseId]);

  // Total product count in the visible (selected) phase(s) for jobs, or the flat
  // list for tickets. Drives the collapsible header count + the empty-state branch.
  const materialsProductCount = useMemo(() => {
    if (!allocations) return 0;
    if (allocations.target_kind === 'job') {
      return visibleMaterialPhases.reduce((sum, ph) => sum + ph.products.length, 0);
    }
    return (allocations.products ?? []).length;
  }, [allocations, visibleMaterialPhases]);

  // Phase 78-02: warehouse stock list — source for the reserve-from-stock
  // picker. Loaded lazily (only when the reserve form is opened) so the panel
  // doesn't fetch stock for every job view.
  const { data: stockRows = [] } = useQuery<ApiStockRow[]>({
    queryKey: ['materials-stock'],
    queryFn: () => api.materials.stock(),
    enabled: showReserveStock,
    staleTime: 60 * 1000,
  });

  function refreshStockAvailability() {
    // The lazy picker and warehouse use separate query keys. Refresh both,
    // including an inactive picker reopened within its one-minute cache window.
    void queryClient.invalidateQueries({ queryKey: ['materials-stock'] });
    void queryClient.invalidateQueries({ queryKey: ['materials', 'stock'] });
    void queryClient.invalidateQueries({ queryKey: ['materials', 'work-items-with-allocations'] });
    void queryClient.invalidateQueries({
      queryKey: ['materials', 'work-item-allocations', job.workItemId],
    });
  }

  // Phase 78-02: planner reserves `qty` of a catalog row from warehouse stock to
  // this job/phase (phase_id) or ticket (omitted). On success invalidate the
  // allocations query + close the form; a 409 (over-available) surfaces inline.
  const reserveAllocationMutation = useMutation({
    mutationFn: (body: {
      target_kind: 'job' | 'ticket';
      work_item_ion?: string | null;
      phase_id?: string;
      phase_number?: number;
      catalog_row_id: string;
      qty: number;
    }) => api.materials.reserveAllocation(job.workItemId!, body),
    onSuccess: () => {
      refreshStockAvailability();
      queryClient.invalidateQueries({ queryKey: ['work-item-allocations', job.workItemId] });
      // GH #246: a new material allocation changes the phase material cost, so
      // refetch the per-phase cost/margin the panel renders.
      queryClient.invalidateQueries({ queryKey: ['phase-costs', job.id] });
      setShowReserveStock(false);
      setReserveCatalogRowId('');
      setReserveQty('');
      setReserveError(null);
    },
    onError: (err) => {
      const code =
        err instanceof ApiHttpError &&
        typeof err.body === 'object' &&
        err.body !== null &&
        'code' in err.body
          ? err.body.code
          : undefined;
      if (code === 'RESERVATION_TARGET_UNAVAILABLE') {
        setReserveError('The work item or phase has changed. Refresh before reserving stock.');
      } else if (code === 'RESERVATION_TARGET_BUSY') {
        setReserveError('The work item or phase is being updated. Refresh and try again.');
      } else if (
        err instanceof ApiHttpError &&
        err.status === 409 &&
        (code === 'INSUFFICIENT_STOCK' || code === undefined)
      ) {
        refreshStockAvailability();
        setReserveError('Exceeds available stock');
      } else {
        setReserveError('Could not reserve stock. Please try again.');
      }
    },
  });

  // Phase 78-02: planner releases a not-yet-checked-out reservation (the source
  // row's allocation id). On success invalidate the allocations query.
  const releaseAllocationMutation = useMutation({
    mutationFn: (allocationId: string) =>
      api.materials.releaseAllocation(job.workItemId!, allocationId),
    onMutate: () => setReleaseError(null),
    onSuccess: () => {
      refreshStockAvailability();
      queryClient.invalidateQueries({ queryKey: ['work-item-allocations', job.workItemId] });
      // GH #246: releasing an allocation changes the phase material cost.
      queryClient.invalidateQueries({ queryKey: ['phase-costs', job.id] });
    },
    onError: () => {
      setReleaseError(
        'Could not release the reservation. Refresh and check its status before trying again.',
      );
    },
  });

  const { data: equipment = [], isLoading: equipmentLoading } = useQuery<ApiJobEquipment[]>({
    queryKey: ['job-equipment', job.id, selectedPhaseId],
    queryFn: () => api.jobs.equipment(job.id, selectedPhaseId ?? undefined),
    staleTime: 60 * 1000,
  });

  // Equipment item catalogue for the add-equipment picker
  const { data: equipmentItems = [] } = useQuery({
    queryKey: ['equipment-items'],
    queryFn: () => api.admin.equipment(),
    staleTime: 5 * 60 * 1000,
  });

  // Phase 34-03 (Issue #36): per-job vehicle assignments + active-vehicle catalogue.
  // Phase 39-07: per-phase filter (see materials/equipment for pattern).
  const { data: jobVehicles = [], isLoading: vehiclesLoading } = useQuery<ApiJobVehicle[]>({
    queryKey: ['job-vehicles', job.id, selectedPhaseId],
    queryFn: () => api.jobs.vehicles(job.id, selectedPhaseId ?? undefined),
    staleTime: 60 * 1000,
  });

  // Active-vehicle catalogue for the add-vehicle picker.
  const { data: activeVehicles = [] } = useQuery<ApiVehicle[]>({
    queryKey: ['vehicles', false],
    queryFn: () => api.vehicles.list({ includeInactive: false }),
    staleTime: 5 * 60 * 1000,
  });

  // Leave entries for the selected staff member across the ACTIVE PHASE date
  // range (per-day grid). Phase 40-01: phase-scoped — was job-scoped pre-40-01
  // which over-fetched leave entries outside the active phase and showed
  // multi-day affordances on phases that are actually single-day.
  const jobEndDate =
    panelDurationDays > 1
      ? format(addDays(panelDate, panelDurationDays - 1), 'yyyy-MM-dd')
      : panelStartDate;
  const { data: staffLeaveEntries = [] } = useQuery({
    queryKey: ['staff-leave', newStaffId, panelStartDate, jobEndDate],
    queryFn: () => api.leave.list({ staffId: newStaffId, from: panelStartDate, to: jobEndDate }),
    enabled: !!newStaffId && panelDurationDays > 1,
    staleTime: 30 * 1000,
  });

  // WTR pre-check: which dates would violate daily/weekly rest rules for this staff member?
  const { data: wtrBlockedDates = {} } = useQuery({
    queryKey: [
      'assignments',
      'wtr-pre-check',
      newStaffId,
      panelStartDate,
      jobEndDate,
      newStartTime,
      newEndTime,
    ],
    queryFn: () =>
      api.assignments.wtrPreCheck({
        staffId: newStaffId,
        fromDate: panelStartDate,
        toDate: jobEndDate,
        startTime: newStartTime,
        endTime: newEndTime,
      }),
    enabled: !!newStaffId && panelDurationDays > 1,
    staleTime: 30_000,
  });

  // Leave entries for the "add days" staff member
  const { data: addDaysLeaveEntries = [], isFetching: addDaysLeaveFetching } = useQuery({
    queryKey: ['staff-leave', addDaysStaffId, panelStartDate, jobEndDate],
    queryFn: () =>
      api.leave.list({ staffId: addDaysStaffId!, from: panelStartDate, to: jobEndDate }),
    enabled: !!addDaysStaffId,
    staleTime: 30 * 1000,
  });

  // Conflict check for unassigned days of the "add days" staff member.
  // Add-days phase-scoping fix: scope to phaseAssignedStaff (this phase's assignments) — NOT
  // the job-wide job.assignedStaff. Pre-fix, an operative's OTHER-phase days
  // were treated as already-assigned-to-THIS-phase and skipped, so a fully
  // phase-covered operative produced zero candidate days → the add-days grid
  // never reached the conflict-check and stuck on "Loading…".
  const addDaysDates = addDaysStaffId
    ? jobDays.filter(
        (d) => !phaseAssignedStaff.some((a) => a.staffId === addDaysStaffId && a.date === d),
      )
    : [];
  // Issue #255 (Facet 1 parity): the bulk-add "Add days" picker now uses the
  // same free-interval availability probe as the single-day dialog, rather than
  // a fixed Working-Day window overlap check. A partially-booked day (e.g. the
  // operative finishes an earlier job at 14:00) is addable into its free window
  // instead of being flagged a hard full-day conflict; only a FULLY booked day
  // is a conflict.
  const { data: addDaysAvailability, isFetching: addDaysAvailFetching } = useQuery({
    queryKey: ['staff-day-availability', addDaysStaffId, addDaysDates.join(',')],
    queryFn: () =>
      api.assignments.dayAvailability({
        staffId: addDaysStaffId!,
        dates: addDaysDates,
      }),
    enabled: !!addDaysStaffId && addDaysDates.length > 0,
    staleTime: 30_000,
  });

  // Add-days phase-scoping fix (Fix B): distinguish "still fetching" from "no addable days".
  // Pre-fix the add-days grid showed "Loading…" whenever the segment list was
  // empty, which could never resolve when Fix A's root cause over-skipped every
  // day. Drive the loading label off the two add-days queries' fetch state so an
  // empty-but-settled grid shows an explicit empty message instead.
  const addDaysLoading = addDaysLeaveFetching || addDaysAvailFetching;

  // Build the per-day segments when staff selection changes or grid opens
  const generatePerDaySegments = (startTime: string, endTime: string) => {
    const leaveDates = new Set(staffLeaveEntries.map((e) => e.date.slice(0, 10)));
    const existingDates = new Set(
      job.assignedStaff.filter((a) => a.staffId === newStaffId).map((a) => a.date),
    );
    // Issue #255 (Facet 1): build a free-interval availability map. A day is a
    // "conflict" only when it is FULLY booked (no free slot) — a partial booking
    // that leaves room is available, and its suggested (largest free gap) slot
    // seeds a non-overlapping default. busy[0] gives the label for the fully-
    // booked case; phase context disambiguates cross-phase clashes (40-01).
    const availabilityMap = new Map<
      string,
      {
        available: boolean;
        onLeave: boolean;
        suggested: { start: string; end: string } | null;
        hasBooking: boolean;
        label: { ion: string; jobName: string; phaseNumber?: number } | null;
      }
    >();
    for (const r of dayAvailabilityResults ?? []) {
      const b = r.busy[0];
      availabilityMap.set(r.date, {
        available: r.available,
        onLeave: r.onLeave,
        suggested: r.suggested,
        hasBooking: r.busy.length > 0,
        label: b
          ? { ion: b.ion, jobName: b.job_name ?? '', phaseNumber: b.phase_number ?? undefined }
          : null,
      });
    }
    const segments: {
      date: string;
      startTime: string;
      endTime: string;
      isLeave: boolean;
      isExisting: boolean;
      isConflict: boolean;
      conflictLabel: string;
      isWtrBlocked: boolean;
      wtrReason: string;
      wtrDetail: WtrBlockDetail | null;
      enabled: boolean;
    }[] = [];
    // Phase 40-01 fixup: iterate phase days, not job days. Migration 067
    // enforces date BETWEEN phase.start AND phase.end at the DB layer.
    for (let i = 0; i < panelDurationDays; i++) {
      const d = format(addDays(panelDate, i), 'yyyy-MM-dd');
      const avail = availabilityMap.get(d);
      const isLeave = leaveDates.has(d) || (avail?.onLeave ?? false);
      const isExisting = existingDates.has(d);
      // A day is a conflict only when availability data has loaded AND no free
      // slot exists (fully booked). Absent data (still loading) is not a conflict
      // — matches the prior "no results yet ⇒ no conflict" behaviour.
      const isConflict =
        !isLeave && !isExisting && avail !== undefined && !avail.available && avail.hasBooking;
      // Phase 40-01 fixup: include "(Phase N)" suffix when known so the
      // planner can tell a cross-phase clash from a cross-job one.
      const conflictLabel =
        isConflict && avail?.label
          ? `${avail.label.ion} ${extractDealName(avail.label.jobName, avail.label.ion)}${
              avail.label.phaseNumber ? ` (Phase ${avail.label.phaseNumber})` : ''
            }`
          : '';
      const wtrViolations = wtrBlockedDates[d];
      const isWtrBlocked =
        !isLeave && !isExisting && !isConflict && !!wtrViolations && wtrViolations.length > 0;
      const wtrReason = isWtrBlocked ? wtrViolations[0].reason : '';
      // Issue #255 (Facet 2): carry the first blocking violation's detail so the
      // grid can render a specific reason + computed "More details" pop-up.
      const wtrDetail = isWtrBlocked ? wtrViolations[0] : null;
      // Issue #255: seed a partially-booked-but-available day to its largest free
      // gap so the planner doesn't have to hand-edit around the existing booking.
      // Fully-free days keep the passed window (the Working Day default, which the
      // planner may have customised via the Start/End inputs).
      const daySlot =
        !isLeave && !isExisting && avail?.available && avail.hasBooking && avail.suggested
          ? avail.suggested
          : { start: startTime, end: endTime };
      segments.push({
        date: d,
        startTime: daySlot.start,
        endTime: daySlot.end,
        isLeave,
        isExisting,
        isConflict,
        conflictLabel,
        isWtrBlocked,
        wtrReason,
        wtrDetail,
        enabled: !isLeave && !isExisting && !isConflict && !isWtrBlocked,
      });
    }
    return segments;
  };

  // Build mini per-day grid for adding extra days to an existing staff member
  const generateAddDaysSegments = (staffId: string) => {
    // Add-days phase-scoping fix: phase-scope the already-assigned set (see addDaysDates
    // above). Using job-wide job.assignedStaff here skipped the operative's
    // other-phase days, collapsing the segment list to empty.
    const assignedDates = new Set(
      phaseAssignedStaff.filter((a) => a.staffId === staffId).map((a) => a.date),
    );
    const leaveDates = new Set(addDaysLeaveEntries.map((e) => e.date.slice(0, 10)));
    // Issue #255 (Facet 1 parity): build a free-interval availability map (mirrors
    // generatePerDaySegments). A day is a conflict ONLY when fully booked; a
    // partially-free day is addable and seeded to its largest free gap so the
    // planner can drop the operative straight into the window after an earlier
    // job finishes. The "(Phase N)" suffix disambiguates a cross-phase clash
    // (e.g. the same job's other phase) from a cross-job one.
    const availabilityMap = new Map<
      string,
      {
        available: boolean;
        onLeave: boolean;
        suggested: { start: string; end: string } | null;
        hasBooking: boolean;
        label: { ion: string; jobName: string; phaseNumber?: number } | null;
      }
    >();
    for (const r of addDaysAvailability ?? []) {
      const b = r.busy[0];
      availabilityMap.set(r.date, {
        available: r.available,
        onLeave: r.onLeave,
        suggested: r.suggested,
        hasBooking: r.busy.length > 0,
        label: b
          ? { ion: b.ion, jobName: b.job_name ?? '', phaseNumber: b.phase_number ?? undefined }
          : null,
      });
    }
    const segments: {
      date: string;
      startTime: string;
      endTime: string;
      isLeave: boolean;
      isConflict: boolean;
      conflictLabel: string;
      enabled: boolean;
    }[] = [];
    // Phase 40-01 fixup: iterate phase days, not job days.
    for (let i = 0; i < panelDurationDays; i++) {
      const d = format(addDays(panelDate, i), 'yyyy-MM-dd');
      if (assignedDates.has(d)) continue; // skip already-assigned days
      const avail = availabilityMap.get(d);
      const isLeave = leaveDates.has(d) || (avail?.onLeave ?? false);
      // Conflict only when availability has loaded AND no free slot exists
      // (fully booked). Absent data (still loading) is not a conflict.
      const isConflict = !isLeave && avail !== undefined && !avail.available && avail.hasBooking;
      const conflictLabel =
        isConflict && avail?.label
          ? `${avail.label.ion} ${extractDealName(avail.label.jobName, avail.label.ion)}${
              avail.label.phaseNumber ? ` (Phase ${avail.label.phaseNumber})` : ''
            }`
          : '';
      // Seed a partially-booked-but-available day to its largest free gap;
      // fully-free days keep the Working-Day default.
      const daySlot =
        !isLeave && avail?.available && avail.hasBooking && avail.suggested
          ? avail.suggested
          : { start: defaultStartTime, end: defaultEndTime };
      segments.push({
        date: d,
        startTime: daySlot.start,
        endTime: daySlot.end,
        isLeave,
        isConflict,
        conflictLabel,
        enabled: !isLeave && !isConflict,
      });
    }
    return segments;
  };

  // Phase 40-01 fixup: phase-scoped dates for the staff conflict-check query.
  const jobDates = Array.from({ length: panelDurationDays }, (_, i) =>
    format(addDays(panelDate, i), 'yyyy-MM-dd'),
  );

  // Issue #255 (Facet 1): free-interval availability drives the per-day grid.
  // Deliberately keyed on staff + dates only (NOT newStartTime/newEndTime): a
  // day's availability is a property of the operative's EXISTING bookings vs the
  // Working Day, independent of the window currently proposed in the form. The
  // old ['staff-conflicts', …, newStartTime, newEndTime] query asked "does the
  // proposed 08:00–16:00 overlap?" and flagged a whole day on a 15:30–19:30
  // booking; this asks "is there a free slot?" and returns the gap to seed.
  const { data: dayAvailabilityResults } = useQuery({
    queryKey: ['staff-day-availability', newStaffId, jobDates.join(',')],
    queryFn: () =>
      api.assignments.dayAvailability({
        staffId: newStaffId,
        dates: jobDates,
      }),
    enabled: !!newStaffId && showAddStaff,
    staleTime: 30_000,
  });

  // Days that are fully booked (an existing booking leaves no free slot) — the
  // genuine "already assigned / no room" case surfaced in the summary banner.
  const conflictDays = (dayAvailabilityResults ?? []).filter(
    (r) => !r.onLeave && !r.available && r.busy.length > 0,
  );

  // Seed the proposed START/END from the first partially-booked day's largest
  // free gap so the planner starts from a non-overlapping slot (Issue #255) and
  // doesn't have to hand-edit. Fully-free days keep the Working Day default (set
  // by the config effect above). Once per staff selection so it never clobbers a
  // planner's subsequent manual edits.
  const seededSlotForStaffRef = useRef<string | null>(null);
  useEffect(() => {
    if (!showAddStaff || !newStaffId || !dayAvailabilityResults) return;
    if (seededSlotForStaffRef.current === newStaffId) return;
    seededSlotForStaffRef.current = newStaffId;
    const firstPartial = dayAvailabilityResults.find(
      (r) => !r.onLeave && r.available && r.busy.length > 0 && r.suggested,
    );
    if (firstPartial?.suggested) {
      setNewStartTime(firstPartial.suggested.start);
      setNewEndTime(firstPartial.suggested.end);
    }
  }, [dayAvailabilityResults, newStaffId, showAddStaff]);

  // Phase 24-01 (rationale) + 24-02 (set-state-in-effect suppression):
  // the next 4 useEffects intentionally setState inside their bodies AND
  // intentionally narrow their deps. Refactor to derived state / event
  // handlers deferred to a future plan that touches the add-staff /
  // per-day-grid effect architecture.
  /* eslint-disable react-hooks/exhaustive-deps */

  // Auto-open per-day grid when conflicts are detected. Triggers only on
  // conflictDays.length / job.durationDays changes — `showPerDayGrid` and
  // `perDaySegments.length` reads are intentionally not deps (this effect
  // SETS them; including would cause re-trigger loops). `newStartTime` /
  // `newEndTime` change as the user types and would cause regeneration on
  // every keystroke.
  useEffect(() => {
    if (conflictDays.length > 0 && panelDurationDays > 1 && !showPerDayGrid) {
      setShowPerDayGrid(true);
      if (perDaySegments.length === 0) {
        setPerDaySegments(generatePerDaySegments(newStartTime, newEndTime));
      }
    }
  }, [conflictDays.length, panelDurationDays]);

  // Regenerate per-day segments when WTR / leave / conflict DATA changes.
  // Intentionally NOT triggered by form-state changes (`newStartTime` /
  // `newEndTime` change-on-type would constantly regenerate; `newStaffId`
  // and `showPerDayGrid` are guards that gate the effect, not triggers).
  useEffect(() => {
    if (showPerDayGrid && newStaffId && panelDurationDays > 1) {
      setPerDaySegments(generatePerDaySegments(newStartTime, newEndTime));
    }
  }, [wtrBlockedDates, staffLeaveEntries, dayAvailabilityResults]);

  // Auto-open per-day grid when WTR blocked dates appear. Triggers only on
  // wtrBlockedDates change — same rationale as the conflicts effect: the
  // body SETS showPerDayGrid + perDaySegments; including them would loop.
  useEffect(() => {
    const hasBlocked = Object.keys(wtrBlockedDates).length > 0;
    if (hasBlocked && panelDurationDays > 1 && !showPerDayGrid && newStaffId) {
      setShowPerDayGrid(true);
      setPerDaySegments(generatePerDaySegments(newStartTime, newEndTime));
    }
  }, [wtrBlockedDates]);

  // Regenerate add-days segments when leave / availability DATA for the
  // current addDaysStaffId changes. addDaysStaffId is the GUARD (the
  // user-initiated open of the add-days mini grid); changing it triggers
  // a separate flow, not this regeneration. Including it as a dep would
  // re-trigger when the user closes/reopens, which we don't want.
  useEffect(() => {
    if (addDaysStaffId) {
      setAddDaysSegments(generateAddDaysSegments(addDaysStaffId));
    }
  }, [addDaysLeaveEntries, addDaysAvailability]);

  /* eslint-enable react-hooks/exhaustive-deps */

  const transitionMutation = useMutation({
    mutationFn: ({ status, reason }: { status: ApiJob['status']; reason?: string }) =>
      api.jobs.transition(job.id, status, reason),
    onSuccess: (updatedJob) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      // Phase 40-01 fixup: Cancel Job releases all vehicles + equipment +
      // materials + segments server-side (services/jobs.ts:150-174). Without
      // these invalidations the fleet-wide busy-dates caches stay stale until
      // staleTime expires, so resources released by a job cancellation don't
      // become immediately assignable elsewhere.
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      setPendingCancel(false);
      setPendingReschedule(false);
      setCancelReason('');
      onTransitioned?.(updatedJob);
    },
  });

  const postponeMutation = useMutation({
    mutationFn: () => api.jobs.postpone(job.id, postponeReason.trim() || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      queryClient.invalidateQueries({ queryKey: ['work-items'] });
      setPostponeReason('');
      setPendingPostpone(false);
      // Phase 40-01 fixup: postpone CASCADE-deletes all phase-scoped
      // resources (job_vehicles / job_equipment / etc) — fleet-wide busy-
      // dates caches must drop those dates so the released resources become
      // immediately rebookable.
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      onClose();
    },
    // Phase 39-05 follow-up (2026-05-23): surface the BE error message
    // (e.g. JOB_HAS_TIME_ENTRIES 409) in the confirm panel so the planner
    // sees why postpone was blocked instead of a silently-failing button.
  });

  // ── Phase 39-07: phases query + add/cancel mutations ─────────────────────
  // Source-of-truth for the navigator. Refetches after add/cancel via query
  // invalidation. staleTime 0 because phase-state changes need immediate
  // reflection on the navigator.
  const phasesQuery = useQuery({
    queryKey: ['phases', job.id],
    queryFn: () => api.phases.list(job.id),
    staleTime: 0,
  });
  // Memoise the empty-array fallback so `phases` keeps a stable reference
  // across renders when the query has no data — otherwise the `?? []` literal
  // allocates a new array every render and re-triggers every hook that depends
  // on `phases` (the default-phase useEffect + selectedPhaseObj useMemo below).
  const phases: ApiPhase[] = useMemo(() => phasesQuery.data ?? [], [phasesQuery.data]);

  // Per-phase profitability: cost/margin rows. Money is finance-sensitive, so
  // the request is gated on `canEdit` (can('contracts_board','update')) — below-
  // planner roles never trigger it. Invalidated by the budget-edit mutation so
  // margins refresh after a save.
  const phaseCostsQuery = useQuery({
    queryKey: ['phase-costs', job.id],
    queryFn: () => api.jobs.phaseCosts(job.id),
    enabled: canEdit,
    staleTime: 0,
  });

  // Per-worker schedule warnings for the active phase — the persistent data
  // source for the "Assignment warnings" banner. The panel mounts fresh on each
  // open, so gating on a resolved selectedPhaseId is sufficient (no separate
  // `open` prop). Invalidated by the assign/edit/delete/batch mutations so the
  // banner reflects the current assignment state after any change.
  const { data: workerWarnings = [] } = useQuery<ApiWorkerWarnings[]>({
    queryKey: ['phase-warnings', job.id, selectedPhaseId],
    queryFn: () => api.jobs.phaseWarnings(job.id, selectedPhaseId as string),
    enabled: selectedPhaseId != null,
    staleTime: 30_000,
  });
  // Re-show the (dismissable) banner whenever the underlying warnings change —
  // e.g. after an assignment mutation invalidates the query. A stable signature
  // avoids resetting on every render / identical refetch.
  const warningsSignature = workerWarnings
    .map((w) => `${w.staffId}:${w.warnings.length}`)
    .join('|');
  useEffect(() => {
    setWarningsDismissed(false);
  }, [warningsSignature]);
  // The panel intentionally shows PHASE-SCOPED costs: rows are keyed by
  // phase_id, so the null-phase_id row (job-level material allocations not tied
  // to any phase) is skipped here. Those job-level costs are reflected only in
  // the BI/Looker job-grain totals, never in this per-phase panel.
  const costByPhaseId = useMemo(() => {
    const m = new Map<string, PhaseCost>();
    for (const c of phaseCostsQuery.data ?? []) {
      if (c.phase_id) m.set(c.phase_id, c);
    }
    return m;
  }, [phaseCostsQuery.data]);

  // JOB-level Σ of every phase's current budget, sourced LIVE from the
  // phase-costs query (invalidated on each budget save) so the phased-job
  // "Amount for all phases" reconciliation refreshes as budgets are edited.
  // A null/empty phase_budget counts as £0 (so partially-budgeted jobs read
  // "under budget" by the unallocated remainder — consistent with the wizard).
  const allPhasesBudgetTotal = useMemo(() => {
    let sum = 0;
    for (const c of phaseCostsQuery.data ?? []) {
      if (c.phase_id) sum += c.phase_budget ?? 0;
    }
    return sum;
  }, [phaseCostsQuery.data]);

  const updatePhaseBudgetMutation = useMutation({
    mutationFn: ({ phaseId, phase_budget }: { phaseId: string; phase_budget: string | null }) =>
      api.phases.update(phaseId, { phase_budget }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phases', job.id] });
      queryClient.invalidateQueries({ queryKey: ['phase-costs', job.id] });
    },
  });

  // Once phases load, if selectedPhaseId is still null (no phaseContext from
  // board click), default to the earliest non-cancelled phase. Falls back to
  // null if all phases are cancelled (cancelled-only multi-phase job).
  useEffect(() => {
    if (selectedPhaseId == null && phases.length > 0) {
      const firstActive = phases.find((p) => p.status !== 'cancelled');
      if (firstActive) setSelectedPhaseId(firstActive.id);
    }
  }, [selectedPhaseId, phases]);

  const addPhaseMutation = useMutation({
    mutationFn: (body: PhaseCreateBody) => api.phases.create(job.id, body),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['phases', job.id] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      // Select the newly-created phase so the planner can immediately edit it.
      setSelectedPhaseId(created.id);
      setPendingAddPhase(false);
      setPhaseFormStartDate('');
      setPhaseFormEndDate('');
      setPhaseFormStatus('provisional');
      setPhaseFormNotes('');
      setPhaseFormContractsManagerId('');
    },
  });

  // Phase 41-03 (#66): per-phase Complete + Uncomplete mutations.
  // GH #120: "Mark … complete" confirmation lives in a central AlertDialog
  // modal (mirrors the Postpone / Cancel modals from #108). `pendingCompletePhaseId`
  // is the phase the modal targets; non-null = modal open. `completeErrorReason`
  // holds the friendly-ified 422 reason and is shown INSIDE the dialog (the
  // dialog stays open on error so the planner sees why completion was blocked).
  const [pendingCompletePhaseId, setPendingCompletePhaseId] = useState<string | null>(null);
  const [completeErrorReason, setCompleteErrorReason] = useState<string | null>(null);

  const completePhaseMutation = useMutation({
    mutationFn: (phaseId: string) => api.phases.complete(phaseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phases', job.id] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      // Completion awaits the stock close sweep. Refresh its derived costs and
      // allocations while this panel stays mounted, plus the Stock page caches.
      queryClient.invalidateQueries({ queryKey: ['phase-costs', job.id] });
      if (job.workItemId) {
        queryClient.invalidateQueries({ queryKey: ['work-item-allocations', job.workItemId] });
        queryClient.invalidateQueries({
          queryKey: ['materials', 'work-item-allocations', job.workItemId],
        });
        queryClient.invalidateQueries({ queryKey: ['materials-stock'] });
        queryClient.invalidateQueries({ queryKey: ['materials', 'stock'] });
        queryClient.invalidateQueries({ queryKey: ['materials', 'work-items-with-allocations'] });
      }
      // Close the modal + clear any prior error.
      setPendingCompletePhaseId(null);
      setCompleteErrorReason(null);
    },
    onError: (err) => {
      // BE returns 422 PHASE_COMPLETE_BLOCKED with the planner-readable
      // reason in the error message. Surface it INSIDE the open modal.
      const raw = err instanceof Error ? err.message : String(err);
      // apiFetch wraps non-2xx response bodies in error.message — try to
      // extract the JSON reason if present.
      let reason = raw;
      const jsonMatch = raw.match(/\{.*\}/);
      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[0]) as { error?: string; code?: string };
          if (parsed.error) reason = parsed.error;
        } catch {
          // fall through to raw
        }
      }
      // GH #108 (part 1): the BE 422 message embeds a raw ISO timestamp
      // ("Scheduled work continues until 2026-06-15T22:59:59.000Z"). Rewrite it
      // to a planner-friendly "23:59 on 15th June 2026" for the modal error.
      // (The global UX-1 toast applies the same transform independently.)
      // Keep `pendingCompletePhaseId` set so the dialog stays open.
      setCompleteErrorReason(friendlyifyIsoTimestamps(reason));
    },
  });

  const uncompletePhaseMutation = useMutation({
    mutationFn: (phaseId: string) => api.phases.uncomplete(phaseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phases', job.id] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      setCompleteErrorReason(null);
    },
  });

  // Click handler for the kebab "Mark … complete" / "Mark … incomplete" items.
  // GH #120: completing now opens a central confirmation modal (mirrors the
  // Postpone / Cancel modals) instead of the old broken two-step inline confirm.
  // Un-complete stays a direct toggle (undo is symmetric — no confirmation).
  function handlePhaseCompleteClick(phase: ApiPhase) {
    if (phase.status === 'cancelled') return; // shouldn't render the item in this state
    if (phase.status === 'completed') {
      // Un-tick: always immediate (no confirmation; undo is symmetric).
      uncompletePhaseMutation.mutate(phase.id);
      return;
    }
    // Open the confirmation modal targeting this phase; Confirm fires the
    // existing completePhaseMutation.
    setCompleteErrorReason(null);
    completePhaseMutation.reset();
    setPendingCompletePhaseId(phase.id);
  }

  const cancelPhaseMutation = useMutation({
    mutationFn: (vars: { phaseId: string; body: PhaseCancelBody }) =>
      api.phases.cancel(vars.phaseId, vars.body),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['phases', job.id] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      // Phase 40-01 fixup (Tom UAT 2026-05-26): cancel-phase releases vehicles
      // + equipment server-side (via services/phases.ts UPDATE released_at).
      // Without these invalidations the fleet-wide busy-dates caches stay
      // stale until staleTime expires (5min) or page refresh, so a vehicle
      // released here doesn't immediately become assignable elsewhere. Same
      // shape as the add/delete mutations.
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      // Also refresh per-job resource lists since the cancelled phase's
      // assignments now render as released on the panel.
      queryClient.invalidateQueries({ queryKey: ['job-vehicles', job.id] });
      queryClient.invalidateQueries({ queryKey: ['job-equipment', job.id] });
      // Phase 78-01: materials section is now allocation-driven (work-item keyed).
      queryClient.invalidateQueries({ queryKey: ['work-item-allocations', job.workItemId] });
      queryClient.invalidateQueries({ queryKey: ['job-skills', job.id] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      // If we cancelled the currently-selected phase, fall back to the next
      // non-cancelled phase (or null if none remain — render handles it).
      if (vars.phaseId === selectedPhaseId) {
        const next = phases.find((p) => p.id !== vars.phaseId && p.status !== 'cancelled');
        setSelectedPhaseId(next?.id ?? null);
      }
      setPendingCancelPhase(false);
      setCancelPhaseReason('');
    },
  });

  // Count of non-cancelled phases drives both the navigator visibility
  // (>=2) and the Cancel Phase last-active guard (== 1 → disabled).
  // Phase 41-03 (#66) UAT 2026-06-01: gate Cancel + Add Phase buttons on
  // terminal-phase state.
  //   - Cancel Phase: hide when the selected phase is already in a
  //     terminal state (completed/cancelled). Cancelling a terminal phase
  //     is meaningless and confuses the audit trail.
  //   - Add Phase: hide when ALL phases are in terminal state. The job is
  //     effectively closed; adding a new phase would resurrect dead work.
  const selectedPhaseObj = useMemo(
    () => phases.find((p) => p.id === selectedPhaseId) ?? null,
    [phases, selectedPhaseId],
  );
  const isSelectedPhaseTerminal =
    selectedPhaseObj !== null &&
    (selectedPhaseObj.status === 'completed' || selectedPhaseObj.status === 'cancelled');
  const allPhasesTerminal =
    phases.length > 0 && phases.every((p) => p.status === 'completed' || p.status === 'cancelled');

  // Phase 78-01: the Materials section is now READ-ONLY (allocation-driven).
  // The HubSpot add/update/delete mutations + the Plan-04-02 review mutations
  // (corrections/confirm/reject) were removed — allocation is the planner's
  // reserve-from-stock surface (Slice C/78-02), not this panel section.

  const deleteEquipmentMutation = useMutation({
    mutationFn: ({ equipmentItemId, date }: { equipmentItemId: string; date: string }) =>
      api.jobs.deleteEquipment(job.id, equipmentItemId, date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-equipment', job.id] });
      // Phase 40-01 fixup: equipment busy-dates + board calendar must refresh.
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
    },
  });

  // Phase 36-02 (Issue #37): real bulk-add (drops 36-01's 1-line shim).
  const addEquipmentMutation = useMutation({
    mutationFn: (body: { equipment_item_id: string; dates: string[]; quantity: number }) =>
      api.jobs.addEquipment(job.id, {
        ...body,
        ...(selectedPhaseId ? { phase_id: selectedPhaseId } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-equipment', job.id] });
      // Phase 40-01 fixup: equipment busy-dates + board calendar must refresh.
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      setShowAddEquipment(false);
      setNewEquipmentItemId('');
      setNewEquipmentDates([]);
      setNewEquipmentQuantity(1);
      setEquipmentAddConflict([]);
      setEquipmentAddError(null);
    },
    onError: (err) => {
      if (err instanceof AddEquipmentConflictError) {
        setEquipmentAddConflict(err.conflicting_dates);
        setEquipmentAddError(null);
      } else {
        setEquipmentAddError(err instanceof Error ? err.message : 'Failed to add equipment');
        setEquipmentAddConflict([]);
      }
    },
  });

  // Phase 36-02 (Issue #37): per-row quantity edit via pen-icon modal.
  const patchEquipmentMutation = useMutation({
    mutationFn: ({
      equipmentItemId,
      date,
      quantity,
    }: {
      equipmentItemId: string;
      date: string;
      quantity: number;
    }) => api.jobs.patchEquipment(job.id, equipmentItemId, date, { quantity }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-equipment', job.id] });
      // Phase 40-01 fixup: quantity change can flip an item over capacity →
      // busy-dates must re-fetch. Calendar should also reflect quantity edit.
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      setEditingEquipmentRow(null);
    },
  });

  // Phase 34-03 (Issue #36): vehicle assignment mutations.
  const addVehiclesMutation = useMutation({
    mutationFn: (body: { vehicle_id: string; dates: string[] }) =>
      api.jobs.addVehicles(job.id, {
        ...body,
        ...(selectedPhaseId ? { phase_id: selectedPhaseId } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-vehicles', job.id] });
      // Phase 40-01 fixup: also invalidate ['vehicles'] (catches the
      // ['vehicles', vehicleId, 'busy-dates', ...] queryKey so the chip filter
      // hides the date the planner just allocated) + ['board'] (so the
      // calendar reflects the new vehicle pill without a page refresh).
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      setShowAddVehicle(false);
      setNewVehicleId('');
      setNewVehicleDates([]);
      setVehicleAddError(null);
      setVehicleAddConflicts([]);
    },
    onError: (err) => {
      if (err instanceof AddVehiclesConflictError) {
        setVehicleAddConflicts(err.conflicts);
        setVehicleAddError(null);
      } else {
        setVehicleAddError(err instanceof Error ? err.message : 'Failed to add vehicle');
        setVehicleAddConflicts([]);
      }
    },
  });

  const deleteVehicleMutation = useMutation({
    mutationFn: ({ vehicleId, date }: { vehicleId: string; date: string }) =>
      api.jobs.deleteVehicle(job.id, vehicleId, date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-vehicles', job.id] });
      // Phase 40-01 fixup: same invalidation set as add — busy-dates + calendar.
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
    },
  });

  const { data: requiredSkills = [], isLoading: skillsLoading } = useQuery<ApiSkill[]>({
    queryKey: ['job-skills', job.id, selectedPhaseId],
    queryFn: () => api.jobs.skills(job.id, selectedPhaseId ?? undefined),
    staleTime: 60 * 1000,
  });

  // All skills in catalogue — for the add-skill dropdown
  const { data: allSkills = [] } = useQuery<ApiSkill[]>({
    queryKey: ['skills'],
    queryFn: () => api.skills.list(),
    staleTime: 5 * 60 * 1000,
  });

  const addSkillMutation = useMutation({
    mutationFn: (skillId: string) =>
      api.jobs.addSkill(job.id, skillId, selectedPhaseId ?? undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-skills', job.id] });
    },
  });

  const removeSkillMutation = useMutation({
    // Phase 40-01 fixup: pass selectedPhaseId so the BE scopes the DELETE to
    // the active phase only — was dropping the skill from ALL phases otherwise.
    mutationFn: (skillId: string) =>
      api.jobs.removeSkill(job.id, skillId, selectedPhaseId ?? undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-skills', job.id] });
    },
  });

  const createAssignmentMutation = useMutation({
    mutationFn: (body: { staffId: string; startTime: string; endTime: string }) =>
      api.assignments.create({
        jobId: job.id,
        staffId: body.staffId,
        // Phase 40-01 fixup: single-day add uses the ACTIVE PHASE's start_date
        // (panelDate), not the parent job's start_date. Required because
        // Migration 067's trigger asserts date is within the phase's range.
        date: format(panelDate, 'yyyy-MM-dd'),
        startTime: body.startTime,
        endTime: body.endTime,
        ...(selectedPhaseId ? { phaseId: selectedPhaseId } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      queryClient.invalidateQueries({ queryKey: ['staff', 'consecutive-days-range'] });
      // GH #246: a new staff assignment creates scheduled labour cost — refresh
      // the per-phase cost/margin the panel renders.
      queryClient.invalidateQueries({ queryKey: ['phase-costs', job.id] });
      // Refresh the per-worker warnings banner for the active phase.
      queryClient.invalidateQueries({ queryKey: ['phase-warnings', job.id, selectedPhaseId] });
      setShowAddStaff(false);
      setNewStaffId('');
      setNewStartTime(defaultStartTime);
      setNewEndTime(defaultEndTime);
    },
  });

  const createBatchAssignmentMutation = useMutation({
    mutationFn: (body: {
      staffId: string;
      segments: { date: string; startTime: string; endTime: string }[];
    }) =>
      api.assignments.createBatch({
        jobId: job.id,
        staffId: body.staffId,
        segments: body.segments,
        ...(selectedPhaseId ? { phaseId: selectedPhaseId } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      queryClient.invalidateQueries({ queryKey: ['staff', 'consecutive-days-range'] });
      // GH #246: batch staff assignment creates scheduled labour cost.
      queryClient.invalidateQueries({ queryKey: ['phase-costs', job.id] });
      // Refresh the per-worker warnings banner for the active phase.
      queryClient.invalidateQueries({ queryKey: ['phase-warnings', job.id, selectedPhaseId] });
      setShowAddStaff(false);
      setShowPerDayGrid(false);
      setPerDaySegments([]);
      setNewStaffId('');
      setNewStartTime(defaultStartTime);
      setNewEndTime(defaultEndTime);
      setAddDaysStaffId(null);
      setAddDaysSegments([]);
    },
  });

  const deleteAssignmentMutation = useMutation({
    mutationFn: (assignmentId: string) => api.assignments.delete(assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      queryClient.invalidateQueries({ queryKey: ['staff', 'consecutive-days-range'] });
      // GH #246: removing an assignment drops scheduled labour cost.
      queryClient.invalidateQueries({ queryKey: ['phase-costs', job.id] });
      // Refresh the per-worker warnings banner for the active phase.
      queryClient.invalidateQueries({ queryKey: ['phase-warnings', job.id, selectedPhaseId] });
    },
  });

  const updateAssignmentMutation = useMutation({
    mutationFn: (body: { id: string; startTime: string; endTime: string }) =>
      api.assignments.update(body.id, { startTime: body.startTime, endTime: body.endTime }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      queryClient.invalidateQueries({ queryKey: ['staff', 'consecutive-days-range'] });
      // GH #246: editing an assignment's hours changes scheduled labour cost.
      queryClient.invalidateQueries({ queryKey: ['phase-costs', job.id] });
      // Refresh the per-worker warnings banner for the active phase.
      queryClient.invalidateQueries({ queryKey: ['phase-warnings', job.id, selectedPhaseId] });
      setEditingAssignmentId(null);
    },
  });

  // Inline date save (DIRECT path) — used when the dates did NOT change. Sends
  // start/end + active phase scoping with no reassignment strategy (BE default
  // = 'preserve', i.e. legacy prune of any out-of-range segments). The
  // whole-phase reassignment (dates changed) goes through the dialog +
  // useRescheduleFlow hook (see `reschedule.startReschedule` in
  // handleDateSaveClick below).
  const dateMutation = useMutation({
    mutationFn: () =>
      api.jobs.update(job.id, {
        startDate: editStartDate || undefined,
        endDate: editEndDate || null,
        // Phase 40-01 fixup: scope the date edit to the ACTIVE phase. Without
        // this the BE PATCH falls back to Phase 1 hardcoded, leaving the
        // non-Phase-1 phase's dates stale and the segment shift then trips
        // Migration 067's range trigger.
        ...(selectedPhaseId ? { phaseId: selectedPhaseId } : {}),
      }),
    onSuccess: (result) => {
      setSaveError(null);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      setDateEditMode(false);
      if (result.pruned.count > 0) {
        const names = result.pruned.staffNames.join(', ');
        setPrunedWarning(
          `${result.pruned.count} assignment(s) removed for ${names} — they fell outside the updated job schedule.`,
        );
      } else {
        onEdited?.();
      }
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Failed to save changes');
    },
  });

  // Phase 47 (GH #104): intercepts the date-edit Save click.
  //   - dates UNCHANGED → direct PATCH (no dialog).
  //   - dates CHANGED   → open the reassignment dialog + run the dry-run
  //                       preview (reschedulePreview) for Step 1.
  // Date comparison is PHASE-scoped (panelStartDate / panelDurationDays) so a
  // multi-day phase whose Phase-1 projection is single-day still compares its
  // own range (Phase 40-01 lineage).
  async function handleDateSaveClick() {
    const newStart = editStartDate;
    const newEnd = editEndDate || '';
    const oldStart = panelStartDate;
    const oldEnd =
      panelDurationDays > 1
        ? new Date(panelDate.getTime() + (panelDurationDays - 1) * 86400000)
            .toISOString()
            .slice(0, 10)
        : '';
    const datesChanged = Boolean(newStart && newStart !== oldStart) || newEnd !== oldEnd;

    if (!datesChanged) {
      dateMutation.mutate();
      return;
    }

    // Dates changed → delegate to the shared reschedule flow (dialog + dry-run
    // preview + apply mutation). onComplete closes the date-edit form on a
    // successful transfer/clear; onCancel (the hook default) just closes the
    // dialog and keeps the form open so the planner can revert.
    reschedule.startReschedule({
      jobId: job.id,
      phaseId: selectedPhaseId ?? '',
      newStartDate: newStart,
      newEndDate: newEnd,
      fromLabel:
        format(new Date(panelStartDate), 'd MMM') +
        (panelDurationDays > 1
          ? `–${format(addDays(panelDate, panelDurationDays - 1), 'd MMM')}`
          : ''),
      isPhase: !!activePhaseEarly,
      onComplete: () => {
        setDateEditMode(false);
        setSaveError(null);
        onEdited?.();
      },
    });
  }

  // Capture the draft's phase explicitly. The legacy endpoint defaults to phase
  // one when phaseId is omitted, so later-phase instructions must carry it.
  const scopeMutation = useMutation({
    mutationFn: ({
      phaseId,
      dailyScopes,
    }: {
      phaseId: string | null;
      dailyScopes: Record<string, string>;
    }) => api.jobs.update(job.id, { dailyScopes, ...(phaseId ? { phaseId } : {}) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      queryClient.invalidateQueries({ queryKey: ['phases', job.id] });
      setScopeEditMode(false);
    },
  });

  // Contracts manager update — Phase 39-07 (resource-scoping merge):
  // CM lives on the *active phase* now, not on the job. Update the phase
  // (or fall back to the job-level field for legacy un-phased jobs where
  // no phase is selected).
  const updateManagerMutation = useMutation({
    mutationFn: (newManagerId: string | null) =>
      selectedPhaseId
        ? api.phases
            .update(selectedPhaseId, { contracts_manager_id: newManagerId })
            .then(() => undefined)
        : api.jobs.update(job.id, { contractsManagerId: newManagerId }).then(() => undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      queryClient.invalidateQueries({ queryKey: ['phases', job.id] });
      setEditingManager(false);
    },
  });

  // Phase status (Provisional ↔ Confirmed) update.
  // 80-02 collapse: posts the single `status` field (was the booking-status field); the BE
  // updateJob drives a job_phases.status transition and returns phase_status. A
  // locked phase → 422 PHASE_STATUS_LOCKED, surfaced via the generic error path.
  // GH #107: scope the PATCH to the addressed phase via phaseId (without it the
  // BE hardcodes Phase 1 — see api.jobs.update doc) so a non-Phase-1 active
  // phase's status is actually written, and invalidate ['phases', id] so the
  // phase-box badge (which reads activePhase.status) refetches. The calendar
  // card already refreshed off ['jobs']/['board'].
  const updatePhaseStatusMutation = useMutation({
    mutationFn: ({ status, phaseId }: { status: 'provisional' | 'confirmed'; phaseId?: string }) =>
      api.jobs.update(job.id, { status, ...(phaseId ? { phaseId } : {}) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phases', job.id] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
    },
  });

  // Phase 47-01 (GH #67): per-phase operative-visibility toggle. Scoped to the
  // addressed phase; invalidate the phases query so the checkbox reflects the
  // persisted value after the PATCH.
  const updateViewableMutation = useMutation({
    mutationFn: ({ phaseId, viewable }: { phaseId: string; viewable: boolean }) =>
      api.jobs.update(job.id, { viewableByOperatives: viewable, phaseId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phases', job.id] });
    },
  });

  // Team Leader toggle
  const setTeamLeaderMutation = useMutation({
    mutationFn: ({ id, isTeamLeader }: { id: string; isTeamLeader: boolean }) =>
      api.assignments.setTeamLeader(id, isTeamLeader),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      setTlConfirm(null);
    },
  });

  // Phase 39-07 (resource-scoping merge): CM is read from the active phase
  // (per Tom: "all details including contracts manager … should change to
  // reflect the correct phase"). Fall back to the job-level field for legacy
  // un-phased jobs / transient no-selection states.
  const activePhase = selectedPhaseId ? phases.find((p) => p.id === selectedPhaseId) : null;
  const activeContractsManagerId =
    activePhase?.contracts_manager_id ?? job.contractsManagerId ?? null;
  const contractsManager = activeContractsManagerId ? staffMap.get(activeContractsManagerId) : null;
  const managerOptions = Array.from(staffMap.values()).filter((s) => s.role === 'manager');

  // Phase 40-01: tighten canEditActivePhase now that activePhase is known.
  // Cancelled or completed phases render read-only (existing rows visible but
  // not editable). Single-phase legacy jobs have no activePhase (selectedPhaseId
  // null) → fall back to canEditUnlocked unchanged.
  if (activePhase && (activePhase.status === 'cancelled' || activePhase.status === 'completed')) {
    canEditActivePhase = false;
  }

  // Phase 78-02 FIX 3: the reserve form + per-row Release are gated on the new
  // can('stock','reserve') permission (planner+) AND an editable active phase —
  // so a non-planner, or a locked/cancelled/completed phase, shows neither.
  const canReserveStock =
    can('stock', 'reserve') &&
    canEditActivePhase &&
    phasesQuery.isSuccess &&
    !phasesQuery.isFetching &&
    (phases.length === 0 || activePhase != null);

  const transitions = TRANSITIONS[job.status] ?? [];

  // Phase 23-05: !isJobLocked is redundant here (the status filter already
  // excludes completed/postponed/cancelled) but added for derivation
  // consistency with canEditActivePhase elsewhere.
  // Phase 40-01: also gated on active-phase status so equipment edits hide
  // when viewing a cancelled/completed phase (matches canEditActivePhase).
  const canEditEquipment =
    canEdit &&
    !isJobLocked &&
    (job.status === 'scheduled' || job.status === 'in_progress') &&
    canEditActivePhase;

  // Phase 34-03 (Issue #36): vehicle assignment mirrors equipment's edit-gate.
  const canEditVehicles = canEditEquipment;

  // #99 (Phase 52 follow-up): the status + dates edit pencils were lifted out of
  // the body and relocated INLINE into the Phase/Job box in the header. The
  // handlers, gating, and the inline date-edit FORM (still rendered in the body)
  // are unchanged — only the trigger buttons moved. Defined here as JSX consts so
  // both the multi-phase box and the singular "Job" box render the same controls.
  //
  // Status pencil — phase-status Provisional ↔ Confirmed toggle. Visibility
  // must track the DISPLAYED status, not the parent job. For a multi-phase job
  // the badge reflects the ACTIVE PHASE's status (in_progress / cancelled /
  // completed / provisional / confirmed); the job-level unifiedStatus.isStatusEditable
  // is only true when job.status === 'scheduled'. So a scheduled job whose
  // displayed active phase is in_progress would otherwise leak the pencil next to
  // an "In Progress" badge (#99 follow-up). canEditActivePhase already excludes
  // cancelled/completed phases; gate additionally on the active phase being in an
  // editable BOOKING state (provisional/confirmed) so in_progress also hides.
  // Singular legacy jobs have no activePhase → fall back to the job-level gate.
  const displayedStatusEditable =
    activePhase == null ||
    activePhase.status === 'provisional' ||
    activePhase.status === 'confirmed';
  const statusEditPencil =
    unifiedStatus.isStatusEditable && canEdit && canEditActivePhase && displayedStatusEditable ? (
      <button
        type="button"
        onClick={() => {
          // GH #107: the badge reflects the DISPLAYED (active-phase) status, so
          // derive the next value from it — not job.phaseStatus, which can
          // differ per phase on a multi-phase job. Fall back to the job-level
          // value for legacy un-phased jobs (no activePhase). The pencil only
          // shows for provisional/confirmed phases (displayedStatusEditable), so
          // current is one of those two.
          const current = activePhase?.status ?? job.phaseStatus;
          const next: 'provisional' | 'confirmed' =
            current === 'confirmed' ? 'provisional' : 'confirmed';
          updatePhaseStatusMutation.mutate({ status: next, phaseId: activePhase?.id });
        }}
        disabled={updatePhaseStatusMutation.isPending}
        className="p-0.5 rounded hover:bg-neutral-100 text-neutral-400 hover:text-[#F26522] transition-colors disabled:opacity-50"
        title="Edit status"
        aria-label="Edit status"
      >
        <Pencil className="w-3 h-3" />
      </button>
    ) : null;

  // Dates pencil — opens the inline date-edit form in the body. Visibility is
  // unchanged from the old body dates line: editable unless the job/phase is
  // postponed/completed/cancelled (canEditUnlocked + canEditActivePhase +
  // scheduled/in_progress). The onClick seeds the form from the ACTIVE phase's
  // dates exactly as before (Phase 40-01 fixup preserved).
  const dateEditPencil =
    canEditUnlocked &&
    canEditActivePhase &&
    (job.status === 'scheduled' || job.status === 'in_progress') ? (
      <button
        type="button"
        onClick={() => {
          setDateEditMode(true);
          setEditStartDate(panelStartDate);
          setEditEndDate(
            panelDurationDays > 1
              ? new Date(panelDate.getTime() + (panelDurationDays - 1) * 86400000)
                  .toISOString()
                  .slice(0, 10)
              : panelStartDate,
          );
        }}
        className="p-0.5 rounded hover:bg-neutral-100 text-neutral-400 hover:text-[#F26522] transition-colors"
        title="Edit dates"
        aria-label="Edit dates"
      >
        <Pencil className="w-3 h-3" />
      </button>
    ) : null;

  // Date range for the bulk-add vehicle picker. Phase 40-01: phase-scoped —
  // vehicles must be assigned to dates within the active phase (Migration 067
  // trigger asserts at the DB layer; mirror at the FE chip filter).
  const jobDateRange: string[] = Array.from({ length: panelDurationDays }, (_, i) =>
    format(addDays(panelDate, i), 'yyyy-MM-dd'),
  );

  // Phase 38-01 (UAT feedback 2026-05-19): lazy-fetched busy-dates for the
  // currently-selected vehicle in the +Add form. Enabled only when a vehicle
  // is picked. Used to filter the chip grid so planners only see definitively-
  // bookable dates (no in-flight 409 surprises). NOTE: don't use a destructured
  // `data = []` default — it creates a new array reference each render when
  // disabled, looping the useMemo + useEffect below. Keep the raw `data` and
  // default inside the deps-stable useMemo.
  const jobLastDate = jobDateRange[jobDateRange.length - 1] ?? jobDateRange[0];
  const busyDatesQuery = useVehicleBusyDates(
    newVehicleId,
    jobDateRange[0] ?? '',
    jobLastDate ?? '',
  );
  const vehicleBusyDatesLoading = busyDatesQuery.isLoading;
  const vehicleBusyDates = busyDatesQuery.data;
  const vehicleBusyDatesSet = useMemo(() => new Set(vehicleBusyDates ?? []), [vehicleBusyDates]);
  const availableChipDates = useMemo(
    () => jobDateRange.filter((d) => !vehicleBusyDatesSet.has(d)),
    [jobDateRange, vehicleBusyDatesSet],
  );

  // Phase 38-01: when busy-dates change (i.e. planner re-picks the vehicle
  // dropdown), drop any newVehicleDates entries that fall into the busy set
  // so submit body never includes them.
  useEffect(() => {
    setNewVehicleDates((prev) => prev.filter((d) => !vehicleBusyDatesSet.has(d)));
  }, [vehicleBusyDatesSet]);

  // Issue #222: group vehicle assignments by vehicle so the section mirrors the
  // Assigned Staff section (staffGroups) — each vehicle collapses to the
  // selected day by default and expands to reveal its other days on this
  // job/phase. Phase 38-01's date-ASC ordering is preserved WITHIN each group;
  // groups are ordered by their earliest assignment date (then registration) so
  // the visual order stays stable on multi-day jobs regardless of insertion.
  const vehicleGroups = useMemo(() => {
    const groups = Array.from(
      jobVehicles
        .reduce((map, v) => {
          if (!map.has(v.vehicle_id)) {
            map.set(v.vehicle_id, {
              vehicleId: v.vehicle_id,
              registration: v.registration,
              make: v.make,
              model: v.model,
              allAssignments: [] as ApiJobVehicle[],
            });
          }
          map.get(v.vehicle_id)!.allAssignments.push(v);
          return map;
        }, new Map<string, { vehicleId: string; registration: string; make: string; model: string; allAssignments: ApiJobVehicle[] }>())
        .values(),
    );
    for (const g of groups) {
      g.allAssignments.sort((a, b) => a.date.localeCompare(b.date));
    }
    return groups.sort(
      (a, b) =>
        (a.allAssignments[0]?.date ?? '').localeCompare(b.allAssignments[0]?.date ?? '') ||
        a.registration.localeCompare(b.registration),
    );
  }, [jobVehicles]);

  // Phase 38-02 (UAT feedback 2026-05-19): mirror of 38-01 chip filter onto
  // equipment. For N/A items (quantity IS NULL) the endpoint returns [] (never
  // unavailable per Q3=a); for quantity-tracked items it returns dates where
  // SUM(je.quantity) >= equipment_items.quantity. Same render-loop trap as
  // 38-01: keep raw `data` + default inside the deps-stable useMemo.
  const equipmentBusyDatesQuery = useEquipmentBusyDates(
    newEquipmentItemId,
    jobDateRange[0] ?? '',
    jobLastDate ?? '',
  );
  const equipmentBusyDatesLoading = equipmentBusyDatesQuery.isLoading;
  const equipmentBusyDatesRaw = equipmentBusyDatesQuery.data;
  const equipmentBusyDatesSet = useMemo(
    () => new Set(equipmentBusyDatesRaw ?? []),
    [equipmentBusyDatesRaw],
  );
  const availableEquipmentChipDates = useMemo(
    () => jobDateRange.filter((d) => !equipmentBusyDatesSet.has(d)),
    [jobDateRange, equipmentBusyDatesSet],
  );

  // Phase 38-02: drop chip selections that became invalid when busy-dates
  // shifted under us (e.g. planner ticked chip then re-picked item).
  useEffect(() => {
    setNewEquipmentDates((prev) => prev.filter((d) => !equipmentBusyDatesSet.has(d)));
  }, [equipmentBusyDatesSet]);

  // Phase 38-02: sort equipment rows date ASC primary, identifier ASC
  // secondary for stable tie-break on same-date multi-item rows.
  const sortedEquipment = useMemo(
    () =>
      [...equipment].sort(
        (a, b) => a.date.localeCompare(b.date) || a.identifier.localeCompare(b.identifier),
      ),
    [equipment],
  );

  function handleTransition(toStatus: ApiJob['status']) {
    if (toStatus === 'cancelled') {
      setPendingCancel(true);
      return;
    }
    if (toStatus === 'scheduled' && job.status === 'cancelled') {
      setPendingReschedule(true);
      return;
    }
    transitionMutation.mutate({ status: toStatus });
  }

  function handleConfirmCancel() {
    if (!cancelReason.trim()) return;
    transitionMutation.mutate({ status: 'cancelled', reason: cancelReason.trim() });
  }

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[420px] bg-white shadow-2xl z-50 flex flex-col border-l border-neutral-200">
      {/* Header — Phase 39-07: replaced 39-05 R4 minimal indicator with
          full phase navigator dropdown (T0 decision: dropdown, locked
          2026-05-26). +Add Phase + Cancel Phase header actions live in
          a secondary actions row below the title block. */}
      <div className="px-5 py-4 border-b border-neutral-200 bg-neutral-50">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <span className="text-[11px] font-mono text-neutral-500 tracking-wide">{job.ion}</span>
            <h2 className="text-[16px] text-neutral-900 mt-0.5 truncate">{job.dealName}</h2>
            {/* #99: the body "Status & Meta" line that carried the company link
                was removed. The company badge was NOT redundant (the body line's
                ION/title ARE duplicated above, but the company name only ever
                appeared there) — so for jobs tied to a REAL company (companyDomain
                set) re-home the linked company chip here under the deal name. Deal-
                fallback jobs (no companyDomain → client == deal name) drop it as
                genuinely redundant (Tom 2026-06-09, GH #99). */}
            {job.companyDomain && job.client && (
              <a
                href={`https://${job.companyDomain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex max-w-full items-center gap-1 text-[11px] text-neutral-600 hover:text-[#F26522] transition-colors"
                title={job.client}
              >
                <ExternalLink className="w-3 h-3 shrink-0" />
                <span className="truncate">{job.client}</span>
              </a>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {/* #95: lifecycle actions overflow (kebab) menu. Replaces the
                scattered +Add/Cancel Phase row buttons, the body Complete
                checkbox, and the footer Postpone/Cancel Job buttons. Each
                item triggers the SAME existing handler/flow (inline reason +
                confirm forms are unchanged — only the trigger moved here). */}
            {canEdit &&
              !postponedView &&
              (() => {
                // #105: a postponed tombstone exposes NO lifecycle actions.
                // The phase the menu's PHASE actions operate on: the selected
                // phase for multi-phase jobs, else the sole phase. Mirrors how
                // the old +Add/Complete/Cancel Phase controls picked a phase.
                const actionPhase =
                  phases.length >= 2 ? selectedPhaseObj : phases.length === 1 ? phases[0] : null;
                const isMultiPhase = phases.length >= 2;
                const phaseCompletable = actionPhase != null && actionPhase.status !== 'cancelled';
                const showPhaseGroup = phases.length >= 1;
                // #106: a ticket is a single one-off unit of work — it gets NO
                // phase actions (no Add phase / no phase navigator) and its
                // lifecycle group + box are labelled "Ticket" not "Job".
                const isTicket = job.source === 'hubspot_ticket';
                // GH #130: every job-state (lifecycle) action additionally
                // requires `contracts_board.manage_job_state`. AND it into each
                // show* flag so the existing lock/terminal-phase logic stays
                // intact and the permission is the only added requirement.
                const showAddPhase = canManageJobState && !allPhasesTerminal && !isTicket;
                // Completion: a genuine multi-phase job completes PER PHASE
                // ("Mark phase complete" in the Phase group). A singular job has
                // no phase concept for the user, so it completes via "Mark job
                // complete" at the top of the Job group (Tom 2026-06-09, GH #95).
                const showMarkPhaseComplete = canManageJobState && isMultiPhase && phaseCompletable;
                const showMarkJobComplete = canManageJobState && !isMultiPhase && phaseCompletable;
                // Cancel phase shows for ANY non-terminal selected phase on a
                // multi-phase job — including the LAST remaining active phase
                // (Tom 2026-06-09: "phases only, not tickets or jobs"). The
                // `phases.length >= 2` gate keeps it OFF singular jobs + tickets
                // (one phase). Cancelling the last active phase leaves all phases
                // terminal; the backend cascade (services/phases.ts) then drives
                // the parent job to cancelled (all-cancelled) or completed (any
                // prior phase completed) — see Part B.
                const showCancelPhase =
                  canManageJobState && phases.length >= 2 && !isSelectedPhaseTerminal;
                // #105: a postponed tombstone shows no job actions (Postpone /
                // Cancel) — guard with postponedView so the legacy case (job row
                // still 'scheduled' but opened from a postponement snapshot) is
                // also covered.
                const showJobActions =
                  canManageJobState &&
                  !postponedView &&
                  (job.status === 'scheduled' || job.status === 'in_progress');
                const showJobGroup = showMarkJobComplete || showJobActions;
                const showPhaseSection =
                  showPhaseGroup && (showAddPhase || showMarkPhaseComplete || showCancelPhase);
                if (!showPhaseSection && !showJobGroup) return null;
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      aria-label="Job actions"
                      className="p-2 hover:bg-neutral-200 rounded-md text-neutral-500 transition-colors outline-none"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      {showPhaseSection && (
                        <DropdownMenuGroup>
                          <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-neutral-500">
                            Phase
                          </DropdownMenuLabel>
                          {showAddPhase && (
                            <DropdownMenuItem
                              onSelect={() => {
                                setPendingAddPhase(true);
                                setPendingCancelPhase(false);
                              }}
                            >
                              <Plus className="w-4 h-4" />
                              Add phase
                            </DropdownMenuItem>
                          )}
                          {showMarkPhaseComplete && actionPhase && (
                            <DropdownMenuItem
                              onSelect={() => handlePhaseCompleteClick(actionPhase)}
                            >
                              <Check className="w-4 h-4" />
                              {actionPhase.status === 'completed'
                                ? 'Mark phase incomplete'
                                : 'Mark phase complete'}
                            </DropdownMenuItem>
                          )}
                          {showCancelPhase && (
                            <DropdownMenuItem
                              variant="destructive"
                              className="text-red-600"
                              onSelect={() => {
                                setPendingCancelPhase(true);
                                setPendingAddPhase(false);
                              }}
                            >
                              Cancel phase
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuGroup>
                      )}
                      {showPhaseSection && showJobGroup && <DropdownMenuSeparator />}
                      {showJobGroup && (
                        <DropdownMenuGroup>
                          <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-neutral-500">
                            {isTicket ? 'Ticket' : 'Job'}
                          </DropdownMenuLabel>
                          {showMarkJobComplete && actionPhase && (
                            <DropdownMenuItem
                              onSelect={() => handlePhaseCompleteClick(actionPhase)}
                            >
                              <Check className="w-4 h-4" />
                              {isTicket
                                ? actionPhase.status === 'completed'
                                  ? 'Mark ticket as incomplete'
                                  : 'Mark ticket as complete'
                                : actionPhase.status === 'completed'
                                  ? 'Mark job incomplete'
                                  : 'Mark job complete'}
                            </DropdownMenuItem>
                          )}
                          {showJobActions && (
                            <DropdownMenuItem onSelect={() => setPendingPostpone(true)}>
                              {isTicket ? 'Postpone ticket' : 'Postpone job'}
                            </DropdownMenuItem>
                          )}
                          {showJobActions && (
                            <DropdownMenuItem
                              variant="destructive"
                              className="text-red-600"
                              onSelect={() => handleTransition('cancelled')}
                            >
                              {isTicket ? 'Cancel ticket' : 'Cancel job'}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuGroup>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })()}
            <button
              onClick={onClose}
              className="p-2 hover:bg-neutral-200 rounded-md text-neutral-500 transition-colors shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Phase 39-07 (closes DEFERRED-39-05-D): historical postponement
            banner. Renders when the panel was opened via a click on a
            postponement-snapshot tombstone on the calendar. Banner is
            informational — the live parent job continues to render below. */}
        {postponementContext && (
          <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
            <strong>Historical snapshot</strong> — Phase {postponementContext.phaseNumber} postponed{' '}
            {(() => {
              const d = new Date(postponementContext.postponedAt);
              return format(d, 'd MMM yyyy');
            })()}
            {postponementContext.postponedBy && staffMap.get(postponementContext.postponedBy)
              ? ` by ${
                  staffMap.get(postponementContext.postponedBy)?.knownAs ??
                  staffMap.get(postponementContext.postponedBy)?.name
                }`
              : ''}
            . Showing the live job below.
            {postponementContext.postponedReason && (
              <span className="block mt-0.5 italic text-amber-700">
                Reason: {postponementContext.postponedReason}
              </span>
            )}
          </div>
        )}

        {/* Phase navigator + actions — only renders for multi-phase jobs
            (phases.length >= 2). Single-phase legacy jobs preserve today's
            UX (navigator hidden; +Add Phase still available so single-phase
            can promote to multi-phase; Cancel Phase hidden because cancelling
            the only phase is meaningless — use Cancel Job instead). */}
        {phases.length >= 2 && (
          <div className="mt-3 flex items-center gap-2">
            <select
              aria-label="Select phase"
              value={selectedPhaseId ?? ''}
              disabled={scopeEditMode || scopeMutation.isPending}
              title={
                scopeEditMode ? 'Save or cancel the scope edit before changing phase' : undefined
              }
              onChange={(e) => setSelectedPhaseId(e.target.value || null)}
              className="text-[12px] flex-1 min-w-0 px-2 py-1.5 rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-1 focus:ring-[#F26522]"
            >
              {phases.map((p) => (
                <option key={p.id} value={p.id}>
                  Phase {p.phase_number} of {phases.length}
                  {` · ${statusLabels[p.status] ?? p.status}`}
                </option>
              ))}
            </select>
            {/* #95: +Add Phase / Cancel Phase buttons relocated to the header
                kebab menu (PHASE group). The navigator <select> stays here. */}
          </div>
        )}

        {/* Phase 39-07 Spec [2] amendment (2026-05-26 Tom): per-phase
            metadata block gives the dropdown an immediate visible effect.
            Shows the active phase's dates + status + notes (+
            cancellation info when applicable). Per-phase RESOURCE sections
            (vehicles/equipment/skills/materials) stay all-phases until
            39-08 — this metadata block is the phase itself, not a
            resource section. */}
        {phases.length >= 2 &&
          selectedPhaseId &&
          (() => {
            const activePhase = phases.find((p) => p.id === selectedPhaseId);
            if (!activePhase) return null;
            const fmtDate = (iso: string) => format(new Date(iso + 'T00:00:00'), 'd MMM yyyy');
            return (
              <div className="mt-3 px-3 py-2 rounded-md bg-white border border-neutral-200 text-[11px] text-neutral-700 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-neutral-900">
                    Phase {activePhase.phase_number}
                  </span>
                  <span className="text-neutral-500">
                    {fmtDate(activePhase.start_date)} – {fmtDate(activePhase.end_date)}
                  </span>
                  {dateEditPencil}
                  <span
                    className={clsx(
                      'ml-auto px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide',
                      // #105: postponed job → amber POSTPONED badge (overrides the
                      // per-phase status/booking badge).
                      postponedView
                        ? 'bg-amber-100 text-amber-800'
                        : activePhase.status === 'cancelled'
                          ? 'bg-red-100 text-red-700'
                          : activePhase.status === 'completed'
                            ? 'bg-emerald-100 text-emerald-800'
                            : activePhase.status === 'in_progress'
                              ? 'bg-[#83de9b]/30 text-emerald-800'
                              : activePhase.status === 'confirmed'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-700',
                    )}
                  >
                    {postponedView
                      ? 'Postponed'
                      : activePhase.status === 'cancelled' ||
                          activePhase.status === 'completed' ||
                          activePhase.status === 'in_progress'
                        ? (statusLabels[activePhase.status] ?? activePhase.status)
                        : activePhase.status === 'confirmed'
                          ? 'Confirmed'
                          : 'Provisional'}
                  </span>
                  {statusEditPencil}
                </div>
                <PhaseNotesEditor
                  phase={activePhase}
                  canEdit={canEditActivePhase && job.status === 'scheduled'}
                />
                {activePhase.status === 'cancelled' && activePhase.cancelled_at && (
                  <p className="text-red-700">
                    Cancelled {fmtDate(activePhase.cancelled_at.slice(0, 10))}
                    {activePhase.cancelled_reason ? ` — ${activePhase.cancelled_reason}` : ''}
                  </p>
                )}
                {/* #95: per-phase Complete moved to the header kebab menu
                    ("Mark phase complete"). #120: the old two-step inline
                    confirm hint + inline 422 error were replaced by a central
                    AlertDialog modal (rendered below); nothing surfaces here. */}
                {/* Phase 47-01 (#67): per-phase operative-visibility toggle. */}
                {canEdit && activePhase.status !== 'cancelled' && (
                  <PhaseViewableCheckboxInline
                    phase={activePhase}
                    isPending={updateViewableMutation.isPending}
                    onChange={(next) =>
                      updateViewableMutation.mutate({ phaseId: activePhase.id, viewable: next })
                    }
                  />
                )}
                {/* Per-phase profitability — Amount/Budget/Cost/Margin (chargeable)
                    or Cost-to-business (ticket). Finance-gated on canEdit. */}
                {canEdit && (
                  <PhaseProfitabilityInline
                    key={activePhase.id}
                    phaseCost={costByPhaseId.get(activePhase.id)}
                    phase={activePhase}
                    isJobLocked={isJobLocked}
                    isTicket={isTicketJob}
                    jobAmount={job.hubspotAmount ?? null}
                    isPhased={true}
                    allPhasesBudgetTotal={allPhasesBudgetTotal}
                    isSaving={updatePhaseBudgetMutation.isPending}
                    onSaveBudget={(phaseId, value) =>
                      updatePhaseBudgetMutation.mutate({ phaseId, phase_budget: value })
                    }
                  />
                )}
              </div>
            );
          })()}

        {/* Singular job (exactly one phase): #99 — a "Job"-labelled box that
            mirrors the multi-phase metadata box (dates + JOB status badge + the
            two relocated edit pencils + Viewable checkbox) but with NO phase
            selector dropdown. Dates/badge come from job-level state (no
            activePhase). The visibility toggle + complete hint stay here.
            Phase 47-01 (#67): operative-visibility toggle. #95: +Add Phase and
            per-phase Complete live in the header kebab menu; confirm hint + 422
            error still surface inline. */}
        {phases.length < 2 &&
          (() => {
            // Singular = 0 or 1 phase. A real job always resolves to exactly one
            // phase once api.phases.list settles; phases.length === 0 is the
            // pre-load / phaseless state. Either way there is no phase selector,
            // so this box reads job-level dates + the unified JOB status.
            const solePhase = phases[0] ?? null;
            const jobStart = panelDate;
            const jobEnd = addDays(panelDate, panelDurationDays - 1);
            const fmtDate = (d: Date) => format(d, 'd MMM yyyy');
            return (
              <div className="mt-3 px-3 py-2 rounded-md bg-white border border-neutral-200 text-[11px] text-neutral-700 space-y-1">
                <div className="flex items-center gap-2">
                  {/* #106: ticket-sourced jobs label this box "Ticket". */}
                  <span className="font-semibold text-neutral-900">
                    {job.source === 'hubspot_ticket' ? 'Ticket' : 'Job'}
                  </span>
                  <span className="text-neutral-500">
                    {fmtDate(jobStart)} – {fmtDate(jobEnd)}
                  </span>
                  {dateEditPencil}
                  <span
                    className={clsx(
                      'ml-auto px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide',
                      unifiedStatus.pillClass,
                    )}
                  >
                    {unifiedStatus.label}
                  </span>
                  {statusEditPencil}
                </div>
                {solePhase && (
                  <PhaseNotesEditor
                    phase={solePhase}
                    canEdit={
                      canEditActivePhase &&
                      activePhase?.id === solePhase.id &&
                      job.status === 'scheduled'
                    }
                  />
                )}
                {/* Phase 47-01 (#67): single-phase jobs get the visibility toggle. */}
                {solePhase && canEdit && solePhase.status !== 'cancelled' && (
                  <PhaseViewableCheckboxInline
                    phase={solePhase}
                    isPending={updateViewableMutation.isPending}
                    onChange={(next) =>
                      updateViewableMutation.mutate({ phaseId: solePhase.id, viewable: next })
                    }
                  />
                )}
                {/* Per-phase profitability — single-phase: job-level Amount + Cost
                    + Margin (chargeable) or Cost-to-business (ticket). The budget
                    edits the sole phase. Finance-gated on canEdit. */}
                {canEdit && (
                  <PhaseProfitabilityInline
                    key={solePhase?.id ?? 'no-phase'}
                    phaseCost={solePhase ? costByPhaseId.get(solePhase.id) : undefined}
                    phase={solePhase}
                    isJobLocked={isJobLocked}
                    isTicket={isTicketJob}
                    jobAmount={job.hubspotAmount ?? null}
                    isPhased={false}
                    allPhasesBudgetTotal={allPhasesBudgetTotal}
                    isSaving={updatePhaseBudgetMutation.isPending}
                    onSaveBudget={(phaseId, value) =>
                      updatePhaseBudgetMutation.mutate({ phaseId, phase_budget: value })
                    }
                  />
                )}
                {/* #95: Complete moved to the kebab menu. #120: confirm + 422
                    error now live in the central AlertDialog modal below. */}
              </div>
            );
          })()}

        {/* Add Phase inline form (mirrors existing inline Cancel-Job
            confirmation pattern — no modal overlay). */}
        {pendingAddPhase && (
          <div className="mt-3 border border-neutral-200 rounded-lg p-3 space-y-2.5 bg-white">
            <p className="text-[12px] font-semibold text-neutral-800">Add a new phase</p>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[10px] text-neutral-600">Start date</span>
                <input
                  type="date"
                  value={phaseFormStartDate}
                  onChange={(e) => setPhaseFormStartDate(e.target.value)}
                  className="w-full text-[12px] px-2 py-1 rounded border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-[#F26522]"
                />
              </label>
              <label className="block">
                <span className="text-[10px] text-neutral-600">End date</span>
                <input
                  type="date"
                  value={phaseFormEndDate}
                  onChange={(e) => setPhaseFormEndDate(e.target.value)}
                  className="w-full text-[12px] px-2 py-1 rounded border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-[#F26522]"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-[10px] text-neutral-600">Status</span>
              <select
                value={phaseFormStatus}
                onChange={(e) => setPhaseFormStatus(e.target.value as 'provisional' | 'confirmed')}
                className="w-full text-[12px] px-2 py-1 rounded border border-neutral-300 bg-white focus:outline-none focus:ring-1 focus:ring-[#F26522]"
              >
                <option value="provisional">Provisional</option>
                <option value="confirmed">Confirmed</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] text-neutral-600">Contracts Manager</span>
              <select
                value={phaseFormContractsManagerId}
                onChange={(e) => setPhaseFormContractsManagerId(e.target.value)}
                className="w-full text-[12px] px-2 py-1 rounded border border-neutral-300 bg-white focus:outline-none focus:ring-1 focus:ring-[#F26522]"
              >
                <option value="">— None —</option>
                {managerOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.knownAs || m.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] text-neutral-600">Notes (optional)</span>
              <textarea
                value={phaseFormNotes}
                onChange={(e) => setPhaseFormNotes(e.target.value)}
                rows={2}
                className="w-full text-[12px] px-2 py-1 rounded border border-neutral-300 resize-none focus:outline-none focus:ring-1 focus:ring-[#F26522]"
              />
            </label>
            {addPhaseMutation.error && (
              <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                {(() => {
                  const raw = (addPhaseMutation.error as Error).message ?? '';
                  const match = raw.match(/^API \d+: (.+)$/s);
                  if (match) {
                    try {
                      const parsed = JSON.parse(match[1]);
                      if (typeof parsed?.error === 'string') return parsed.error;
                    } catch {
                      /* fall through */
                    }
                  }
                  return raw || 'Add phase failed — please try again.';
                })()}
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() =>
                  addPhaseMutation.mutate({
                    start_date: phaseFormStartDate,
                    end_date: phaseFormEndDate,
                    status: phaseFormStatus,
                    notes: phaseFormNotes.trim() || undefined,
                    contracts_manager_id: phaseFormContractsManagerId || null,
                  })
                }
                disabled={!phaseFormStartDate || !phaseFormEndDate || addPhaseMutation.isPending}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md bg-[#F26522] text-white text-[12px] hover:bg-[#d4561c] disabled:opacity-50 transition-colors"
              >
                {addPhaseMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                Add Phase
              </button>
              <button
                onClick={() => {
                  setPendingAddPhase(false);
                  addPhaseMutation.reset();
                }}
                className="px-3 py-1.5 rounded-md border border-neutral-300 text-[12px] text-neutral-700 hover:bg-neutral-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* GH #108 (part 2): Cancel Phase as a centered modal dialog (was an
            inline reason+confirm form). Mirrors the staff-deletion AlertDialog
            pattern. "Back" closes with no change; Confirm fires the existing
            cancelPhaseMutation with the existing cancelPhaseReason state. We use
            a plain button (not AlertDialogAction) for Confirm so the dialog
            stays open to show an inline error; onSuccess resets the state which
            closes the dialog. */}
        <AlertDialog
          open={pendingCancelPhase && selectedPhaseId != null}
          onOpenChange={(open) => {
            if (!open) {
              setPendingCancelPhase(false);
              setCancelPhaseReason('');
              cancelPhaseMutation.reset();
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Cancel Phase {phases.find((p) => p.id === selectedPhaseId)?.phase_number}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This releases all phase-scoped allocations.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <label className="block">
              <span className="sr-only">Reason for cancellation</span>
              <textarea
                value={cancelPhaseReason}
                onChange={(e) => setCancelPhaseReason(e.target.value)}
                placeholder="Reason for cancellation"
                rows={3}
                className="w-full text-[13px] border border-neutral-300 rounded-md px-2.5 py-1.5 resize-none bg-white focus:outline-none focus:ring-1 focus:ring-red-400"
              />
            </label>
            {cancelPhaseMutation.error && (
              <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                {(() => {
                  const raw = (cancelPhaseMutation.error as Error).message ?? '';
                  const match = raw.match(/^API \d+: (.+)$/s);
                  if (match) {
                    try {
                      const parsed = JSON.parse(match[1]);
                      if (typeof parsed?.error === 'string') return parsed.error;
                    } catch {
                      /* fall through */
                    }
                  }
                  return raw || 'Cancel phase failed.';
                })()}
              </p>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel>Back</AlertDialogCancel>
              <button
                onClick={() => {
                  if (!selectedPhaseId) return;
                  cancelPhaseMutation.mutate({
                    phaseId: selectedPhaseId,
                    body: { reason: cancelPhaseReason.trim() },
                  });
                }}
                disabled={!cancelPhaseReason.trim() || cancelPhaseMutation.isPending}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-red-600 text-white text-[13px] hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {cancelPhaseMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                Confirm Cancel Phase
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* GH #120: Mark-complete confirmation as a central AlertDialog modal
            (was a broken two-step inline "click again to confirm" hint).
            Mirrors the Postpone / Cancel modals. "Back" closes with no change;
            Confirm fires the existing completePhaseMutation (unchanged). The
            422 PHASE_COMPLETE_BLOCKED error surfaces INSIDE the dialog and the
            dialog stays open so the planner sees why completion was blocked.
            Noun varies: multi-phase = phase (with phase number); singular
            deal/manual job = "job {ion}"; ticket = "ticket {ion}". */}
        {(() => {
          const target = phases.find((p) => p.id === pendingCompletePhaseId) ?? null;
          // Singular = one (or zero) phases → "job"/"ticket" wording; multi =
          // per-phase wording with the phase number.
          const isMulti = phases.length >= 2;
          const jobNoun = isTicketJob ? 'ticket' : 'job';
          const completesJob =
            target != null &&
            job.status !== 'completed' &&
            phases.every(
              (p) => p.id === target.id || p.status === 'completed' || p.status === 'cancelled',
            );
          let title: string;
          let body: string;
          if (isMulti && target) {
            title = `Mark Phase ${target.phase_number} as complete?`;
            body = completesJob
              ? `This completes the phase and the ${jobNoun}.`
              : 'This completes the phase.';
            if (!completesJob && job.status !== 'completed') {
              body += ` The ${jobNoun} remains open while other phases are unfinished.`;
            }
          } else if (isTicketJob) {
            title = `Mark ticket ${job.ion} as complete?`;
            body = 'This completes the ticket.';
          } else {
            title = `Mark job ${job.ion} as complete?`;
            body = 'This completes the job.';
          }
          // The link ID is not part of the panel's Job model. Backend external
          // updates are conditional and best-effort, only on a new job cascade.
          if (completesJob && job.source === 'hubspot') {
            body += ' If a HubSpot deal is linked, Work will try to move it to Job-Complete.';
          } else if (completesJob && isTicketJob) {
            body += ' If a HubSpot ticket is linked, Work will try to close it.';
          }
          return (
            <AlertDialog
              open={pendingCompletePhaseId != null}
              onOpenChange={(open) => {
                if (!open) {
                  setPendingCompletePhaseId(null);
                  setCompleteErrorReason(null);
                  completePhaseMutation.reset();
                }
              }}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{title}</AlertDialogTitle>
                  <AlertDialogDescription>{body}</AlertDialogDescription>
                </AlertDialogHeader>
                {completeErrorReason !== null && (
                  <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                    {completeErrorReason}
                  </p>
                )}
                <AlertDialogFooter>
                  <AlertDialogCancel>Back</AlertDialogCancel>
                  <button
                    onClick={() => {
                      if (!pendingCompletePhaseId) return;
                      completePhaseMutation.mutate(pendingCompletePhaseId);
                    }}
                    disabled={completePhaseMutation.isPending}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-[#F26522] text-white text-[13px] hover:bg-[#d9551a] disabled:opacity-50 transition-colors"
                  >
                    {completePhaseMutation.isPending && (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    )}
                    Confirm
                  </button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          );
        })()}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Pruned assignment warning */}
        {prunedWarning && (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <span className="flex-1">{prunedWarning}</span>
            <button
              onClick={() => setPrunedWarning(null)}
              className="ml-2 text-amber-500 hover:text-amber-700"
              aria-label="Dismiss warning"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Linked Deal — Phase 32-02 (Issue #34 sub-item 1).
            Shown only for ticket-sourced jobs with an associated deal.
            Inline row: ION · Deal Name · Client + small "View Deal" button
            that opens a Popover with the same fields plus Site Address. */}
        {job.source === 'hubspot_ticket' && job.linkedDealId && <LinkedDealSection job={job} />}

        {/* #99: the body's "Status & Meta" block (unified status pill + booking
            pencil + client badge) was REMOVED. The status pill is duplicated by
            the Phase/Job box status badge in the header; the booking-status edit
            pencil was relocated INLINE next to that badge (statusEditPencil). The
            body now starts directly at the LOCATION row below. */}

        {/* Phase 32-04 (Issue #34 sub-item 2): location-source aware row.
            For ticket-source jobs we render a labelled chip (G2 / HQ / Client
            Site Address / Employee Address) followed by the resolved address
            (or the clock-in resolution note for the Employee variant).
            For deal/manual jobs (no locationSource) we keep the existing
            MapPin link unchanged. */}
        {(() => {
          const src = formatLocationSource(job.locationSource);
          if (src) {
            return (
              <div
                className="flex items-center gap-2 text-[13px] text-neutral-600"
                data-testid="location-source-row"
              >
                <MapPin className="w-4 h-4 text-neutral-400" />
                <span className="px-2 py-0.5 rounded bg-neutral-100 text-[11px] font-medium text-neutral-700">
                  {src.label}
                </span>
                <span className="text-neutral-300">·</span>
                {src.resolvedNote ? (
                  <span className="italic text-neutral-500">{src.resolvedNote}</span>
                ) : job.location ? (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#F26522] transition-colors"
                  >
                    {job.location}
                  </a>
                ) : (
                  <span className="text-neutral-400">—</span>
                )}
              </div>
            );
          }
          if (job.location) {
            return (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.location)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-[13px] text-neutral-600 hover:text-[#F26522] transition-colors"
              >
                <MapPin className="w-4 h-4 text-neutral-400" />
                {job.location}
              </a>
            );
          }
          return null;
        })()}

        {dateEditMode && (
          <div className="space-y-3">
            {saveError && (
              <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {saveError}
              </p>
            )}
            <div>
              <label className="block text-[12px] text-neutral-600 mb-1">Start date</label>
              <input
                type="date"
                value={editStartDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                className="w-full text-[13px] border border-neutral-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522]"
              />
              {startDateChanged && !startDateInPast && (
                <p className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  Moving this job to {editStartDate} — the HubSpot deal owner will be notified by
                  email.
                </p>
              )}
              {startDateInPast && (
                <p className="mt-1.5 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2.5 py-1.5 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  {startDateChanged
                    ? `This date (${editStartDate}) is in the past — confirm this is intended. The deal owner will be notified.`
                    : `Start date is in the past.`}
                </p>
              )}
            </div>
            <div>
              <label className="block text-[12px] text-neutral-600 mb-1">
                End date
                <span className="text-neutral-400 ml-1">
                  (optional — leave blank for single day)
                </span>
              </label>
              <input
                type="date"
                value={editEndDate}
                min={editStartDate || job.date.toISOString().slice(0, 10)}
                onChange={(e) => setEditEndDate(e.target.value)}
                className="w-full text-[13px] border border-neutral-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522]"
              />
            </div>
            {/* Phase 47 (GH #104): the reassignment dialog renders as a real
                modal (sibling at the end of the panel), not inline here.
                Opening it is handled by handleDateSaveClick on a date change. */}
            <div className="flex gap-2">
              <button
                onClick={() => void handleDateSaveClick()}
                disabled={dateMutation.isPending || reschedule.dialog !== null}
                className="flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-md bg-[#F26522] text-white text-[12px] hover:bg-[#d9561d] disabled:opacity-50 transition-colors"
              >
                {dateMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                Save
              </button>
              <button
                onClick={() => {
                  setDateEditMode(false);
                  reschedule.onCancel();
                  setSaveError(null);
                  setEditStartDate(job.startDate);
                  setEditEndDate(
                    job.durationDays > 1
                      ? new Date(job.date.getTime() + (job.durationDays - 1) * 86400000)
                          .toISOString()
                          .slice(0, 10)
                      : '',
                  );
                }}
                className="px-4 py-1.5 rounded-md border border-neutral-300 text-[12px] text-neutral-700 hover:bg-neutral-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {/* #99: the body's dates DISPLAY line was REMOVED — the active phase's
            dates now live in the Phase/Job box in the header, and the date-edit
            pencil was relocated INLINE next to those dates (dateEditPencil). The
            inline date-edit FORM above (dateEditMode block) is unchanged — its
            pencil trigger simply moved into the header box. */}

        {/* Phase 23-05: phase-status display moved to the unified status pill
            in the header (above). The 23-02 source-gated dropdown has been
            removed — the unified pill subsumes its functionality, gates pencil
            edit visibility per source, and adds lifecycle-state lock semantics. */}

        {/* Warnings */}
        {job.warnings.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1.5">
            {job.warnings.map((w, i) => (
              <div key={i} className="flex items-center gap-2 text-[12px] text-red-700">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                {w}
              </div>
            ))}
          </div>
        )}

        {/* Transition error */}
        {transitionMutation.isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-[12px] text-red-700 space-y-2">
            {(() => {
              const msg =
                transitionMutation.error instanceof Error
                  ? transitionMutation.error.message
                  : 'Failed to update job status';
              // Parse 409 conflict details for structured display
              const jsonMatch = msg.match(/^API 409: (.+)$/s);
              if (jsonMatch) {
                try {
                  const parsed = JSON.parse(jsonMatch[1]);
                  const conflicts = parsed?.details?.conflicts;
                  return (
                    <>
                      <p className="font-medium">{parsed.error ?? 'Schedule conflict'}</p>
                      {Array.isArray(conflicts) && conflicts.length > 0 && (
                        <ul className="list-disc pl-4 space-y-1">
                          {conflicts.map(
                            (
                              c: { staffName: string; date: string; conflictingJobName: string },
                              i: number,
                            ) => (
                              <li key={i}>
                                <span className="font-medium">{c.staffName}</span> on {c.date} —
                                conflicts with{' '}
                                <span className="font-medium">{c.conflictingJobName}</span>
                              </li>
                            ),
                          )}
                        </ul>
                      )}
                    </>
                  );
                } catch {
                  /* fall through to raw message */
                }
              }
              return <p>{msg}</p>;
            })()}
          </div>
        )}

        {/* Status transitions */}
        {transitions.length > 0 && !pendingCancel && !pendingReschedule && (
          <div className="flex flex-wrap gap-2">
            {transitions.map(({ toStatus, label, icon: Icon, variant }) => (
              <button
                key={toStatus}
                onClick={() => handleTransition(toStatus)}
                disabled={transitionMutation.isPending}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] transition-colors disabled:opacity-50',
                  variant === 'primary' && 'bg-[#F26522] text-white hover:bg-[#d9561d]',
                  variant === 'danger' && 'bg-red-600 text-white hover:bg-red-700',
                  variant === 'neutral' &&
                    'border border-neutral-300 text-neutral-700 hover:bg-neutral-100',
                )}
              >
                {transitionMutation.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Icon className="w-3.5 h-3.5" />
                )}
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Phase 37-01 (Issue #52): Cancel confirmation moved to footer
            (mirrors Postpone position) so it stays in view on tall panels.
            The reschedule confirmation stays here — it's a lighter affordance
            with no reason input. */}

        {/* Reschedule confirmation */}
        {pendingReschedule && (
          <div className="border border-amber-200 rounded-lg p-3 space-y-2.5 bg-amber-50">
            <p className="text-[12px] text-amber-800">
              Move <span className="font-medium">{job.dealName}</span> back to Scheduled? Existing
              assignments will be preserved.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => transitionMutation.mutate({ status: 'scheduled' })}
                disabled={transitionMutation.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] bg-[#F26522] text-white hover:bg-[#d9561d] disabled:opacity-50 transition-colors"
              >
                {transitionMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Confirm Reschedule
              </button>
              <button
                onClick={() => setPendingReschedule(false)}
                className="px-3 py-1.5 rounded-md text-[12px] border border-neutral-300 text-neutral-700 hover:bg-neutral-100 transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {/* Scope */}
        <Section
          title="Detail / Scope"
          icon={FileText}
          action={
            canEditActivePhase && phaseScopeReady && !scopeEditMode && !scopeMutation.isPending ? (
              <button
                onClick={() => {
                  setScopeEditMode(true);
                  setInlineDailyScopes({ ...activeDailyScopes });
                  setInlineScopeAllDays(false);
                }}
                className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-[#F26522] transition-colors"
                title="Edit scope"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            ) : undefined
          }
        >
          {!phaseScopeReady ? (
            <p role="status" className="text-[13px] text-neutral-500">
              {phasesQueryEarly.isPending
                ? 'Loading phase scope…'
                : 'Phase scope unavailable. Reload to try again.'}
            </p>
          ) : scopeEditMode ? (
            <div className="space-y-3">
              {panelDurationDays > 1 && (
                <div className="flex items-center justify-between">
                  <label className="text-[12px] text-neutral-600">Daily scope</label>
                  <button
                    type="button"
                    onClick={() => setInlineScopeAllDays((v) => !v)}
                    className="text-[11px] text-[#F26522] hover:underline"
                  >
                    {inlineScopeAllDays ? 'Single day' : 'All days'}
                  </button>
                </div>
              )}
              {inlineScopeAllDays ? (
                <div className="space-y-3">
                  {jobDays.map((dateStr) => (
                    <div key={dateStr}>
                      <p className="text-[11px] text-neutral-500 mb-1">
                        {format(parseISO(dateStr), 'EEE d MMM')}
                      </p>
                      <textarea
                        value={inlineDailyScopes[dateStr] ?? ''}
                        onChange={(e) =>
                          setInlineDailyScopes((prev) => ({ ...prev, [dateStr]: e.target.value }))
                        }
                        rows={2}
                        placeholder="Scope for this day…"
                        className="w-full text-[13px] border border-neutral-200 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522]"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <textarea
                  value={inlineDailyScopes[selectedDateKey ?? jobDays[0] ?? ''] ?? ''}
                  onChange={(e) => {
                    const key = selectedDateKey ?? jobDays[0] ?? '';
                    setInlineDailyScopes((prev) => ({ ...prev, [key]: e.target.value }));
                  }}
                  rows={3}
                  placeholder={`Scope for ${selectedDateKey ? format(parseISO(selectedDateKey), 'EEE d MMM') : 'this day'}…`}
                  className="w-full text-[13px] border border-neutral-200 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522]"
                />
              )}
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    scopeMutation.mutate({
                      phaseId: selectedPhaseId,
                      dailyScopes: inlineDailyScopes,
                    })
                  }
                  disabled={scopeMutation.isPending || !canEditActivePhase}
                  className="flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-md bg-[#F26522] text-white text-[12px] hover:bg-[#d9561d] disabled:opacity-50 transition-colors"
                >
                  {scopeMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                  Save
                </button>
                <button
                  onClick={() => setScopeEditMode(false)}
                  className="px-4 py-1.5 rounded-md border border-neutral-300 text-[12px] text-neutral-700 hover:bg-neutral-100 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : panelDurationDays > 1 ? (
            <div>
              <p className="text-[13px] text-neutral-600 leading-relaxed mb-2">
                {activeDailyScopes[selectedDateKey ?? jobDays[0] ?? ''] || (
                  <span className="italic text-neutral-400">No scope for this day</span>
                )}
              </p>
              {Object.keys(activeDailyScopes).length > 0 && (
                <details className="text-[12px]">
                  <summary className="cursor-pointer text-neutral-500 hover:text-neutral-700 select-none">
                    All days
                  </summary>
                  <div className="mt-2 space-y-2">
                    {jobDays.map((dateStr) =>
                      activeDailyScopes[dateStr] ? (
                        <div key={dateStr}>
                          <p className="text-[11px] text-neutral-500">
                            {format(parseISO(dateStr), 'EEE d MMM')}
                          </p>
                          <p className="text-[13px] text-neutral-700">
                            {activeDailyScopes[dateStr]}
                          </p>
                        </div>
                      ) : null,
                    )}
                  </div>
                </details>
              )}
            </div>
          ) : (
            <p className="text-[13px] text-neutral-600 leading-relaxed">
              {activeDailyScopes[jobDays[0] ?? ''] || (
                <span className="italic text-neutral-400">No scope notes</span>
              )}
            </p>
          )}
        </Section>

        {/* Contracts Manager */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <User className="w-4 h-4 text-neutral-400" />
              <span className="text-[12px] text-neutral-500 uppercase tracking-wide">
                Contracts Manager
              </span>
            </div>
            {canEditActivePhase && !editingManager && (
              <button
                onClick={() => {
                  setEditingManager(true);
                  setPendingManagerId(activeContractsManagerId ?? '');
                }}
                className="p-1 text-neutral-400 hover:text-neutral-600 transition-colors"
                aria-label="Edit contracts manager"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {editingManager && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => updateManagerMutation.mutate(pendingManagerId || null)}
                  disabled={updateManagerMutation.isPending}
                  className="p-1 text-green-600 hover:text-green-800 transition-colors"
                  aria-label="Save manager"
                >
                  {updateManagerMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                </button>
                <button
                  onClick={() => setEditingManager(false)}
                  className="p-1 text-neutral-400 hover:text-neutral-600 transition-colors"
                  aria-label="Cancel"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {editingManager ? (
            <select
              value={pendingManagerId}
              onChange={(e) => setPendingManagerId(e.target.value)}
              className="w-full text-[13px] border border-neutral-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522] bg-white"
            >
              <option value="">Select manager...</option>
              {managerOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.knownAs || m.name}
                </option>
              ))}
            </select>
          ) : contractsManager ? (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] text-white shrink-0 bg-purple-500">
                {contractsManager.initials ??
                  (contractsManager.knownAs || contractsManager.name || '?')
                    .split(' ')
                    .map((n) => n[0])
                    .join('')}
              </div>
              <div>
                <div className="text-[13px] text-neutral-900">
                  {contractsManager.knownAs || contractsManager.name}
                </div>
                <div className="text-[11px] text-neutral-500 capitalize">
                  {contractsManager.role}
                </div>
              </div>
            </div>
          ) : (
            <span className="text-[12px] text-neutral-400 italic">No manager assigned</span>
          )}
        </div>

        {/* Assigned Staff — collapsible with initials badges */}
        <div>
          <button
            onClick={() => setStaffOpen((o) => !o)}
            className="w-full flex items-center justify-between mb-2 group"
          >
            <div className="flex items-center gap-1.5">
              <User className="w-4 h-4 text-neutral-400" />
              <span className="text-[12px] text-neutral-500 uppercase tracking-wide">
                Assigned Staff
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              {staffGroups.length > 0 ? (
                <div className="flex items-center -space-x-1">
                  {staffGroups.map((group) => {
                    const role = group.staffRecord?.role ?? '';
                    const avatarBg =
                      role === 'manager'
                        ? 'bg-purple-500'
                        : role === 'subcontractor'
                          ? 'bg-[#ccf5ff]/70'
                          : 'bg-[#F26522]/20';
                    const avatarText = role === 'manager' ? 'text-white' : 'text-neutral-900';
                    const displayName =
                      group.staffRecord?.knownAs || group.staffRecord?.name || '?';
                    const initials =
                      group.staffRecord?.initials ??
                      displayName
                        .split(' ')
                        .map((n) => n[0])
                        .join('');
                    // #112: 1–2 char initials stay fixed circles (w-5); 3+ char
                    // initials (e.g. subcontractor "MJF1") become auto-width pills
                    // (px-1.5 min-w-5, whitespace-nowrap) so the text never spills
                    // out of the circle. Same h-5 keeps the header badge row aligned.
                    const isPill = (initials?.length ?? 0) >= 3;
                    return (
                      <div
                        key={group.staffId}
                        className={clsx(
                          'h-5 rounded-full flex items-center justify-center text-[9px] font-medium ring-1 ring-white shrink-0',
                          isPill ? 'px-1.5 min-w-5 whitespace-nowrap' : 'w-5',
                          avatarBg,
                          avatarText,
                        )}
                        title={displayName}
                      >
                        {initials}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="text-[11px] text-neutral-400">No staff assigned</span>
              )}
              {staffOpen ? (
                <ChevronDown className="w-3.5 h-3.5 text-neutral-400 ml-1" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5 text-neutral-400 ml-1" />
              )}
            </div>
          </button>
          {staffOpen && (
            <div className="space-y-2">
              {/* Per-worker assignment warnings (persistent; driven by the
                  phase-warnings query). Each returned worker is a row: avatar +
                  name; a single warning renders inline, multiple warnings
                  collapse behind an expand chevron mirroring the Assigned Staff
                  pattern. Dismissable; re-shows when the warnings change. */}
              {!warningsDismissed && workerWarnings.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-yellow-700 font-medium uppercase tracking-wide">
                      Assignment warnings
                    </span>
                    <button
                      onClick={() => setWarningsDismissed(true)}
                      className="text-yellow-500 hover:text-yellow-700 transition-colors"
                      aria-label="Dismiss warnings"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {workerWarnings.map((worker) => {
                    const staff = staffMap.get(worker.staffId);
                    const name = staff?.knownAs || staff?.name || 'Unknown';
                    const initials =
                      staff?.initials ??
                      name
                        .split(' ')
                        .map((n) => n[0])
                        .join('');
                    const multiple = worker.warnings.length > 1;
                    const isExpanded = expandedWarningStaffIds.has(worker.staffId);
                    const renderWarning = (w: ScheduleWarning, key: number) => (
                      <div
                        key={key}
                        className="text-[12px] text-yellow-800 flex items-start gap-1.5"
                      >
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <div>
                          <span>{w.message}</span>
                          {w.code === 'SKILL_MISMATCH' &&
                            Array.isArray(
                              (w.details as { missing_skills?: Array<{ name: string }> })
                                .missing_skills,
                            ) && (
                              <ul className="mt-0.5 pl-2 space-y-0.5">
                                {(
                                  w.details as { missing_skills: Array<{ name: string }> }
                                ).missing_skills.map((sk, j) => (
                                  <li
                                    key={j}
                                    className="text-[11px] text-yellow-700 before:content-['·'] before:mr-1"
                                  >
                                    {sk.name}
                                  </li>
                                ))}
                              </ul>
                            )}
                        </div>
                      </div>
                    );
                    return (
                      <div key={worker.staffId} className="space-y-1">
                        {/* Worker header row */}
                        <div
                          className={clsx(
                            'flex items-center justify-between',
                            multiple &&
                              'cursor-pointer select-none rounded hover:bg-yellow-100/60 -mx-1 px-1 py-0.5 transition-colors',
                          )}
                          onClick={
                            multiple ? () => toggleWarningStaffExpanded(worker.staffId) : undefined
                          }
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {multiple &&
                              (isExpanded ? (
                                <ChevronDown className="w-3.5 h-3.5 text-yellow-600 shrink-0" />
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5 text-yellow-600 shrink-0" />
                              ))}
                            <div className="w-6 h-6 rounded-full bg-yellow-200 text-yellow-900 flex items-center justify-center text-[10px] shrink-0">
                              {initials || '?'}
                            </div>
                            <span className="text-[12px] font-medium text-yellow-900 truncate">
                              {name}
                            </span>
                          </div>
                          {multiple ? (
                            <span className="text-[11px] text-yellow-700 shrink-0 ml-2">
                              {worker.warnings.length} warnings
                            </span>
                          ) : null}
                        </div>
                        {/* Warning bodies: single → always inline; multiple →
                            revealed on expand. */}
                        {multiple ? (
                          isExpanded && (
                            <div className="pl-8 space-y-1">
                              {worker.warnings.map((w, j) => renderWarning(w, j))}
                            </div>
                          )
                        ) : (
                          <div className="pl-8">{renderWarning(worker.warnings[0], 0)}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Team Leader missing warnings */}
              {datesMissingTl.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 space-y-0.5">
                  {datesMissingTl.map((d) => (
                    <div key={d} className="text-[12px] text-amber-800 flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      <span>No Team Leader assigned for {format(new Date(d), 'EEE d MMM')}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* #94 (panel half): Team Leader NOT-APPLICABLE indicator. Days
                  with assignments but no Reprotec operatives (fully
                  subcontracted) need no TL — show a subtle struck-through
                  shield, consistent with the amber warning's layout. */}
              {datesTlNotApplicable.length > 0 && (
                <div
                  className="bg-neutral-50 border border-neutral-200 rounded-lg p-2 space-y-0.5"
                  data-testid="tl-not-applicable"
                >
                  {datesTlNotApplicable.map((d) => (
                    <div key={d} className="text-[12px] text-neutral-400 flex items-center gap-1.5">
                      <ShieldOff className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                      <span>
                        Team Leader N/A (subcontracted) for {format(new Date(d), 'EEE d MMM')}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* TL confirmation dialog (Phase 26-06: multi-conflict-aware) */}
              {tlConfirm && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2">
                  <p className="text-[12px] text-neutral-800">
                    Setting <strong>{tlConfirm.staffName}</strong> as Team Leader
                    {tlConfirm.assignmentIds.length === 1
                      ? ' for this day'
                      : ` for ${tlConfirm.assignmentIds.length} days`}
                    {tlConfirm.conflicts.length > 0 && ' will replace the current Team Leader on:'}
                  </p>
                  {tlConfirm.conflicts.length > 0 && (
                    <ul className="text-[11px] text-neutral-700 space-y-0.5 ml-2 list-disc list-inside">
                      {tlConfirm.conflicts.map((c) => (
                        <li key={c.date}>
                          <strong>{c.currentTlName}</strong> on{' '}
                          {format(new Date(c.date), 'EEE d MMM')}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        tlConfirm.assignmentIds.forEach((id) => {
                          setTeamLeaderMutation.mutate({
                            id,
                            isTeamLeader: true,
                          });
                        });
                        setTlConfirm(null);
                      }}
                      disabled={setTeamLeaderMutation.isPending}
                      className="px-3 py-1 text-[11px] bg-[#F26522] text-white rounded hover:bg-[#d4571e] transition-colors disabled:opacity-50"
                    >
                      {setTeamLeaderMutation.isPending ? 'Updating...' : 'Confirm'}
                    </button>
                    <button
                      onClick={() => setTlConfirm(null)}
                      className="px-3 py-1 text-[11px] border border-neutral-300 text-neutral-600 rounded hover:bg-neutral-100 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {staffGroups.length === 0 && !showAddStaff && (
                <span className="text-[12px] text-neutral-400 italic">No staff assigned</span>
              )}
              {staffGroups.map((group) => {
                const isExpanded = expandedStaffIds.has(group.staffId);
                const role = group.staffRecord?.role;
                const todayAssignments = selectedDateKey
                  ? group.allAssignments.filter((a) => a.date === selectedDateKey)
                  : group.allAssignments;
                const visibleAssignments = isExpanded ? group.allAssignments : todayAssignments;
                const avatarBg =
                  role === 'manager'
                    ? 'bg-purple-500'
                    : role === 'subcontractor'
                      ? 'bg-[#ccf5ff]/70'
                      : 'bg-[#F26522]/20';

                const hasUnassignedDays =
                  panelDurationDays > 1 &&
                  jobDays.some((d) => !group.allAssignments.some((a) => a.date === d));

                return (
                  <div
                    key={group.staffId}
                    className="rounded-lg border border-neutral-200 overflow-hidden"
                  >
                    {/* Staff header row — click to expand/collapse */}
                    <div
                      className="flex items-center justify-between p-2.5 bg-neutral-50 cursor-pointer hover:bg-neutral-100 transition-colors select-none"
                      onClick={() => toggleStaffExpanded(group.staffId)}
                    >
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                        )}
                        <div
                          className={clsx(
                            'w-7 h-7 rounded-full flex items-center justify-center text-[10px] shrink-0',
                            avatarBg,
                            role === 'manager' ? 'text-white' : 'text-neutral-900',
                          )}
                        >
                          {group.staffRecord?.initials ??
                            (group.staffRecord?.knownAs || group.staffRecord?.name || '?')
                              .split(' ')
                              .map((n) => n[0])
                              .join('')}
                        </div>
                        <div>
                          <div className="text-[13px] text-neutral-900">
                            {group.staffRecord?.knownAs || group.staffRecord?.name || 'Unknown'}
                          </div>
                          <div className="text-[11px] text-neutral-500 capitalize">
                            {role ?? 'staff'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Add days button — only for multi-day jobs with unassigned days */}
                        {canEditActivePhase && hasUnassignedDays && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (addDaysStaffId === group.staffId) {
                                setAddDaysStaffId(null);
                                setAddDaysSegments([]);
                              } else {
                                setAddDaysStaffId(group.staffId);
                                // Segments will be populated by the useEffect when leave/conflict data arrives
                                setAddDaysSegments([]);
                              }
                            }}
                            className={clsx(
                              'p-1 rounded transition-colors',
                              addDaysStaffId === group.staffId
                                ? 'text-[#F26522] bg-orange-50'
                                : 'text-neutral-300 hover:text-[#F26522] hover:bg-orange-50',
                            )}
                            title="Add extra days"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {/* Collapsed summary: today's time or "Not working today" /
                            "Not assigned to this job today" depending on whether
                            the staff is on another phase/job today.
                            Phase 40-01 fixup (Tom UAT 2026-05-26): pre-fix the
                            label was always "Not working today" even when the
                            staff WAS working another phase — misleading. */}
                        {!isExpanded && (
                          <div className="text-right">
                            {todayAssignments.length > 0 ? (
                              <div className="text-[12px] text-neutral-700">
                                {todayAssignments[0].startTime.slice(0, 5)}–
                                {todayAssignments[0].endTime.slice(0, 5)}
                              </div>
                            ) : (
                              (() => {
                                // Check cross-phase: is this staff assigned to a
                                // sibling phase of THIS job on the selected date?
                                const elsewhere = selectedDateKey
                                  ? job.assignedStaff.some(
                                      (a) =>
                                        a.staffId === group.staffId &&
                                        a.date === selectedDateKey &&
                                        a.phaseId !== selectedPhaseId,
                                    )
                                  : false;
                                return (
                                  <div className="text-[11px] text-neutral-400 italic">
                                    {elsewhere
                                      ? 'Not assigned to this phase today'
                                      : 'Not working today'}
                                  </div>
                                );
                              })()
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Assignment rows */}
                    {visibleAssignments.length > 0 && (
                      <div className="divide-y divide-neutral-100">
                        {visibleAssignments.map((a) => (
                          <div
                            key={a.assignmentId}
                            className="flex items-center justify-between px-2.5 py-2 bg-white"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {isExpanded && (
                                <span className="text-[11px] text-neutral-400 w-20 shrink-0">
                                  {format(new Date(a.date), 'EEE d MMM')}
                                </span>
                              )}
                              {editingAssignmentId === a.assignmentId ? (
                                <div className="flex items-center gap-1.5">
                                  <input
                                    type="time"
                                    value={editStartTime}
                                    onChange={(e) => setEditStartTime(e.target.value)}
                                    className="text-[12px] border border-neutral-300 rounded px-1.5 py-0.5 w-24 focus:outline-none focus:ring-1 focus:ring-[#F26522]"
                                  />
                                  <span className="text-[11px] text-neutral-400">–</span>
                                  <input
                                    type="time"
                                    value={editEndTime}
                                    onChange={(e) => setEditEndTime(e.target.value)}
                                    className="text-[12px] border border-neutral-300 rounded px-1.5 py-0.5 w-24 focus:outline-none focus:ring-1 focus:ring-[#F26522]"
                                  />
                                </div>
                              ) : (
                                <div className="text-[12px] text-neutral-700">
                                  {a.startTime.slice(0, 5)}–{a.endTime.slice(0, 5)}
                                  <span className="text-neutral-400 ml-1.5">{a.hours}h</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {editingAssignmentId === a.assignmentId ? (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (a.assignmentId) {
                                        updateAssignmentMutation.mutate({
                                          id: a.assignmentId,
                                          startTime: editStartTime,
                                          endTime: editEndTime,
                                        });
                                      }
                                    }}
                                    disabled={updateAssignmentMutation.isPending}
                                    className="px-2 py-0.5 text-[11px] bg-[#F26522] text-white rounded hover:bg-[#d4571e] transition-colors disabled:opacity-50"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingAssignmentId(null);
                                    }}
                                    className="px-2 py-0.5 text-[11px] border border-neutral-300 text-neutral-600 rounded hover:bg-neutral-100 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  {/* Team Leader toggle — only for Reprotec operatives */}
                                  {role === 'operative' &&
                                    canEditActivePhase &&
                                    (() => {
                                      const isPast = a.date < today;
                                      const isTl = a.isTeamLeader === true;
                                      // Issue #223: a collapsed group's visible
                                      // row may be a PAST day, but the shield
                                      // bulk-applies TL to this staff's FUTURE
                                      // days — so it must stay clickable while
                                      // any future day exists. Unset stays
                                      // single-day (gated by the visible row);
                                      // expanded set is per-day (gated by the
                                      // row); collapsed set is gated only by the
                                      // absence of future days.
                                      const hasFutureDays = group.allAssignments.some(
                                        (x) => x.date >= today,
                                      );
                                      const tlBlocked = isTl
                                        ? isPast
                                        : isExpanded
                                          ? isPast
                                          : !hasFutureDays;
                                      return (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (tlBlocked) return;
                                            if (isTl) {
                                              // Unset TL is always single-day
                                              // — bulk-unset would surprise.
                                              setTeamLeaderMutation.mutate({
                                                id: a.assignmentId,
                                                isTeamLeader: false,
                                              });
                                              return;
                                            }
                                            // Phase 26-06: collapsed click
                                            // bulk-applies TL to all of this
                                            // staff's future days; expanded
                                            // click stays per-day.
                                            const targets = isExpanded
                                              ? [a]
                                              : group.allAssignments.filter((x) => x.date >= today);
                                            const conflicts = targets
                                              .map((target) => {
                                                // Phase 40-01 fixup (Tom UAT 2026-05-26):
                                                // TL conflicts must be PHASE-scoped — each
                                                // phase has its own TL. Pre-fix this used
                                                // job-wide search and falsely flagged the
                                                // TL of a different phase as a "replace".
                                                // assignment_segments.phase_id is NOT NULL
                                                // since Migration 061; adapters.ts projects
                                                // it onto JobAssignment.phaseId in 39-07.
                                                const existingTl = job.assignedStaff.find(
                                                  (other) =>
                                                    other.date === target.date &&
                                                    other.isTeamLeader === true &&
                                                    other.staffId !== group.staffId &&
                                                    other.phaseId === target.phaseId,
                                                );
                                                if (!existingTl) return null;
                                                const existingStaff = staffMap.get(
                                                  existingTl.staffId,
                                                );
                                                return {
                                                  date: target.date,
                                                  currentTlName:
                                                    existingStaff?.knownAs ||
                                                    existingStaff?.name ||
                                                    'Unknown',
                                                };
                                              })
                                              .filter(
                                                (
                                                  x,
                                                ): x is {
                                                  date: string;
                                                  currentTlName: string;
                                                } => x !== null,
                                              );
                                            const newName =
                                              group.staffRecord?.knownAs ||
                                              group.staffRecord?.name ||
                                              'Unknown';
                                            if (conflicts.length > 0) {
                                              setTlConfirm({
                                                assignmentIds: targets.map((t) => t.assignmentId),
                                                staffName: newName,
                                                conflicts,
                                              });
                                            } else {
                                              targets.forEach((t) => {
                                                setTeamLeaderMutation.mutate({
                                                  id: t.assignmentId,
                                                  isTeamLeader: true,
                                                });
                                              });
                                            }
                                          }}
                                          disabled={tlBlocked || setTeamLeaderMutation.isPending}
                                          className={clsx(
                                            'p-1 rounded transition-colors',
                                            tlBlocked && 'cursor-not-allowed opacity-40',
                                            isTl
                                              ? 'text-[#F26522] hover:bg-orange-50'
                                              : 'text-neutral-300 hover:text-[#F26522] hover:bg-orange-50',
                                          )}
                                          title={
                                            isTl
                                              ? 'Team Leader (click to remove)'
                                              : tlBlocked
                                                ? 'All assigned days are in the past'
                                                : 'Set as Team Leader'
                                          }
                                        >
                                          <Shield className="w-3.5 h-3.5" />
                                        </button>
                                      );
                                    })()}
                                  {canEditActivePhase && (
                                    <>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          startEditAssignment(a);
                                        }}
                                        className="p-1 rounded text-neutral-300 hover:text-[#F26522] hover:bg-orange-50 transition-colors"
                                        title="Edit assignment"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                      </button>
                                      {/* P-C UX-2: destructive remove now gated
                                          behind an AlertDialog confirmation (was a
                                          single-click direct mutate). */}
                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                            }}
                                            disabled={deleteAssignmentMutation.isPending}
                                            className="p-1 rounded text-neutral-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                            title="Remove assignment"
                                          >
                                            <X className="w-3.5 h-3.5" />
                                          </button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>
                                              Remove staff allocation?
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                              Remove{' '}
                                              {group.staffRecord?.knownAs ||
                                                group.staffRecord?.name ||
                                                'this staff member'}{' '}
                                              from {formatDateDDMMYY(a.date)}? This cannot be
                                              undone.
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:pointer-events-none"
                                              disabled={deleteAssignmentMutation.isPending}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (a.assignmentId) {
                                                  deleteAssignmentMutation.mutate(a.assignmentId);
                                                }
                                              }}
                                            >
                                              Remove
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add-days mini per-day grid */}
                    {addDaysStaffId === group.staffId && (
                      <div className="border-t border-neutral-200 bg-orange-50/50 px-3 py-2.5 space-y-2">
                        <div className="text-[11px] text-neutral-600 font-medium">
                          Add days for{' '}
                          {group.staffRecord?.knownAs || group.staffRecord?.name || 'Unknown'}
                        </div>
                        {addDaysSegments.length === 0 ? (
                          // Add-days phase-scoping fix (Fix B): only show "Loading…" while the
                          // add-days queries are actually fetching; otherwise the
                          // grid is genuinely empty (no addable days for this
                          // phase) and we say so instead of spinning forever.
                          <div className="text-[11px] text-neutral-400 italic">
                            {addDaysLoading
                              ? 'Loading…'
                              : 'No days available to add for this phase.'}
                          </div>
                        ) : (
                          <>
                            <div className="space-y-1">
                              {addDaysSegments.map((seg, i) => (
                                <div
                                  key={seg.date}
                                  className={clsx(
                                    'flex items-center gap-1.5 text-[11px] rounded px-1 py-0.5',
                                    seg.isLeave && 'bg-amber-50 opacity-60',
                                    seg.isConflict && 'bg-red-50 opacity-60',
                                    !seg.isLeave && !seg.isConflict && !seg.enabled && 'opacity-40',
                                  )}
                                >
                                  {!seg.isLeave && !seg.isConflict && (
                                    <input
                                      type="checkbox"
                                      checked={seg.enabled}
                                      onChange={() => {
                                        const updated = [...addDaysSegments];
                                        updated[i] = { ...seg, enabled: !seg.enabled };
                                        setAddDaysSegments(updated);
                                      }}
                                      className="w-3.5 h-3.5 shrink-0 accent-[#F26522]"
                                    />
                                  )}
                                  <span
                                    className={clsx(
                                      'w-[72px] shrink-0 text-neutral-600 font-mono',
                                      (seg.isLeave || seg.isConflict) && 'ml-5',
                                    )}
                                  >
                                    {format(new Date(seg.date + 'T00:00:00'), 'EEE dd/MM')}
                                  </span>
                                  {seg.isLeave ? (
                                    <span className="text-amber-700 text-[10px] italic">
                                      On Leave
                                    </span>
                                  ) : seg.isConflict ? (
                                    <span className="text-red-600 text-[10px] italic">
                                      Conflict: {seg.conflictLabel}
                                    </span>
                                  ) : seg.enabled ? (
                                    <>
                                      <input
                                        type="time"
                                        value={seg.startTime}
                                        onChange={(e) => {
                                          const updated = [...addDaysSegments];
                                          updated[i] = { ...seg, startTime: e.target.value };
                                          setAddDaysSegments(updated);
                                        }}
                                        className="w-[80px] text-[11px] border border-neutral-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#F26522]"
                                      />
                                      <span className="text-neutral-300">–</span>
                                      <input
                                        type="time"
                                        value={seg.endTime}
                                        onChange={(e) => {
                                          const updated = [...addDaysSegments];
                                          updated[i] = { ...seg, endTime: e.target.value };
                                          setAddDaysSegments(updated);
                                        }}
                                        className="w-[80px] text-[11px] border border-neutral-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#F26522]"
                                      />
                                    </>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-2 pt-1">
                              <button
                                disabled={
                                  addDaysSegments.filter(
                                    (s) => !s.isLeave && !s.isConflict && s.enabled,
                                  ).length === 0 || createBatchAssignmentMutation.isPending
                                }
                                onClick={() => {
                                  const segments = addDaysSegments
                                    .filter((s) => !s.isLeave && !s.isConflict && s.enabled)
                                    .map(({ date, startTime, endTime }) => ({
                                      date,
                                      startTime,
                                      endTime,
                                    }));
                                  if (segments.length === 0) return;
                                  createBatchAssignmentMutation.mutate({
                                    staffId: group.staffId,
                                    segments,
                                  });
                                }}
                                className="flex-1 py-1.5 rounded-md text-[12px] bg-[#F26522] text-white hover:bg-[#d9561d] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              >
                                {createBatchAssignmentMutation.isPending
                                  ? 'Adding…'
                                  : `Add days (${addDaysSegments.filter((s) => !s.isLeave && !s.isConflict && s.enabled).length})`}
                              </button>
                              <button
                                onClick={() => {
                                  setAddDaysStaffId(null);
                                  setAddDaysSegments([]);
                                }}
                                className="px-3 py-1.5 text-[12px] border border-neutral-300 text-neutral-600 rounded-md hover:bg-neutral-100 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add staff inline form */}
              {showAddStaff ? (
                <div className="border border-neutral-200 rounded-lg p-3 space-y-2 bg-neutral-50">
                  <select
                    value={newStaffId}
                    onChange={(e) => setNewStaffId(e.target.value)}
                    className="w-full text-[12px] border border-neutral-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#F26522]"
                  >
                    <option value="">Select staff member…</option>
                    {(() => {
                      // Exclude any staff already assigned (even partially) — extra days go via the + icon
                      const assignedIds = new Set(staffGroups.map((g) => g.staffId));
                      const available = Array.from(staffMap.values())
                        .filter((s) => !assignedIds.has(s.id))
                        .sort((a, b) => a.name.localeCompare(b.name));
                      const employees = available.filter((s) => s.role === 'operative');
                      const subs = available.filter((s) => s.role === 'subcontractor');
                      return (
                        <>
                          {employees.length > 0 && (
                            <optgroup label="Employees">
                              {employees.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.knownAs || s.name}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {subs.length > 0 && (
                            <optgroup label="Subcontractors">
                              {subs.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.knownAs || s.name}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </>
                      );
                    })()}
                  </select>
                  {conflictDays.length > 0 && (
                    <div className="rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2 text-[11px] text-amber-800 space-y-0.5">
                      <p className="font-medium">Already assigned on:</p>
                      {conflictDays.map((c) => (
                        <p key={c.date}>
                          {'• '}
                          {format(parseISO(c.date), 'EEE d MMM')}
                          {c.busy[0] && (
                            <span className="text-amber-600 ml-1">
                              ({c.busy[0].start_time.slice(0, 5)}–{c.busy[0].end_time.slice(0, 5)}{' '}
                              on {c.busy[0].ion}{' '}
                              {extractDealName(c.busy[0].job_name ?? '', c.busy[0].ion)})
                            </span>
                          )}
                        </p>
                      ))}
                      {panelDurationDays > 1 && (
                        <p className="text-amber-600 mt-0.5">
                          Adjust times per day below to avoid the overlap.
                        </p>
                      )}
                      {panelDurationDays === 1 && (
                        <p className="text-amber-600 mt-0.5">
                          Adjust start/end times to avoid the overlap.
                        </p>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="text-[10px] text-neutral-500 uppercase tracking-wide">
                        Start
                      </label>
                      <input
                        type="time"
                        value={newStartTime}
                        onChange={(e) => setNewStartTime(e.target.value)}
                        className="w-full text-[12px] border border-neutral-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#F26522]"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] text-neutral-500 uppercase tracking-wide">
                        End
                      </label>
                      <input
                        type="time"
                        value={newEndTime}
                        onChange={(e) => setNewEndTime(e.target.value)}
                        className="w-full text-[12px] border border-neutral-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#F26522]"
                      />
                    </div>
                  </div>
                  {/* Per-day breakdown for multi-day phases. Phase 40-01:
                       phase-scoped (was job-scoped pre-40-01 which hid this
                       affordance on multi-day phases of jobs where Phase 1 is
                       single-day). */}
                  {panelDurationDays > 1 && newStaffId && (
                    <div className="border border-neutral-200 rounded-md bg-white">
                      <button
                        type="button"
                        onClick={() => {
                          const next = !showPerDayGrid;
                          setShowPerDayGrid(next);
                          if (next && perDaySegments.length === 0) {
                            setPerDaySegments(generatePerDaySegments(newStartTime, newEndTime));
                          }
                        }}
                        className="w-full flex items-center justify-between px-2 py-1.5 text-[11px] text-neutral-600 hover:bg-neutral-50 transition-colors"
                      >
                        <span>Per-day hours ({panelDurationDays} days)</span>
                        {showPerDayGrid ? (
                          <ChevronDown className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5" />
                        )}
                      </button>
                      {showPerDayGrid && (
                        <div className="border-t border-neutral-200 px-2 py-2 space-y-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] text-neutral-400 uppercase tracking-wide">
                              Date / Start / End
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  const allEnabled = perDaySegments.every(
                                    (s) =>
                                      s.isLeave ||
                                      s.isExisting ||
                                      s.isConflict ||
                                      s.isWtrBlocked ||
                                      s.enabled,
                                  );
                                  setPerDaySegments(
                                    perDaySegments.map((s) =>
                                      s.isLeave || s.isExisting || s.isConflict || s.isWtrBlocked
                                        ? s
                                        : { ...s, enabled: !allEnabled },
                                    ),
                                  );
                                }}
                                className="text-[10px] text-neutral-500 hover:text-neutral-700 hover:underline"
                              >
                                {perDaySegments.every(
                                  (s) =>
                                    s.isLeave ||
                                    s.isExisting ||
                                    s.isConflict ||
                                    s.isWtrBlocked ||
                                    s.enabled,
                                )
                                  ? 'None'
                                  : 'All'}
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setPerDaySegments(
                                    perDaySegments.map((s) =>
                                      s.isLeave ||
                                      s.isExisting ||
                                      s.isConflict ||
                                      s.isWtrBlocked ||
                                      !s.enabled
                                        ? s
                                        : { ...s, startTime: newStartTime, endTime: newEndTime },
                                    ),
                                  )
                                }
                                className="text-[10px] text-[#F26522] hover:underline"
                              >
                                Apply to all
                              </button>
                            </div>
                          </div>
                          <div className="max-h-[240px] overflow-y-auto space-y-1">
                            {perDaySegments.map((seg, i) => (
                              <div
                                key={seg.date}
                                className={clsx(
                                  'flex items-center gap-1.5 text-[11px] rounded px-1 py-0.5',
                                  seg.isLeave && 'bg-amber-50 opacity-60',
                                  seg.isExisting && 'bg-blue-50 opacity-60',
                                  seg.isConflict && 'bg-red-50 opacity-60',
                                  seg.isWtrBlocked && 'bg-red-50 opacity-60',
                                  !seg.isLeave &&
                                    !seg.isExisting &&
                                    !seg.isConflict &&
                                    !seg.isWtrBlocked &&
                                    !seg.enabled &&
                                    'opacity-40',
                                )}
                              >
                                {!seg.isLeave &&
                                  !seg.isExisting &&
                                  !seg.isConflict &&
                                  !seg.isWtrBlocked && (
                                    <input
                                      type="checkbox"
                                      checked={seg.enabled}
                                      onChange={() => {
                                        const updated = [...perDaySegments];
                                        updated[i] = { ...seg, enabled: !seg.enabled };
                                        setPerDaySegments(updated);
                                      }}
                                      className="w-3.5 h-3.5 shrink-0 accent-[#F26522]"
                                    />
                                  )}
                                <span
                                  className={clsx(
                                    'w-[72px] shrink-0 text-neutral-600 font-mono',
                                    (seg.isLeave ||
                                      seg.isExisting ||
                                      seg.isConflict ||
                                      seg.isWtrBlocked) &&
                                      'ml-5',
                                  )}
                                >
                                  {format(new Date(seg.date + 'T00:00:00'), 'EEE dd/MM')}
                                </span>
                                {seg.isLeave ? (
                                  <span className="text-amber-700 text-[10px] italic">
                                    On Leave
                                  </span>
                                ) : seg.isExisting ? (
                                  <span className="text-blue-600 text-[10px] italic">
                                    Already assigned
                                  </span>
                                ) : seg.isConflict ? (
                                  <span className="text-red-600 text-[10px] italic">
                                    Conflict: {seg.conflictLabel}
                                  </span>
                                ) : seg.isWtrBlocked ? (
                                  // Issue #255 (Facet 2): replace the generic
                                  // "WTR rest required" with a specific reason
                                  // ("11h rest gap to the DD/MM shift") + a
                                  // computed "More details" pop-up. All content
                                  // is derived from the pre-check detail — no AI.
                                  (() => {
                                    const wtr = seg.wtrDetail
                                      ? describeWtrBlock(
                                          seg.wtrDetail,
                                          seg.date,
                                          newStartTime,
                                          newEndTime,
                                        )
                                      : null;
                                    if (!wtr) {
                                      return (
                                        <span className="text-red-600 text-[10px] italic">
                                          WTR rest required
                                        </span>
                                      );
                                    }
                                    return (
                                      <span className="flex items-center gap-1.5 text-[10px] text-red-600 italic">
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span className="cursor-help">{wtr.label}</span>
                                            </TooltipTrigger>
                                            <TooltipContent className="max-w-xs whitespace-pre-wrap text-xs">
                                              {seg.wtrReason}
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                        <Popover>
                                          <PopoverTrigger asChild>
                                            <button
                                              type="button"
                                              className="not-italic underline text-red-700 hover:text-red-900 text-[10px]"
                                            >
                                              More details
                                            </button>
                                          </PopoverTrigger>
                                          <PopoverContent
                                            align="start"
                                            className="w-80 text-xs text-neutral-700 space-y-2"
                                          >
                                            <p className="font-medium text-neutral-900">
                                              Why can’t this be scheduled?
                                            </p>
                                            <p>{wtr.headline}</p>
                                            <div className="rounded border border-neutral-200 divide-y">
                                              <div className="px-2 py-1">
                                                <div className="text-[10px] uppercase tracking-wide text-neutral-400">
                                                  This shift
                                                </div>
                                                <div className="font-mono text-neutral-800">
                                                  {wtr.proposedShiftLabel}
                                                </div>
                                              </div>
                                              {wtr.offendingShiftLabel && (
                                                <div className="px-2 py-1">
                                                  <div className="text-[10px] uppercase tracking-wide text-neutral-400">
                                                    Conflicting shift
                                                    {wtr.offendingJobLabel
                                                      ? ` · ${wtr.offendingJobLabel}`
                                                      : ''}
                                                  </div>
                                                  <div className="font-mono text-neutral-800">
                                                    {wtr.offendingShiftLabel}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                            <p className="text-red-700">{wtr.gapSentence}</p>
                                            {wtr.resolution && (
                                              <p className="text-neutral-600">{wtr.resolution}</p>
                                            )}
                                          </PopoverContent>
                                        </Popover>
                                      </span>
                                    );
                                  })()
                                ) : seg.enabled ? (
                                  <>
                                    <input
                                      type="time"
                                      value={seg.startTime}
                                      onChange={(e) => {
                                        const updated = [...perDaySegments];
                                        updated[i] = { ...seg, startTime: e.target.value };
                                        setPerDaySegments(updated);
                                      }}
                                      className="w-[80px] text-[11px] border border-neutral-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#F26522]"
                                    />
                                    <span className="text-neutral-300">–</span>
                                    <input
                                      type="time"
                                      value={seg.endTime}
                                      onChange={(e) => {
                                        const updated = [...perDaySegments];
                                        updated[i] = { ...seg, endTime: e.target.value };
                                        setPerDaySegments(updated);
                                      }}
                                      className="w-[80px] text-[11px] border border-neutral-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#F26522]"
                                    />
                                  </>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {(createAssignmentMutation.isError || createBatchAssignmentMutation.isError) &&
                    (() => {
                      const err =
                        createAssignmentMutation.error || createBatchAssignmentMutation.error;
                      if (err instanceof Error) {
                        // apiFetch throws "API {status}: {jsonBody}" — try to parse WTR_VIOLATION
                        const jsonStart = err.message.indexOf('{');
                        if (jsonStart !== -1) {
                          try {
                            const body = JSON.parse(err.message.slice(jsonStart)) as {
                              code?: string;
                              error?: string;
                              details?: { violations?: Array<{ reason?: string }> };
                            };
                            if (body.code === 'WTR_VIOLATION') {
                              const reason =
                                body.details?.violations?.[0]?.reason ?? body.error ?? err.message;
                              return (
                                <p className="text-[11px] text-amber-700">
                                  Work limit reached — {reason}
                                </p>
                              );
                            }
                          } catch {
                            /* fall through to default */
                          }
                        }
                        return <p className="text-[11px] text-red-700">{err.message}</p>;
                      }
                      return <p className="text-[11px] text-red-700">Failed to assign staff</p>;
                    })()}
                  <div className="flex gap-2">
                    <button
                      disabled={
                        !newStaffId ||
                        createAssignmentMutation.isPending ||
                        createBatchAssignmentMutation.isPending ||
                        // Gate duplicate staff and available days on the active phase.
                        (panelDurationDays === 1
                          ? phaseAssignedStaff.some((a) => a.staffId === newStaffId)
                          : showPerDayGrid
                            ? perDaySegments.filter(
                                (s) => !s.isLeave && !s.isExisting && !s.isConflict && s.enabled,
                              ).length === 0
                            : generatePerDaySegments(newStartTime, newEndTime).filter(
                                (s) => !s.isLeave && !s.isExisting && !s.isConflict,
                              ).length === 0)
                      }
                      onClick={() => {
                        // Multi-day (PHASE) with per-day grid open: use batch endpoint
                        if (panelDurationDays > 1 && showPerDayGrid && perDaySegments.length > 0) {
                          const segments = perDaySegments
                            .filter(
                              (s) => !s.isLeave && !s.isExisting && !s.isConflict && s.enabled,
                            )
                            .map(({ date, startTime, endTime }) => ({ date, startTime, endTime }));
                          if (segments.length === 0) return;
                          createBatchAssignmentMutation.mutate({ staffId: newStaffId, segments });
                        } else if (panelDurationDays > 1) {
                          // Multi-day but grid collapsed: apply same hours to all non-leave, non-existing, non-conflict days
                          const segs = generatePerDaySegments(newStartTime, newEndTime);
                          const segments = segs
                            .filter(
                              (s) => !s.isLeave && !s.isExisting && !s.isConflict && s.enabled,
                            )
                            .map(({ date, startTime, endTime }) => ({ date, startTime, endTime }));
                          if (segments.length === 0) return;
                          createBatchAssignmentMutation.mutate({ staffId: newStaffId, segments });
                        } else {
                          // Single-day: use existing endpoint
                          createAssignmentMutation.mutate({
                            staffId: newStaffId,
                            startTime: newStartTime,
                            endTime: newEndTime,
                          });
                        }
                      }}
                      className="flex-1 py-1.5 rounded-md text-[12px] bg-[#F26522] text-white hover:bg-[#d9561d] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {createAssignmentMutation.isPending || createBatchAssignmentMutation.isPending
                        ? 'Assigning…'
                        : panelDurationDays > 1
                          ? (() => {
                              const availDays = showPerDayGrid
                                ? perDaySegments.filter(
                                    (s) =>
                                      !s.isLeave &&
                                      !s.isExisting &&
                                      !s.isConflict &&
                                      !s.isWtrBlocked,
                                  ).length
                                : generatePerDaySegments(newStartTime, newEndTime).filter(
                                    (s) =>
                                      !s.isLeave &&
                                      !s.isExisting &&
                                      !s.isConflict &&
                                      !s.isWtrBlocked,
                                  ).length;
                              const enabledDays = showPerDayGrid
                                ? perDaySegments.filter(
                                    (s) =>
                                      !s.isLeave &&
                                      !s.isExisting &&
                                      !s.isConflict &&
                                      !s.isWtrBlocked &&
                                      s.enabled,
                                  ).length
                                : availDays;
                              if (enabledDays === 0) return 'No days available';
                              if (enabledDays < availDays)
                                return `Assign (${enabledDays} of ${availDays} days)`;
                              return `Assign (${availDays} days)`;
                            })()
                          : newStaffId && phaseAssignedStaff.some((a) => a.staffId === newStaffId)
                            ? 'Already assigned'
                            : 'Assign'}
                    </button>
                    <button
                      onClick={() => {
                        setShowAddStaff(false);
                        setNewStaffId('');
                        setShowPerDayGrid(false);
                        setPerDaySegments([]);
                      }}
                      className="px-3 py-1.5 text-[12px] border border-neutral-300 text-neutral-600 rounded-md hover:bg-neutral-100 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : canEditActivePhase ? (
                <button
                  onClick={() => setShowAddStaff(true)}
                  className="flex items-center gap-1 text-[12px] text-neutral-500 hover:text-[#F26522] transition-colors mt-1"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Add staff
                </button>
              ) : null}
            </div>
          )}
        </div>

        {/* Documents — 4 doc-type badges linking to Drive files.
            Phase 32-01: hidden for ticket-sourced jobs (Issue #34 sub-item 3).
            Tickets don't have associated Drive folders so the badge row
            would always be empty (4 red "missing" pills) — pure noise. */}
        {job.source !== 'hubspot_ticket' && (
          <Section title="Documents" icon={FileText}>
            {docsError ? (
              <div role="alert" className="text-sm text-amber-800">
                <p>Documents could not be checked. Try again.</p>
                <button
                  type="button"
                  className="mt-1 underline disabled:opacity-50"
                  disabled={docsFetching}
                  onClick={() => void refetchDocuments()}
                >
                  {docsFetching ? 'Retrying documents…' : 'Retry documents'}
                </button>
              </div>
            ) : (
              <div className="flex gap-2 flex-wrap">
                {(
                  [
                    {
                      label: 'PAPERWORK',
                      match: (d: { name: string; category: string }) =>
                        d.name.toLowerCase().includes('paperwork'),
                    },
                    {
                      label: 'ESTIMATION',
                      match: (d: { name: string; category: string }) =>
                        d.name.toLowerCase().includes('estimation'),
                    },
                    {
                      label: 'RAMS',
                      match: (d: { name: string; category: string }) => d.category === 'rams',
                    },
                    {
                      label: 'JOB SHEET',
                      match: (d: { name: string; category: string }) =>
                        d.name.toLowerCase().includes('job sheet'),
                    },
                  ] as const
                ).map(({ label, match }) => {
                  const doc = !docsLoading && !docsError ? documents.find(match) : undefined;
                  return doc ? (
                    <a
                      key={label}
                      href={doc.webViewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-2.5 py-1 text-[11px] font-medium rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                    >
                      {label}
                    </a>
                  ) : (
                    <span
                      key={label}
                      className={`px-2.5 py-1 text-[11px] font-medium rounded-full cursor-default ${docsLoading ? 'bg-neutral-100 text-neutral-500' : 'bg-red-100 text-red-700'}`}
                    >
                      {label}
                    </span>
                  );
                })}
                {docsLoading && (
                  <span role="status" className="flex items-center gap-1 text-xs text-neutral-500">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Loading documents…
                  </span>
                )}
              </div>
            )}
          </Section>
        )}

        {/* Vehicles — collapsible. Phase 34-03 (Issue #36). Placed underneath
            Documents per the issue spec; renders per-row soft-warn badges when
            an assignment's date overlaps the vehicle's unavailability. */}
        <div>
          <button
            onClick={() => setVehiclesOpen((o) => !o)}
            className="w-full flex items-center justify-between mb-2 group"
          >
            <div className="flex items-center gap-1.5">
              <Truck className="w-4 h-4 text-neutral-400" />
              <span className="text-[12px] text-neutral-500 uppercase tracking-wide">
                Vehicles
                {!vehiclesLoading && jobVehicles.length > 0 ? ` (${jobVehicles.length})` : ''}
              </span>
            </div>
            {vehiclesOpen ? (
              <ChevronDown className="w-3.5 h-3.5 text-neutral-400" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-neutral-400" />
            )}
          </button>
          {vehiclesOpen &&
            (vehiclesLoading ? (
              <span className="text-[12px] text-neutral-400 flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </span>
            ) : (
              <div className="space-y-1.5">
                {(() => {
                  // Issue #222: default to the selected day's vehicles, mirroring
                  // the Assigned Staff section. Groups with no assignment on the
                  // selected day are hidden by default; with no day selected
                  // (selectedDateKey null — e.g. the standalone panel) all groups
                  // show, preserving the prior flat-list behaviour.
                  const visibleGroups = selectedDateKey
                    ? vehicleGroups.filter((g) =>
                        g.allAssignments.some((a) => a.date === selectedDateKey),
                      )
                    : vehicleGroups;
                  return visibleGroups.length > 0 ? (
                    <div className="space-y-1">
                      {visibleGroups.map((group) => {
                        const isExpanded = expandedVehicleIds.has(group.vehicleId);
                        const todayAssignments = selectedDateKey
                          ? group.allAssignments.filter((a) => a.date === selectedDateKey)
                          : group.allAssignments;
                        // Collapsed shows the selected day; expanded reveals all of
                        // this vehicle's days — same derivation as the Staff rows.
                        const visibleAssignments = isExpanded
                          ? group.allAssignments
                          : todayAssignments;
                        const hasOtherDays = group.allAssignments.length > todayAssignments.length;
                        return (
                          <div key={group.vehicleId} className="space-y-1">
                            {visibleAssignments.map((v, idx) => (
                              <div
                                key={v.assignment_id}
                                className="flex items-center justify-between text-[12px] text-neutral-700 bg-neutral-50 border border-neutral-200 rounded px-2 py-1"
                              >
                                {/* Phase 38-01 (UAT feedback 2026-05-19): row format
                                    is `DD/MM/YY - REG - Make Model` (date-first,
                                    UK-conventional; allAssignments enforces date ASC). */}
                                <span className="flex items-center gap-1.5 flex-wrap">
                                  {/* Issue #222: per-vehicle expand control, mirroring
                                      the Staff row chevron. Shown on the lead row only
                                      when the vehicle has days other than the selected
                                      one; a matching spacer keeps following rows aligned. */}
                                  {hasOtherDays ? (
                                    idx === 0 ? (
                                      <button
                                        onClick={() => toggleVehicleExpanded(group.vehicleId)}
                                        className="text-neutral-400 hover:text-[#F26522] transition-colors shrink-0"
                                        title={isExpanded ? 'Hide other days' : 'Show other days'}
                                      >
                                        {isExpanded ? (
                                          <ChevronDown className="w-3.5 h-3.5" />
                                        ) : (
                                          <ChevronRight className="w-3.5 h-3.5" />
                                        )}
                                      </button>
                                    ) : (
                                      <span className="w-3.5 h-3.5 shrink-0" />
                                    )
                                  ) : null}
                                  <span className="text-neutral-700">
                                    {formatDateDDMMYY(v.date)} -{' '}
                                    <span className="font-medium">{v.registration}</span> -{' '}
                                    <span className="text-neutral-500">
                                      {v.make} {v.model}
                                    </span>
                                  </span>
                                  {v.unavailability_reason !== null && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] bg-amber-50 text-amber-700">
                                      <AlertCircle className="w-3 h-3" />
                                      Unavailable — {REASON_LABELS[v.unavailability_reason]}
                                    </span>
                                  )}
                                </span>
                                {canEditVehicles && (
                                  /* P-C UX-2: destructive remove now gated behind an
                                     AlertDialog confirmation (was a single-click direct
                                     mutate). */
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <button
                                        disabled={deleteVehicleMutation.isPending}
                                        className="p-0.5 text-neutral-300 hover:text-red-500 transition-colors ml-2"
                                        title="Remove"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Remove vehicle?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Remove {v.registration} ({v.make} {v.model}) from{' '}
                                          {formatDateDDMMYY(v.date)}? This cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:pointer-events-none"
                                          disabled={deleteVehicleMutation.isPending}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            deleteVehicleMutation.mutate({
                                              vehicleId: v.vehicle_id,
                                              date: v.date,
                                            });
                                          }}
                                        >
                                          Remove
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    !showAddVehicle && (
                      <span className="text-[12px] text-neutral-400 italic">None assigned</span>
                    )
                  );
                })()}

                {canEditVehicles && !showAddVehicle && (
                  <button
                    onClick={() => {
                      setShowAddVehicle(true);
                      setVehicleAddError(null);
                      setVehicleAddConflicts([]);
                    }}
                    className="flex items-center gap-1 text-[12px] text-neutral-500 hover:text-[#F26522] transition-colors mt-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add vehicle
                  </button>
                )}

                {showAddVehicle && (
                  <div className="border border-neutral-200 rounded-lg p-2.5 space-y-2 bg-neutral-50">
                    <select
                      value={newVehicleId}
                      onChange={(e) => setNewVehicleId(e.target.value)}
                      className="w-full text-[12px] border border-neutral-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#F26522] bg-white"
                    >
                      <option value="">Select vehicle…</option>
                      {activeVehicles.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.registration} · {v.make} {v.model}
                        </option>
                      ))}
                    </select>
                    <div className="space-y-1">
                      <p className="text-[11px] text-neutral-500 uppercase tracking-wide">Dates</p>
                      {/* Phase 38-01 (UAT feedback 2026-05-19): chips display in
                          DD/MM/YY format AND are filtered to hide dates where
                          the selected vehicle is already busy (other-job
                          job_vehicles ∪ vehicle_unavailability). With no
                          vehicle picked, the full job range chips render. */}
                      {newVehicleId && vehicleBusyDatesLoading ? (
                        <p className="text-[11px] text-neutral-400 italic flex items-center gap-1.5">
                          <Loader2 className="w-3 h-3 animate-spin" /> Loading availability…
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {availableChipDates.map((d) => {
                            const checked = newVehicleDates.includes(d);
                            return (
                              <label
                                key={d}
                                className={clsx(
                                  'flex items-center gap-1 text-[11px] px-2 py-1 rounded border cursor-pointer transition-colors',
                                  checked
                                    ? 'border-[#F26522] bg-[#F26522]/5 text-neutral-800'
                                    : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300',
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    setNewVehicleDates((prev) =>
                                      e.target.checked
                                        ? [...prev, d].sort()
                                        : prev.filter((x) => x !== d),
                                    );
                                  }}
                                  className="sr-only"
                                />
                                {formatDateDDMMYY(d)}
                              </label>
                            );
                          })}
                          {newVehicleId && availableChipDates.length === 0 && (
                            <span className="text-[11px] text-neutral-400 italic">
                              No available dates for this vehicle in the job range
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => {
                          if (!newVehicleId || newVehicleDates.length === 0) return;
                          setVehicleAddError(null);
                          setVehicleAddConflicts([]);
                          addVehiclesMutation.mutate({
                            vehicle_id: newVehicleId,
                            dates: newVehicleDates,
                          });
                        }}
                        disabled={
                          !newVehicleId ||
                          newVehicleDates.length === 0 ||
                          addVehiclesMutation.isPending
                        }
                        className="flex items-center gap-1 px-3 py-1.5 text-[12px] bg-[#F26522] text-white rounded-md hover:bg-[#d9561d] disabled:opacity-50 transition-colors"
                      >
                        {addVehiclesMutation.isPending && (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        )}
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setShowAddVehicle(false);
                          setNewVehicleId('');
                          setNewVehicleDates([]);
                          setVehicleAddError(null);
                          setVehicleAddConflicts([]);
                        }}
                        className="px-3 py-1.5 text-[12px] border border-neutral-300 text-neutral-600 rounded-md hover:bg-neutral-100 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                    {vehicleAddConflicts.length > 0 && (
                      <div className="text-[11px] text-amber-700 space-y-0.5 pt-1">
                        {vehicleAddConflicts.map((c) => (
                          <div key={c.date} className="flex items-start gap-1">
                            <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                            <span>
                              Conflict on {c.date} — already assigned to {c.conflictingJob.ion} '
                              {c.conflictingJob.name}'
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {vehicleAddError && (
                      <p className="text-[11px] text-red-700">{vehicleAddError}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
        </div>

        {/* Equipment — collapsible */}
        <div>
          <button
            onClick={() => setEquipmentOpen((o) => !o)}
            className="w-full flex items-center justify-between mb-2 group"
          >
            <div className="flex items-center gap-1.5">
              <Wrench className="w-4 h-4 text-neutral-400" />
              <span className="text-[12px] text-neutral-500 uppercase tracking-wide">
                Equipment
                {!equipmentLoading && sortedEquipment.length > 0
                  ? ` (${sortedEquipment.length})`
                  : ''}
              </span>
            </div>
            {equipmentOpen ? (
              <ChevronDown className="w-3.5 h-3.5 text-neutral-400" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-neutral-400" />
            )}
          </button>
          {equipmentOpen &&
            (equipmentLoading ? (
              <span className="text-[12px] text-neutral-400 flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </span>
            ) : (
              <div className="space-y-1.5">
                {sortedEquipment.length > 0 ? (
                  <div className="space-y-1">
                    {sortedEquipment.map((e) => {
                      const overAllocated =
                        e.available_quantity_for_date !== null && e.available_quantity_for_date < 0;
                      const overBy = overAllocated
                        ? Math.abs(e.available_quantity_for_date as number)
                        : 0;
                      const rowKey = `${e.equipment_item_id}-${e.date}`;
                      return (
                        <div
                          key={rowKey}
                          className="flex items-center justify-between text-[12px] text-neutral-700 bg-neutral-50 border border-neutral-200 rounded px-2 py-1 gap-2"
                        >
                          {/* Phase 38-02: DD/MM/YY-first row format mirrors
                              vehicles section; quantity prefix from 36-02 is
                              preserved (load-bearing planner signal). Sort
                              ASC enforced by sortedEquipment useMemo. */}
                          <div className="flex items-center gap-1.5 min-w-0 flex-1 flex-wrap">
                            <span className="font-medium">
                              {formatDateDDMMYY(e.date)} - {e.quantity} × {e.identifier}
                            </span>
                            <span className="text-neutral-400">({e.type_name})</span>
                            {overAllocated && (
                              <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">
                                Over-allocated by {overBy}
                              </span>
                            )}
                          </div>
                          {canEditEquipment && (
                            <div className="flex items-center gap-0.5">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingEquipmentRow(e);
                                  setEditingEquipmentQty(e.quantity);
                                }}
                                className="p-0.5 text-neutral-300 hover:text-[#F26522] transition-colors"
                                title="Edit quantity"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                              {/* P-C UX-2: destructive remove now gated behind an
                                  AlertDialog confirmation (was a single-click direct
                                  mutate). */}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button
                                    type="button"
                                    disabled={deleteEquipmentMutation.isPending}
                                    className="p-0.5 text-neutral-300 hover:text-red-500 transition-colors"
                                    title="Remove"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Remove equipment?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Remove {e.quantity} × {e.identifier} ({e.type_name}) from{' '}
                                      {formatDateDDMMYY(e.date)}? This cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:pointer-events-none"
                                      disabled={deleteEquipmentMutation.isPending}
                                      onClick={(ev) => {
                                        ev.preventDefault();
                                        deleteEquipmentMutation.mutate({
                                          equipmentItemId: e.equipment_item_id,
                                          date: e.date,
                                        });
                                      }}
                                    >
                                      Remove
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  !showAddEquipment && (
                    <span className="text-[12px] text-neutral-400 italic">None assigned</span>
                  )
                )}

                {canEditEquipment && !showAddEquipment && (
                  <button
                    onClick={() => setShowAddEquipment(true)}
                    className="flex items-center gap-1 text-[12px] text-neutral-500 hover:text-[#F26522] transition-colors mt-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add equipment
                  </button>
                )}

                {showAddEquipment && (
                  <div className="border border-neutral-200 rounded-lg p-2.5 space-y-2 bg-neutral-50">
                    {/* Equipment item picker — active items only, with qty suffix when tracked */}
                    <select
                      value={newEquipmentItemId}
                      onChange={(e) => setNewEquipmentItemId(e.target.value)}
                      className="w-full text-[12px] border border-neutral-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#F26522] bg-white"
                    >
                      <option value="">Select equipment…</option>
                      {equipmentItems
                        .filter((item) => item.is_available !== false)
                        .map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.type_name} — {item.identifier}
                            {item.quantity !== null ? ` (qty ${item.quantity})` : ''}
                          </option>
                        ))}
                    </select>
                    {/* Quantity dropdown — defaults to 1 (Q5=a) */}
                    <div className="flex items-center gap-2">
                      <label className="text-[11px] text-neutral-500 uppercase tracking-wide">
                        Quantity
                      </label>
                      <select
                        value={newEquipmentQuantity}
                        onChange={(e) => setNewEquipmentQuantity(Number(e.target.value))}
                        className="text-[12px] border border-neutral-200 rounded px-2 py-1 bg-white"
                        aria-label="Quantity"
                      >
                        {Array.from({ length: 50 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {i + 1}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Multi-date checkbox grid (mirrors vehicles section).
                        Phase 38-02: DD/MM/YY chip labels + at-capacity dates
                        hidden when a quantity-tracked item is picked (N/A
                        items show all dates — endpoint returns []). */}
                    <div className="space-y-1">
                      <p className="text-[11px] text-neutral-500 uppercase tracking-wide">Dates</p>
                      {newEquipmentItemId !== '' && equipmentBusyDatesLoading ? (
                        <span className="text-[12px] text-neutral-400">Loading availability…</span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {availableEquipmentChipDates.map((d) => {
                            const checked = newEquipmentDates.includes(d);
                            return (
                              <label
                                key={d}
                                className={clsx(
                                  'flex items-center gap-1 text-[11px] px-2 py-1 rounded border cursor-pointer transition-colors',
                                  checked
                                    ? 'border-[#F26522] bg-[#F26522]/5 text-neutral-800'
                                    : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300',
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) =>
                                    setNewEquipmentDates((prev) =>
                                      e.target.checked
                                        ? [...prev, d].sort()
                                        : prev.filter((x) => x !== d),
                                    )
                                  }
                                  className="sr-only"
                                />
                                {formatDateDDMMYY(d)}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => {
                          if (!newEquipmentItemId || newEquipmentDates.length === 0) return;
                          setEquipmentAddConflict([]);
                          setEquipmentAddError(null);
                          addEquipmentMutation.mutate({
                            equipment_item_id: newEquipmentItemId,
                            dates: newEquipmentDates,
                            quantity: newEquipmentQuantity,
                          });
                        }}
                        disabled={
                          !newEquipmentItemId ||
                          newEquipmentDates.length === 0 ||
                          addEquipmentMutation.isPending
                        }
                        className="flex items-center gap-1 px-3 py-1.5 text-[12px] bg-[#F26522] text-white rounded-md hover:bg-[#d9561d] disabled:opacity-50 transition-colors"
                      >
                        {addEquipmentMutation.isPending && (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        )}
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setShowAddEquipment(false);
                          setNewEquipmentItemId('');
                          setNewEquipmentDates([]);
                          setNewEquipmentQuantity(1);
                          setEquipmentAddConflict([]);
                          setEquipmentAddError(null);
                        }}
                        className="px-3 py-1.5 text-[12px] border border-neutral-300 text-neutral-600 rounded-md hover:bg-neutral-100 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                    {/* Inline 409 conflict list (form stays open) */}
                    {equipmentAddConflict.length > 0 && (
                      <div className="text-[11px] text-amber-700 space-y-0.5 pt-1">
                        {equipmentAddConflict.map((d) => (
                          <div key={d} className="flex items-start gap-1">
                            <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                            <span>Conflict on {d} — already assigned</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {equipmentAddError && (
                      <p className="text-[11px] text-red-700">{equipmentAddError}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
        </div>

        {/* Required Skills — collapsible */}
        <div>
          <button
            onClick={() => setSkillsOpen((o) => !o)}
            className="w-full flex items-center justify-between mb-2 group"
          >
            <div className="flex items-center gap-1.5">
              <GraduationCap className="w-4 h-4 text-neutral-400" />
              <span className="text-[12px] text-neutral-500 uppercase tracking-wide">
                Required Skills
                {!skillsLoading && requiredSkills.length > 0 ? ` (${requiredSkills.length})` : ''}
              </span>
            </div>
            {skillsOpen ? (
              <ChevronDown className="w-3.5 h-3.5 text-neutral-400" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-neutral-400" />
            )}
          </button>
          {skillsOpen &&
            (skillsLoading ? (
              <span className="text-[12px] text-neutral-400 flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </span>
            ) : (
              <div className="space-y-1.5">
                {requiredSkills.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between text-[12px] text-neutral-700 bg-neutral-50 border border-neutral-200 rounded px-2 py-1.5"
                  >
                    <div>
                      <span className="font-medium">{s.name}</span>
                      {s.description && (
                        <span className="text-neutral-400 ml-1.5">{s.description}</span>
                      )}
                    </div>
                    {canEditActivePhase && (
                      <button
                        onClick={() => removeSkillMutation.mutate(s.id)}
                        disabled={removeSkillMutation.isPending}
                        className="p-0.5 text-neutral-300 hover:text-red-500 transition-colors ml-2 shrink-0"
                        title="Remove"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}

                {canEditActivePhase &&
                  (() => {
                    const assignedIds = new Set(requiredSkills.map((s) => s.id));
                    const available = allSkills.filter((s) => !assignedIds.has(s.id));
                    if (available.length === 0 && requiredSkills.length > 0) return null;
                    return (
                      <select
                        value=""
                        onChange={(e) => e.target.value && addSkillMutation.mutate(e.target.value)}
                        disabled={addSkillMutation.isPending || available.length === 0}
                        className="w-full text-[12px] border border-dashed border-neutral-300 rounded px-2 py-1.5 text-neutral-500 bg-white focus:outline-none focus:ring-1 focus:ring-[#F26522] disabled:opacity-50 mt-1"
                      >
                        <option value="">
                          {available.length === 0 ? 'All skills assigned' : '+ Add required skill…'}
                        </option>
                        {available.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    );
                  })()}
              </div>
            ))}
        </div>

        {/* Materials — collapsible */}
        <div>
          <button
            onClick={() => setMaterialsOpen((o) => !o)}
            className="w-full flex items-center justify-between mb-2 group"
          >
            <div className="flex items-center gap-1.5">
              <Package className="w-4 h-4 text-neutral-400" />
              <span className="text-[12px] text-neutral-500 uppercase tracking-wide">
                Materials
                {!materialsLoading && materialsProductCount > 0
                  ? ` (${materialsProductCount})`
                  : ''}
              </span>
            </div>
            {materialsOpen ? (
              <ChevronDown className="w-3.5 h-3.5 text-neutral-400" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-neutral-400" />
            )}
          </button>
          {materialsOpen &&
            (materialsLoading ? (
              <span className="text-[12px] text-neutral-400 flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </span>
            ) : materialsError ? (
              <span className="text-[12px] text-red-700">Failed to load materials.</span>
            ) : (
              // Phase 78-01 (§4): READ-ONLY allocation view. Jobs group products
              // under phases (reusing the panel's phase visual pattern); tickets
              // render products[] flat. Cost is hidden from viewers.
              // Phase 78-02: per-row Release control (planner) + a reserve-from-
              // stock affordance below the list (planner). The reserve affordance
              // renders even when empty so a planner can seed an empty section.
              <div className="space-y-2.5">
                {releaseError && (
                  <p role="alert" className="text-[11px] text-red-700">
                    {releaseError}
                  </p>
                )}
                {materialsProductCount === 0 ? (
                  <span className="text-[12px] text-neutral-400 italic">
                    No materials allocated
                  </span>
                ) : allocations?.target_kind === 'job' ? (
                  // GH #220 item 1: render only the selected phase's materials
                  // (visibleMaterialPhases), not every phase of the job.
                  visibleMaterialPhases
                    .filter((ph) => ph.products.length > 0)
                    .map((ph) => (
                      <div key={ph.phase_id} className="space-y-1.5">
                        <div className="text-[11px] text-neutral-500 font-medium">
                          Phase {ph.phase_number}
                        </div>
                        {ph.products.map((p) => (
                          <MaterialAllocationProduct
                            key={p.catalog_row_id}
                            product={p}
                            showCost={canViewMaterialsCost}
                            canEdit={canReserveStock}
                            onRelease={(id) => releaseAllocationMutation.mutate(id)}
                            releasing={releaseAllocationMutation.isPending}
                          />
                        ))}
                      </div>
                    ))
                ) : (
                  (allocations?.products ?? []).map((p) => (
                    <MaterialAllocationProduct
                      key={p.catalog_row_id}
                      product={p}
                      showCost={canViewMaterialsCost}
                      canEdit={canReserveStock}
                      onRelease={(id) => releaseAllocationMutation.mutate(id)}
                      releasing={releaseAllocationMutation.isPending}
                    />
                  ))
                )}

                {/* Phase 78-02: planner reserve-from-stock affordance. Gated on
                    canReserveStock = can('stock','reserve') && canEditActivePhase. */}
                {canReserveStock &&
                  (showReserveStock ? (
                    <div className="border border-neutral-200 rounded-lg p-2.5 space-y-2 bg-white">
                      <div className="text-[11px] text-neutral-500 font-medium">
                        Reserve from stock
                      </div>
                      <div className="space-y-1.5">
                        <label
                          htmlFor="reserve-product"
                          className="block text-[11px] text-neutral-500"
                        >
                          Product
                        </label>
                        <select
                          id="reserve-product"
                          value={reserveCatalogRowId}
                          onChange={(e) => {
                            setReserveCatalogRowId(e.target.value);
                            setReserveError(null);
                          }}
                          className="w-full text-[12px] border border-neutral-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#F26522]"
                        >
                          <option value="">Select a product…</option>
                          {stockRows.map((row) => (
                            <option key={row.catalog_row_id} value={row.catalog_row_id}>
                              {row.product_name}
                              {row.item_code ? ` (${row.item_code})` : ''} — Catalogue pack:{' '}
                              {row.catalogue_pack_description ?? 'size not recorded'} —{' '}
                              {row.qty_available} packs available
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1.5">
                        <label htmlFor="reserve-qty" className="block text-[11px] text-neutral-500">
                          Quantity (packs)
                        </label>
                        <input
                          id="reserve-qty"
                          type="number"
                          min={0}
                          value={reserveQty}
                          onChange={(e) => {
                            setReserveQty(e.target.value);
                            setReserveError(null);
                          }}
                          className="w-full text-[12px] border border-neutral-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#F26522]"
                        />
                      </div>
                      {reserveError && (
                        <span className="block text-[11px] text-red-700">{reserveError}</span>
                      )}
                      {(() => {
                        const selectedRow = stockRows.find(
                          (r) => r.catalog_row_id === reserveCatalogRowId,
                        );
                        const qtyNum = Number(reserveQty);
                        const overAvailable = !!selectedRow && qtyNum > selectedRow.qty_available;
                        const disabled =
                          reserveAllocationMutation.isPending ||
                          !reserveCatalogRowId ||
                          !(qtyNum > 0) ||
                          overAvailable;
                        return (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={() => {
                                setReserveError(null);
                                // FIX 2: build the LIVE Slice-C body. CB owns the
                                // work-item snapshot (target_kind + work_item_ion)
                                // and Materials stores it verbatim — so the ION must
                                // be the job's REAL ION (the value the panel header
                                // displays as job.ion), NOT allocations.work_item_ion
                                // which is null until the first allocation lands. A
                                // null ION breaks the 78-04 Stock-page per-job
                                // breakdown's job labelling. Fall back to the
                                // allocations snapshot only if job.ion is absent.
                                // This panel is opened for a scheduled Job, even
                                // when its source is a ticket. Historical ticket
                                // allocations cannot determine the new target.
                                reserveAllocationMutation.mutate({
                                  target_kind: 'job',
                                  work_item_ion: job.ion ?? allocations?.work_item_ion ?? null,
                                  ...(activePhase
                                    ? {
                                        phase_id: activePhase.id,
                                        phase_number: activePhase.phase_number,
                                      }
                                    : {}),
                                  catalog_row_id: reserveCatalogRowId,
                                  qty: qtyNum,
                                });
                              }}
                              className="text-[12px] px-2.5 py-1 rounded bg-[#F26522] text-white font-medium hover:bg-[#d9551a] transition-colors disabled:opacity-50"
                            >
                              Reserve
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowReserveStock(false);
                                setReserveCatalogRowId('');
                                setReserveQty('');
                                setReserveError(null);
                              }}
                              className="text-[12px] px-2.5 py-1 rounded border border-neutral-300 text-neutral-600 hover:bg-neutral-100 transition-colors"
                            >
                              Cancel
                            </button>
                            {overAvailable && (
                              <span className="text-[11px] text-amber-700">
                                Exceeds available stock
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setShowReserveStock(true);
                        setReserveError(null);
                      }}
                      className="w-full text-[12px] border border-dashed border-neutral-300 rounded px-2 py-1.5 text-neutral-500 bg-white hover:bg-neutral-50 transition-colors"
                    >
                      + Reserve from stock
                    </button>
                  ))}
              </div>
            ))}
        </div>
      </div>

      {/* GH #108 (part 2): Postpone + Cancel Job as centered modal dialogs.
          #95 had moved the trigger buttons to the header kebab; #108 replaces
          the awkward inline footer reason+confirm forms with AlertDialog modals
          (same pattern as the staff-deletion / equipment-edit confirms). The
          existing pendingPostpone / pendingCancel state still drives open/close,
          and the existing postponeMutation / transitionMutation handlers fire on
          Confirm. "Back" closes with no change. #106: ticket-sourced jobs use
          "ticket" wording. {n} = active (non-cancelled) phase count. */}
      {canManageJobState && (job.status === 'scheduled' || job.status === 'in_progress') && (
        <>
          {/* Postpone */}
          <AlertDialog
            open={pendingPostpone}
            onOpenChange={(open) => {
              if (!open) {
                setPendingPostpone(false);
                setPostponeReason('');
                postponeMutation.reset();
              }
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Postpone {unit} {job.ion}?
                </AlertDialogTitle>
                {/* #118/#119: simplified prompt — no phase wording, no Cancel
                    Phase second line. Ticket vs job noun via `unit`. */}
                <AlertDialogDescription>
                  This {unit} will be released and returned to Available Work for rescheduling.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <label className="block">
                <span className="sr-only">Reason for postponing</span>
                <textarea
                  value={postponeReason}
                  onChange={(e) => setPostponeReason(e.target.value)}
                  placeholder="Reason for postponing"
                  rows={3}
                  className="w-full text-[13px] border border-neutral-300 rounded-md px-2.5 py-1.5 resize-none bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
                />
              </label>
              {postponeMutation.error && (
                <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                  {(() => {
                    // apiFetch wraps non-2xx as `API {status}: {body}` —
                    // body is the JSON {error, code, details?} from the BE.
                    const raw = (postponeMutation.error as Error).message ?? '';
                    const match = raw.match(/^API \d+: (.+)$/s);
                    if (match) {
                      try {
                        const parsed = JSON.parse(match[1]);
                        if (typeof parsed?.error === 'string') return parsed.error;
                      } catch {
                        /* fall through to raw */
                      }
                    }
                    return raw || 'Postpone failed — please refresh and try again.';
                  })()}
                </p>
              )}
              <AlertDialogFooter>
                <AlertDialogCancel>Back</AlertDialogCancel>
                <button
                  onClick={() => postponeMutation.mutate()}
                  disabled={postponeMutation.isPending}
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-amber-600 text-white text-[13px] hover:bg-amber-700 disabled:opacity-50 transition-colors"
                >
                  {postponeMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                  Confirm Postpone
                </button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Cancel Job */}
          <AlertDialog
            open={pendingCancel}
            onOpenChange={(open) => {
              if (!open) {
                setPendingCancel(false);
                setCancelReason('');
                transitionMutation.reset();
              }
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                {/* #118/#119: cancel question — ticket gets NO ION ("Cancel
                    Ticket?"); deal/manual job carries the ION ("Cancel Job
                    {ion}?"). Description simplified — no phase wording. */}
                <AlertDialogTitle>
                  {isTicketJob ? 'Cancel Ticket?' : `Cancel Job ${job.ion}?`}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This {unit} will be cancelled and will not be available to reschedule.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <label className="block">
                <span className="sr-only">Reason for cancellation</span>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Reason for cancellation"
                  rows={3}
                  className="w-full text-[13px] border border-neutral-300 rounded-md px-2.5 py-1.5 resize-none bg-white focus:outline-none focus:ring-1 focus:ring-red-400"
                />
              </label>
              {transitionMutation.error && (
                <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                  {(() => {
                    const raw = (transitionMutation.error as Error).message ?? '';
                    const match = raw.match(/^API \d+: (.+)$/s);
                    if (match) {
                      try {
                        const parsed = JSON.parse(match[1]);
                        if (typeof parsed?.error === 'string') return parsed.error;
                      } catch {
                        /* fall through to raw */
                      }
                    }
                    return raw || 'Cancel failed — please refresh and try again.';
                  })()}
                </p>
              )}
              <AlertDialogFooter>
                <AlertDialogCancel>Back</AlertDialogCancel>
                <button
                  onClick={handleConfirmCancel}
                  disabled={!cancelReason.trim() || transitionMutation.isPending}
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-red-600 text-white text-[13px] hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {transitionMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                  Confirm Cancel {Unit}
                </button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}

      {/* Phase 36-02 mid-loop bundle (Tom UAT Q1=a): pen icon → AlertDialog
          modal for editing equipment row quantity. */}
      <AlertDialog
        open={editingEquipmentRow !== null}
        onOpenChange={(open) => {
          if (!open) setEditingEquipmentRow(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit equipment quantity</AlertDialogTitle>
            <AlertDialogDescription>
              {editingEquipmentRow ? (
                <>
                  {editingEquipmentRow.type_name} ({editingEquipmentRow.identifier}) on{' '}
                  {editingEquipmentRow.date}
                </>
              ) : (
                'Update quantity for this assignment'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1">
            <label
              htmlFor="edit-equipment-quantity"
              className="text-xs text-neutral-600 font-medium block"
            >
              Quantity
            </label>
            <select
              id="edit-equipment-quantity"
              value={editingEquipmentQty}
              onChange={(e) => setEditingEquipmentQty(Number(e.target.value))}
              className="w-full text-[13px] border border-neutral-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522] bg-white"
            >
              {Array.from({ length: 50 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#F26522] hover:bg-[#d9561d] text-white disabled:opacity-50 disabled:pointer-events-none"
              disabled={
                patchEquipmentMutation.isPending ||
                !editingEquipmentRow ||
                editingEquipmentQty === editingEquipmentRow.quantity
              }
              onClick={(e) => {
                e.preventDefault();
                if (!editingEquipmentRow) return;
                patchEquipmentMutation.mutate({
                  equipmentItemId: editingEquipmentRow.equipment_item_id,
                  date: editingEquipmentRow.date,
                  quantity: editingEquipmentQty,
                });
              }}
            >
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase 47 (GH #104): whole-phase reassignment modal. Opened by Save on
          a date change. Step 1 = dry-run conflict analysis; Step 2 = A/B/C. */}
      {reschedule.dialog && (
        <ReassignmentDialog
          state={reschedule.dialog}
          fromLabel={reschedule.dialog.fromLabel ?? ''}
          isPhase={reschedule.dialog.isPhase}
          isExecuting={reschedule.isExecuting}
          onTransfer={reschedule.onTransfer}
          onClear={reschedule.onClear}
          onCancel={reschedule.onCancel}
          onDone={reschedule.onDone}
        />
      )}
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  action,
  children,
}: {
  title: string;
  icon: React.ElementType;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="w-4 h-4 text-neutral-400" />
        <span className="text-[12px] text-neutral-500 uppercase tracking-wide">{title}</span>
        {action && <span className="ml-auto">{action}</span>}
      </div>
      {children}
    </div>
  );
}
